import React, { useState, useEffect } from 'react';
import { Scanner } from '@yudiel/react-qr-scanner';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { 
  Play, 
  Pause, 
  Square, 
  Edit, 
  Trash2, 
  Plus, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  X,
  HelpCircle,
  StopCircle,
  Coffee,
  Briefcase,
  Phone,
  Users,
  Monitor,
  Info,
  UserPlus,
  ArrowLeft,
  Settings,
  XCircle,
  Paperclip,
  Download
} from 'lucide-react';

const getLeadTeamRole = (role) => {
  const map = {
    preflight_lead: 'preflight',
    cmd_lead: 'cmd',
    prepress_lead: 'prepress',
    qc_lead: 'qc',
    production_lead: 'production',
    epson_lead: 'epson'
  };
  return map[role] || '';
};

const Jobs = ({ variant = 'active' }) => {
  const { token, user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [complexityConfig, setComplexityConfig] = useState({});
  const [jobNatureList, setJobNatureList] = useState([]);
  const [jobTypeList, setJobTypeList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [taskTimings, setTaskTimings] = useState({});
  
  // Project Dialog State
  const [currentProject, setCurrentProject] = useState({
    title: '',
    client: '',
    job_code: '',
    job_location: '',
    job_nature: '',
    job_type: '',
    priority: 'normal',
    complexity: 'simple',  // Add complexity field
    deadline: '',
    description: '',
    enabled_tasks: [1, 2, 3, 4, 5, 6, 7, 8]
  });
  // Optional overrides: whether to include Epson Printout (step 7) / Plate and Despatch (step 8).
  // Both default to false for new projects; set from enabled_tasks when editing.
  const [epsonTaskOverride, setEpsonTaskOverride] = useState(false);
  const [plateTaskOverride, setPlateTaskOverride] = useState(false);
  const [isEditingProject, setIsEditingProject] = useState(false);
  
  // Task Complexity Modal State
  const [complexityModal, setComplexityModal] = useState({
    isOpen: false,
    taskId: null,
    taskName: '',
    currentComplexity: '',
    newComplexity: '',
    reason: '',
    requiresReason: false
  });
  
  // Task Management State
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectTasks, setProjectTasks] = useState([]);
  const [allProjectTasks, setAllProjectTasks] = useState({});  // Store tasks for all projects
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  // Sticky sort anchors for special tasks so they stay next to the
  // Preflight round that was active when they were inserted.
  const [specialTaskAnchors, setSpecialTaskAnchors] = useState({});
  
  // Assignment Modal
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedTaskForAssign, setSelectedTaskForAssign] = useState(null);
  const [selectedUserForAssign, setSelectedUserForAssign] = useState('');
  const [assignUserFilter, setAssignUserFilter] = useState('');
  const [assignDropdownOpen, setAssignDropdownOpen] = useState(false);

  // Client dropdown filter state
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [clientDropdownFilter, setClientDropdownFilter] = useState('');

  // Assigned user filter dropdown state
  const [assignedUserDropdownOpen, setAssignedUserDropdownOpen] = useState(false);
  const [assignedUserDropdownFilter, setAssignedUserDropdownFilter] = useState('');

  // Activity Log Modal State
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [currentTaskActivities, setCurrentTaskActivities] = useState([]);
  const [currentTaskTitle, setCurrentTaskTitle] = useState('');

  // Rejection modal (reason and error categories)
  const [rejectionModal, setRejectionModal] = useState({
    isOpen: false,
    taskId: null,
    taskName: '',
    reason: '',
    internalCategories: [],
    externalCategories: [],
    files: []
  });

  // View rejection attachments modal
  const [viewAttachmentsModal, setViewAttachmentsModal] = useState({
    isOpen: false,
    projectId: null,
    taskId: null,
    taskName: '',
    files: [],
    loading: false
  });

  // Error categories for rejection
  const [errorCategories, setErrorCategories] = useState([]);
  const [errorCategoriesLoading, setErrorCategoriesLoading] = useState(false);

  // Cancel project modal
  const [cancelProjectModal, setCancelProjectModal] = useState({ isOpen: false, projectId: null, projectTitle: '', comment: '' });

  // Hold / Pause reason modal
  const [holdPauseModal, setHoldPauseModal] = useState({ isOpen: false, taskId: null, action: '', reason: '' });

  // QR verification state for guarded task completion
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [pendingCompletionTask, setPendingCompletionTask] = useState(null);
  const [qrScanError, setQrScanError] = useState('');
  const [qrScanResult, setQrScanResult] = useState('');
  const [manualQrCode, setManualQrCode] = useState('');
  const [qrVerifiedTasks, setQrVerifiedTasks] = useState({});

  // Search and Filter State
  const [projectSearch, setProjectSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentStageFilter, setCurrentStageFilter] = useState('');
  const [jobLocationFilter, setJobLocationFilter] = useState('');
  const [allJobLocations, setAllJobLocations] = useState([]);
  const [assignedUserFilter, setAssignedUserFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [clientFilter, setClientFilter] = useState([]);
  const [clientFilterDialogOpen, setClientFilterDialogOpen] = useState(false);
  const [tempClientFilter, setTempClientFilter] = useState([]);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalProjectsCount, setTotalProjectsCount] = useState(0);

  const [shiftSummary, setShiftSummary] = useState({
    current_status: 'offline',
    total_office_hours: 0,
    working_hours: 0,
    break_hours: 0,
    idle_hours: 0,
    shift_start: null,
    shift_end: null
  });
  const [usedBreakTypes, setUsedBreakTypes] = useState([]);
  // Break-block overlay: the user explains the overrun and releases their own
  // block, and the TL/Manager is notified rather than being asked to act.
  const [breakReason, setBreakReason] = useState('');
  const [breakReasonSaving, setBreakReasonSaving] = useState(false);
  const [breakReasonError, setBreakReasonError] = useState('');
  // Late clock-in: the worker explains the late start, and the TL/PM is emailed.
  const [lateModal, setLateModal] = useState({ open: false });
  const [lateReason, setLateReason] = useState('');
  const [lateReasonSaving, setLateReasonSaving] = useState(false);
  const [lateReasonError, setLateReasonError] = useState('');
  // Meal PIN dialog shown after starting a lunch/dinner/snacks break (when the
  // cafeteria meal service is enabled and the user is eligible).
  const [mealPinModal, setMealPinModal] = useState({ open: false, pin: '', meal: '' });
  // Read-only CMD task status for the open project (so prepress can see it).
  const [cmdStatus, setCmdStatus] = useState({ cmd_tasks: [], count: 0, all_completed: false });
  // Blocking acknowledgement dialog shown on top of the project summary when CMD
  // work is not yet complete — user must click "Got it" to proceed.
  const [cmdAck, setCmdAck] = useState({ open: false, tasks: [] });

  const [teams, setTeams] = useState([]);
  const [productionLocations, setProductionLocations] = useState([]);

  // Quick filter tabs: '', 'my_projects', 'in_progress', 'on_approval', 'on_hold', 'paused', 'on_query', 'not_started'
  const [quickFilter, setQuickFilter] = useState('');
  // For TL roles: 'my' = only their own tasks | 'team' = all team (default)
  const [myProjectsScope, setMyProjectsScope] = useState('team');
  // Backend-sourced flags: which quick-filter buttons have matching tasks (across ALL projects)
  const [taskStatusFlags, setTaskStatusFlags] = useState({});
  
  // Error Modal State
  const [errorModal, setErrorModal] = useState({
    isOpen: false,
    message: ''
  });

  // "What next?" — shown to opted-in operators whenever they finish, pause,
  // hold or query a job, and on shift start. Not shown to anyone else.
  const [whatNext, setWhatNext] = useState({
    open: false,
    loading: false,
    working: false,
    data: null,
    message: '',
  });

  const openWhatNext = async () => {
    setWhatNext({ open: true, loading: true, working: false, data: null, message: '' });
    try {
      const r = await api.get('/auto-assign/prompt', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.data?.enabled) {
        // Not opted in — close silently, this operator is assigned work by
        // their TL/PM and should never see the dialog.
        setWhatNext({ open: false, loading: false, working: false, data: null, message: '' });
        return;
      }
      setWhatNext({ open: true, loading: false, working: false, data: r.data, message: '' });
    } catch {
      setWhatNext({ open: false, loading: false, working: false, data: null, message: '' });
    }
  };

  const takeNextJob = async () => {
    setWhatNext((w) => ({ ...w, working: true, message: '' }));
    try {
      const r = await api.post('/auto-assign/next', {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.data?.ok) {
        setWhatNext({ open: false, loading: false, working: false, data: null, message: '' });
        const t = r.data.task || {};
        alert(`Started: ${t.job_code || t.title || 'job'} — ${t.task_name}`);
        fetchProjects();
        fetchShiftSummary();
        if (selectedProject?.id) fetchProjectTasks(selectedProject.id);
      } else {
        setWhatNext((w) => ({ ...w, working: false, message: r.data?.error || 'No job available.' }));
      }
    } catch (e) {
      setWhatNext((w) => ({
        ...w,
        working: false,
        message: e.response?.data?.error || 'Could not get a job.',
      }));
    }
  };

  // Asked when a second CMD is inserted: is it a correction of the first CMD,
  // or a new change? The two are scored very differently.
  const [cmdKindModal, setCmdKindModal] = useState({
    open: false,
    kind: 'correction',
    reason: '',
    saving: false,
    previous: null,
  });

  const showErrorModal = (message) => {
    setErrorModal({
      isOpen: true,
      message: String(message || 'An error occurred')
    });
  };

  const closeErrorModal = () => {
    setErrorModal({
      isOpen: false,
      message: ''
    });
  };

  const formatHours = (hours) => {
    if (hours == null || isNaN(Number(hours))) return '0h';
    const totalMinutes = Math.round(Number(hours) * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    if (h === 0 && m === 0) return '0h';
    if (m === 0) return `${h}h`;
    if (h === 0) return `${m}m`;
    return `${h}h ${m}m`;
  };
  
  // Default task workflow with team assignments
  // NOTE: step numbers here are aligned with backend step_order used by
  // create_default_tasks and _get_enabled_tasks_for_client:
  //   1 = Preflight
  //   2 = Single-up approval
  //   4 = Prepress , QC (self QC)
  //   5 = Layout approval
  //   6 = RIP QC
  //   7 = Epson Printout
  //   8 = Plate and Despatch
  const DEFAULT_TASK_WORKFLOW = [
    { step: 1, name: 'Preflight', type: 'standard', team: 'preflight' },
    { step: 2, name: 'Single-up approval', type: 'approval', approval_type: 'Single-up approval', team: 'preflight', conditional: true },
    { step: 4, name: 'Prepress , QC (self QC)', type: 'standard', team: 'prepress' },
    { step: 5, name: 'Layout approval', type: 'approval', approval_type: 'Layout approval', team: 'prepress' },
    { step: 6, name: 'RIP QC', type: 'standard', team: 'qc' },
    { step: 7, name: 'Epson Printout', type: 'standard', team: 'epson' },
    { step: 8, name: 'Plate and Despatch', type: 'standard', team: 'production' }
  ];
  
  const [currentJob, setCurrentJob] = useState({
    title: '',
    client: '',
    status: 'planning',
    priority: 'normal',
    complexity: 'simple',
    deadline: '',
    description: ''
  });
  const [isEditing, setIsEditing] = useState(false);

  const isPrivileged = user && ['preflight_lead', 'cmd_lead', 'cmd_qc', 'prepress', 'prepress_lead', 'preflight_prepress', 'qc_lead', 'production_lead', 'epson_lead', 'pm', 'admin'].includes(user.role);
  // Restrict project-level Edit action to PM, Admin, Preflight Lead, and Preflight users.
  const canEditProject = user && ['pm', 'admin', 'preflight_lead', 'preflight'].includes(user.role);
  const canInsertPreflightExtras = user && ['admin', 'pm', 'preflight_lead'].includes(user.role);
  const canInsertFlexibleTasks = !!user;
  const canInsertPrepressExtras = user && ['prepress', 'prepress_lead', 'preflight_prepress'].includes(user.role);
  const canInsertCMDPath = user && ['cmd', 'cmd_lead', 'cmd_qc', 'pm', 'admin'].includes(user.role);
  const canInsertMountingRIPQC = user && ['production', 'production_lead', 'qc', 'qc_lead', 'pm', 'admin', 'prepress', 'prepress_lead', 'preflight_prepress'].includes(user.role);


  // Die creation can be added by Preflight/Prepress users and TLs (plus PM/Admin),
  // but the work itself remains assigned to the preflight team.
  const canInsertDieCreationTask =
    user &&
    [
      'preflight',
      'preflight_lead',
      'preflight_prepress',
      'prepress',
      'prepress_lead',
      'pm',
      'admin'
    ].includes(user.role);

  // Recreation task can be added by Preflight/Prepress users and TLs (plus PM/Admin),
  // and the work itself can be assigned to either preflight or prepress team.
  const canInsertRecreationTask =
    user &&
    [
      'preflight',
      'preflight_lead',
      'preflight_prepress',
      'prepress',
      'prepress_lead',
      'pm',
      'admin'
    ].includes(user.role);

  useEffect(() => {
    if (token) {
      fetchProjects();
      fetchShiftSummary();
      fetchComplexityConfig();
      // Always fetch clients to get require_single_app_approval settings
      fetchClients();
      // Fetch Job Nature and Job Type for project creation dropdowns
      fetchJobNature();
      fetchJobType();
      // Preload error categories so task error summaries can always
      // resolve internal/external category IDs to readable names,
      // instead of only after opening the rejection modal.
      fetchErrorCategories();
      if (isPrivileged) {
        fetchUsers();
      }
      // For production roles, load teams for location-based visibility
      if (user?.role === 'production' || user?.role === 'production_lead') {
        fetchTeams();
      }
      // Load production locations for roles that are allowed to manage/use them (PM/Admin/Production)
      if (user?.role === 'pm' || user?.role === 'admin' || user?.role === 'production' || user?.role === 'production_lead') {
        fetchProductionLocations();
      }
      // Poll for shift summary updates every minute
      const interval = setInterval(fetchShiftSummary, 60000);

      // Re-sync immediately when the tab regains focus so stale break/idle
      // state from another tab does not leave buttons incorrectly enabled.
      const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') fetchShiftSummary();
      };
      document.addEventListener('visibilitychange', onVisibilityChange);

      return () => {
        clearInterval(interval);
        document.removeEventListener('visibilitychange', onVisibilityChange);
      };
    }
  }, [token, user, currentPage, itemsPerPage, quickFilter, projectSearch, priorityFilter, statusFilter, currentStageFilter, jobLocationFilter, assignedUserFilter, dateFrom, dateTo, clientFilter, variant]);

  // Load persistent client filter preferences for PM/Admin
  useEffect(() => {
    const loadPreferences = async () => {
      if (!token || !user) return;
      if (user.role !== 'pm' && user.role !== 'admin') return;

      try {
        const response = await api.get('/user/preferences', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const storedFilter = Array.isArray(response.data?.client_filter)
          ? response.data.client_filter.filter((c) => typeof c === 'string' && c.trim().length > 0)
          : [];
        setClientFilter(storedFilter);
      } catch (err) {
        console.error('[Jobs] Failed to load user preferences', err);
      }
    };

    loadPreferences();
  }, [token, user]);

  // Periodically refresh projects and the currently selected project's tasks
  useEffect(() => {
    if (!token) return;

    const interval = setInterval(() => {
      // Refresh projects with current filters applied
      fetchProjects();
      if (selectedProject && selectedProject.id) {
        if (user?.role === 'epson_lead') {
          // For Epson roles, rely on tasks embedded in the project list
          const cachedTasks = allProjectTasks[selectedProject.id];
          if (Array.isArray(cachedTasks)) {
            setProjectTasks(cachedTasks);
          }
          fetchProjectAuditLogs(selectedProject.id);
        } else {
          fetchProjectTasks(selectedProject.id);
          fetchProjectAuditLogs(selectedProject.id);
        }
      }
    }, 40000); // every 40 seconds

    return () => clearInterval(interval);
  }, [token, user, selectedProject?.id, currentPage, itemsPerPage, projectSearch, priorityFilter, statusFilter, currentStageFilter, jobLocationFilter, assignedUserFilter, dateFrom, dateTo, clientFilter, variant]);

  // When a project is selected, load its tasks.
  // Epson roles use tasks embedded in the project list instead of the per-project API.
  useEffect(() => {
    if (selectedProject && selectedProject.id) {
      if (user?.role === 'epson_lead') {
        const cachedTasks = allProjectTasks[selectedProject.id];
        if (Array.isArray(cachedTasks)) {
          setProjectTasks(cachedTasks);
        }
        fetchProjectAuditLogs(selectedProject.id);
      } else {
        fetchProjectTasks(selectedProject.id);
        fetchProjectAuditLogs(selectedProject.id);
      }
    }
  }, [selectedProject, user]);

  // Keep Epson roles' task list in sync when the cached project tasks update
  useEffect(() => {
    if (!selectedProject || !selectedProject.id) return;
    if (user?.role !== 'epson_lead') return;
    const cachedTasks = allProjectTasks[selectedProject.id];
    if (!Array.isArray(cachedTasks)) return;

    // Avoid resetting projectTasks if there is no meaningful change, to
    // prevent unnecessary re-renders and duplicate /timings calls.
    if (
      projectTasks.length === cachedTasks.length &&
      projectTasks.every((t, idx) => {
        const other = cachedTasks[idx];
        return t && other && t.id === other.id && t.status === other.status;
      })
    ) {
      return;
    }

    setProjectTasks(cachedTasks);
  }, [allProjectTasks, selectedProject?.id, user, projectTasks]);

  useEffect(() => {
    if (!projectTasks.length) return;

    // Only fetch timings for tasks that don't have timings cached yet.
    const missingTasks = projectTasks.filter((task) => task && !taskTimings[task.id]);
    if (!missingTasks.length) return;

    missingTasks.forEach((task) => fetchTaskTimings(task.id));
  }, [projectTasks, taskTimings]);

  // Refresh timings for the active task whenever shift summary updates so the
  // "Time today" counter in the active-task card stays current.
  useEffect(() => {
    if (shiftSummary.active_task_id) {
      fetchTaskTimings(shiftSummary.active_task_id);
    }
  }, [shiftSummary.active_task_id, shiftSummary.current_status]); // eslint-disable-line

  useEffect(() => {
    if (qrModalOpen) {
      setManualQrCode('');
      setQrScanError('');
      setQrScanResult('');
    }
  }, [qrModalOpen, pendingCompletionTask]);

  const tasksRequiringQr = ['Epson Printout', 'Plate and Despatch'];

  const requiresQrBeforeCompletion = (task) => {
    if (!task) return false;

    const jobNature = (selectedProject?.job_nature || '').toLowerCase();
    const isCorrugation = jobNature === 'corrugation';
    const isRotary = jobNature === 'rotary';
    const isNotebook = jobNature === 'notebook';

    // Rotary, Corrugation, or Notebook: never require QR for Plate and Despatch.
    if (task.name === 'Plate and Despatch' && (isCorrugation || isRotary || isNotebook)) {
      return false;
    }

    // If there is no Epson task in this project, Plate and Despatch should not
    // require QR at all.
    if (task.name === 'Plate and Despatch') {
      const projectId = selectedProject?.id;
      const fullTasks = projectId ? (allProjectTasks[projectId] || projectTasks) : projectTasks;
      const hasEpsonTask = Array.isArray(fullTasks) && fullTasks.some((t) => t.name === 'Epson Printout');
      if (!hasEpsonTask) {
        return false;
      }
    }

    // Epson Printout itself still always requires QR when present.
    return task.name === 'Epson Printout' || task.name === 'Plate and Despatch';
  };

  const handleCloseQrModal = async () => {
    setQrModalOpen(false);
    setPendingCompletionTask(null);
    setManualQrCode('');
    setQrScanError('');
    setQrScanResult('');
  };

  const normalizeQrCode = (code) => {
    if (code === undefined || code === null) return '';
    return String(code).trim();
  };

  // Normalize job codes for comparison between QR and project job_code.
  // This allows formats like "222" and "project 222" to be treated as the same job
  // by stripping non-digits first, then falling back to a lowercased string.
  const normalizeJobCodeForCompare = (value) => {
    if (value === undefined || value === null) return '';
    const str = String(value).trim();
    if (!str) return '';
    const digitsOnly = str.replace(/\D+/g, '');
    if (digitsOnly) return digitsOnly;
    return str.toLowerCase();
  };

  const getStoredQrForTask = (taskId) => {
    const projectId = selectedProject?.id;
    const fullTasks = projectId ? (allProjectTasks[projectId] || projectTasks) : projectTasks;
    const taskFromState = (fullTasks || projectTasks).find((t) => t.id === taskId);
    const raw = qrVerifiedTasks[taskId] || taskFromState?.qr_code || taskFromState?.qr_value || taskFromState?.qrCode || null;
    return normalizeQrCode(raw) || null;
  };

  const getEpsonQrCodeForCurrentProject = () => {
    const projectId = selectedProject?.id;
    const fullTasks = projectId ? (allProjectTasks[projectId] || projectTasks) : projectTasks;
    const epsonTask = (fullTasks || projectTasks).find((t) => t.name === 'Epson Printout');
    return epsonTask ? getStoredQrForTask(epsonTask.id) : null;
  };

  const persistQrCodeForTask = async (task, code) => {
    if (!task || !code) return;
    const normalized = normalizeQrCode(code);
    if (!normalized) return;
    try {
      // Only update the qr_code field without sending the entire task object
      await api.put(`/pm/tasks/${task.id}`, 
        { qr_code: normalized, qr_scanned_at: new Date().toISOString() }, 
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      console.log('QR code saved to database for task:', task.id);
    } catch (err) {
      console.error('Failed to persist QR code for task', task.id, err);
    }
  };

  const recordQrAndComplete = async (code) => {
    const task = pendingCompletionTask;
    if (!task) return;

    const normalizedCode = normalizeQrCode(code);
    if (!normalizedCode) {
      setQrScanError('Invalid QR code. Please try again.');
      return;
    }

    // Extract job code from QR code (expected format: "job_code - date")
    const qrJobCodeRaw = code.split(' - ')[0]?.trim();
    const projectJobCodeRaw = selectedProject?.job_code?.trim();

    const qrJobCode = normalizeJobCodeForCompare(qrJobCodeRaw);
    const projectJobCode = normalizeJobCodeForCompare(projectJobCodeRaw);

    // Validate that the scanned job code matches the project's job code,
    // but use normalized forms so that minor differences like
    // "222" vs "project 222" do not cause a mismatch.
    if (qrJobCode && projectJobCode && qrJobCode !== projectJobCode) {
      setQrScanError(`Job code mismatch! Scanned: ${qrJobCodeRaw || qrJobCode}, Expected: ${projectJobCodeRaw || projectJobCode}`);
      return;
    }

    await persistQrCodeForTask(task, normalizedCode);
    setQrVerifiedTasks((prev) => ({ ...prev, [task.id]: normalizedCode }));
    setQrScanResult(normalizedCode);
    setQrModalOpen(false);
    setPendingCompletionTask(null);
    setManualQrCode('');
    setQrScanError('');

    await handleTaskStatusChange(task.id, 'complete', { bypassQrCheck: true, qrCodeOverride: normalizedCode });
  };

  const handleScannerCode = async (rawCode) => {
    const normalized = normalizeQrCode(rawCode);
    if (!normalized) {
      setQrScanError('Invalid QR code. Please try again.');
      return;
    }

    try {
      const task = pendingCompletionTask;
      if (!task) return;

      if (task.name === 'Plate and Despatch') {
        const jobNature = (selectedProject?.job_nature || '').toLowerCase();
        const isCorrugation = jobNature === 'corrugation';
        const isRotary = jobNature === 'rotary';
        const isNotebook = jobNature === 'notebook';

        // For Corrugation, Rotary, or Notebook jobs, Plate and Despatch does not need
        // QR matching against Epson.
        if (!isCorrugation && !isRotary && !isNotebook) {
          // Only enforce Epson QR dependency when the Epson Printout task
          // actually exists for this project. Some workflows disable Epson.
          const projectId = selectedProject?.id;
          const fullTasks = projectId ? (allProjectTasks[projectId] || projectTasks) : projectTasks;
          const epsonTask = (fullTasks || projectTasks).find((t) => t.name === 'Epson Printout');
          if (epsonTask) {
            const epsonCode = getStoredQrForTask(epsonTask.id);
            if (!epsonCode) {
              setQrScanError('Epson Printout must be scanned first before Plate and Despatch.');
              return;
            }
            if (normalized !== normalizeQrCode(epsonCode)) {
              setQrScanError('Scanned code does not match the Epson Printout QR. Scan the same code.');
              return;
            }
          }
        }
      }

      setManualQrCode(normalized);
      await recordQrAndComplete(normalized);
    } catch (err) {
      console.error('Failed to handle scanner QR code:', err);
      setQrScanError('Failed to process QR code. Please try again.');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to fetch users');
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await api.get('/admin/teams', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch teams');
      setTeams([]);
    }
  };

  const fetchProductionLocations = async () => {
    try {
      const response = await api.get('/admin/production-locations', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductionLocations(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch production locations');
      setProductionLocations([]);
    }
  };

  const getEnabledTasksForClient = (clientName, jobNature = '') => {
    // Mirror backend logic in _get_enabled_tasks_for_client (backend uses
    // steps 1, 2, 4, 5, 6, 7, 8), then apply job-nature specific defaults.

    const trimmedName = (clientName || '').trim();
    const client = clients.find((c) => (c.name || '').trim() === trimmedName);

    // Defaults: enabled for all clients (even if client record is missing flags)
    let preflightEnabled = client?.preflight_enabled !== false;
    let singleAppEnabled = client?.single_app_approval_enabled !== false;
    // Epson Printout is the opposite: OFF unless the client explicitly needs it.
    const epsonEnabled = client?.epson_enabled === true;

    // Dependency: Single-up approval cannot be enabled if Preflight is disabled.
    if (!preflightEnabled) {
      singleAppEnabled = false;
    }

    // Base: enable core production steps (Prepress QC, Layout, RIP QC).
    // Epson Printout (7) comes from the client's setting; Plate and Despatch (8)
    // stays opt-in per project via the toggle in the UI.
    let enabled = [4, 5, 6];
    if (preflightEnabled) {
      // When Preflight is enabled, include step 1 as well.
      enabled = [1].concat(enabled);
      if (singleAppEnabled) {
        // When Single-up approval is enabled, include step 2 too.
        enabled = [1, 2].concat([4, 5, 6]);
      }
    }
    if (epsonEnabled) {
      enabled = enabled.concat([7]);
    }

    const normalizedNature = (jobNature || '').toLowerCase();

    // Corrugation or Notebook: by default skip Preflight (1) and Single-up approval (2).
    if (normalizedNature === 'corrugation' || normalizedNature === 'notebook') {
      enabled = enabled.filter((step) => ![1, 2].includes(step));
    }

    // Rotary: only keep Prepress QC (4) and RIP QC (6) — plus Epson Printout if
    // the client requires a proof, since that is a customer commitment rather
    // than a production-workflow choice. Mirrors the backend.
    if (normalizedNature === 'rotary') {
      const keep = epsonEnabled ? [4, 6, 7] : [4, 6];
      enabled = enabled.filter((step) => keep.includes(step));
    }

    return enabled;
  };

  // Whether the selected client has Epson Printout switched on in Settings.
  const clientWantsEpson = (clientName) => {
    const trimmed = (clientName || '').trim();
    const client = clients.find((c) => (c.name || '').trim() === trimmed);
    return client?.epson_enabled === true;
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/admin/clients', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (err) {
      console.error('Failed to fetch clients');
    }
  };

  const fetchJobNature = async () => {
    try {
      const response = await api.get('/admin/job-nature', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobNatureList(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch job nature list');
      setJobNatureList([]);
    }
  };

  const fetchJobType = async () => {
    try {
      const response = await api.get('/admin/job-type', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobTypeList(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch job type list');
      setJobTypeList([]);
    }
  };

  const fetchComplexityConfig = async () => {
    try {
      // Try to fetch the detailed admin config first (has nested task times)
      const response = await api.get('/admin/config/complexity', {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Fetched detailed complexity config:', response.data);
      setComplexityConfig(response.data);
    } catch (err) {
      console.error('Failed to fetch detailed complexity config, trying public endpoint:', err);
      try {
        // Fallback to public endpoint if admin endpoint fails
        const response = await api.get('/config/complexity');
        setComplexityConfig(response.data);
      } catch (err2) {
        console.error('Failed to fetch complexity config');
        // Set default values with nested structure
        setComplexityConfig({
          'simple_simple': {
            'Preflight': 5,
            'Single-up approval': 10,
            'Layout approval': 5,
            'CMD': 15,
            'Prepress , QC (self QC)': 15,
            'Epson Printout': 10,
            'RIP QC': 10,
            'Plate and Despatch': 15
          },
          'simple': {
            'Preflight': 5,
            'Single-up approval': 15,
            'Layout approval': 5,
            'CMD': 20,
            'Prepress , QC (self QC)': 20,
            'Epson Printout': 15,
            'RIP QC': 15,
            'Plate and Despatch': 20
          },
          'medium_simple': {
            'Preflight': 7,
            'Single-up approval': 17,
            'Layout approval': 5,
            'CMD': 25,
            'Prepress , QC (self QC)': 25,
            'Epson Printout': 17,
            'RIP QC': 17,
            'Plate and Despatch': 24
          },
          'medium': {
            'Preflight': 10,
            'Single-up approval': 20,
            'Layout approval': 5,
            'CMD': 30,
            'Prepress , QC (self QC)': 30,
            'Epson Printout': 20,
            'RIP QC': 20,
            'Plate and Despatch': 30
          },
          'complex': {
            'Preflight': 15,
            'Single-up approval': 30,
            'Layout approval': 10,
            'CMD': 45,
            'Prepress , QC (self QC)': 45,
            'Epson Printout': 30,
            'RIP QC': 30,
            'Plate and Despatch': 45
          },
          'super_complex': {
            'Preflight': 20,
            'Single-up approval': 40,
            'Layout approval': 15,
            'CMD': 60,
            'Prepress , QC (self QC)': 60,
            'Epson Printout': 40,
            'RIP QC': 40,
            'Plate and Despatch': 60
          },
          'combined_job': {
            'Preflight': 25,
            'Single-up approval': 50,
            'Layout approval': 20,
            'CMD': 75,
            'Prepress , QC (self QC)': 75,
            'Epson Printout': 50,
            'RIP QC': 50,
            'Plate and Despatch': 75
          }
        });
      }
    }
  };

  const fetchTaskStatusFlags = async () => {
    // Only relevant for worker / TL roles; PM and Admin don't use quick-filter tabs
    const workerRoles = [
      'preflight', 'preflight_lead', 'preflight_prepress',
      'cmd', 'cmd_lead',
      'prepress', 'prepress_lead',
      'qc', 'qc_lead',
      'production', 'production_lead',
      'epson', 'epson_lead',
    ];
    if (!token || !user || !workerRoles.includes(user.role)) return;
    try {
      const response = await api.get('/pm/projects/task-status-summary', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaskStatusFlags(response.data || {});
    } catch (err) {
      // Non-critical — silently ignore; buttons will just not appear if flags are absent
    }
  };

  const fetchProjects = async () => {
    try {
      // Show loader while fetching projects (also used for pagination/filter changes)
      setLoading(true);
      // Build filter params for server-side filtering
      const params = {
        // When a quick filter is active we must see ALL projects so the client-side
        // task-level filtering works across the full dataset, not just the current page.
        page: quickFilter ? 1 : currentPage,
        limit: quickFilter ? 9999 : itemsPerPage,
        variant: variant  // Pass variant to backend for server-side filtering
      };
      
      // Add search parameter
      if (projectSearch.trim()) {
        params.search = projectSearch.trim();
      }
      
      // Add priority filter
      if (priorityFilter) {
        params.priority = priorityFilter;
      }
      
      // Add status filter
      if (statusFilter) {
        params.status = statusFilter;
      }
      
      // Add current stage filter
      if (currentStageFilter) {
        params.current_stage = currentStageFilter;
      }
      
      // Add job location filter
      if (jobLocationFilter) {
        params.job_location = jobLocationFilter;
      }
      
      // Add client filter (comma-separated)
      if (clientFilter.length > 0) {
        params.clients = clientFilter.join(',');
      }

      // Add date range filter (created_at)
      if (dateFrom) {
        params.date_from = dateFrom;
      }
      if (dateTo) {
        params.date_to = dateTo;
      }

      // Add assigned user filter (leads / PM / admin)
      if (assignedUserFilter) {
        params.assigned_user = assignedUserFilter;
      }

      // When a TL/PM is viewing "My Projects" scope, tell the backend to restrict
      // to only projects where this user personally has an active task. Without this
      // the backend returns all role-based projects paginated at 25/100, and the
      // frontend filter leaves fewer visible items per page than expected.
      const isTLorPM = user && [
        'preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead',
        'production_lead', 'epson_lead', 'pm', 'admin'
      ].includes(user.role);
      if (isTLorPM && myProjectsScope === 'my' && !assignedUserFilter) {
        params.assigned_user = user.id;
      }
      
      // Fetch current page with server-side pagination and filtering
      const response = await api.get('/pm/projects', {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Handle both new paginated format and legacy format
      const projectsData = response.data.data || response.data;
      const paginationInfo = response.data.pagination;
      
      setProjects(projectsData);
      
      // Store total count from server for proper pagination display
      if (paginationInfo && paginationInfo.total !== undefined) {
        setTotalProjectsCount(paginationInfo.total);
      } else {
        // Fallback: use the length of the returned data
        setTotalProjectsCount(projectsData?.length ?? 0);
      }
      
      // Populate allProjectTasks from the embedded tasks in the response
      if (projectsData && Array.isArray(projectsData)) {
        const tasksMap = {};
        const locationsFromPage = new Set();
        projectsData.forEach(project => {
          if (project.tasks) {
            tasksMap[project.id] = project.tasks;
          }
          const loc = (project.job_location || '').trim();
          if (loc) {
            locationsFromPage.add(loc);
          }
        });
        setAllProjectTasks(prev => ({ ...prev, ...tasksMap }));

        // Accumulate distinct job locations across all fetched pages so that
        // the location filter dropdown is not limited by the currently
        // filtered project list.
        if (locationsFromPage.size > 0) {
          setAllJobLocations((prev) => {
            const combined = new Set(prev);
            locationsFromPage.forEach((loc) => combined.add(loc));
            return Array.from(combined).sort();
          });
        }
      }
      
      setLoading(false);
      fetchTaskStatusFlags();
      return projectsData;
    } catch (err) {
      showErrorModal('Failed to fetch projects');
      setLoading(false);
      return null;
    }
  };

  const fetchProjectTasks = async (projectId) => {
    try {
      const response = await api.get(`/pm/projects/${projectId}/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const sortedTasks = [...response.data].sort((a, b) => (a.step_order || 0) - (b.step_order || 0));

      // When tasks are fetched, capture stable sort anchors for
      // Die creation and Recreation based on the latest Preflight
      // round that exists at this moment. This ensures these tasks
      // stay next to the Preflight that was active when they were
      // inserted, even if new Preflight rounds are added later.
      setSpecialTaskAnchors((prev) => {
        const next = { ...prev };
        const preflightTasks = sortedTasks.filter(
          (t) => (t.name || '').toLowerCase() === 'preflight' && typeof t.step_order === 'number'
        );

        if (preflightTasks.length > 0) {
          const maxPreflightOrder = Math.max(
            ...preflightTasks.map((t) => t.step_order)
          );

          sortedTasks.forEach((t) => {
            const tName = (t.name || '').toLowerCase();
            if (tName === 'die creation' || tName === 'recreation') {
              const key = String(t.id);
              if (next[key] == null) {
                const offset = tName === 'die creation' ? 0.01 : 0.02;
                next[key] = maxPreflightOrder + offset;
              }
            }
          });
        }

        return next;
      });

      const qrFromTasks = {};
      sortedTasks.forEach((t) => {
        const code = t.qr_code || t.qr_value || t.qrCode;
        if (code) qrFromTasks[t.id] = code;
      });
      if (Object.keys(qrFromTasks).length > 0) {
        setQrVerifiedTasks((prev) => ({ ...prev, ...qrFromTasks }));
      }

      if (selectedProject && selectedProject.id === projectId) {
        // Update tasks for selected project
        setProjectTasks(sortedTasks);
      }
      
      // Merge fetched (possibly team-filtered) tasks into the cached
      // allProjectTasks entry without discarding tasks from other teams
      // (e.g. Epson for production users). This ensures Epson remains
      // visible in allProjectTasks for QR and sequencing logic even
      // though /pm/projects/<id>/tasks only returns the current team's
      // tasks.
      setAllProjectTasks((prev) => {
        const existing = Array.isArray(prev[projectId]) ? prev[projectId] : [];
        const mergedById = {};

        existing.forEach((t) => {
          if (t && t.id) {
            mergedById[t.id] = t;
          }
        });

        sortedTasks.forEach((t) => {
          if (t && t.id) {
            mergedById[t.id] = t;
          }
        });

        return {
          ...prev,
          [projectId]: Object.values(mergedById)
        };
      });
    } catch (err) {
      console.error('Failed to fetch project tasks');
      showErrorModal('Failed to fetch project tasks');
    }
  };

  const fetchProjectAuditLogs = async (projectId) => {
    try {
      const response = await api.get(`/pm/projects/${projectId}/audit-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuditLogs(response.data);
    } catch (err) {
      console.error('Failed to fetch project audit logs:', err);
    }
  };

  const createProjectWithTasks = async (projectData) => {
    try {
      // Calculate automatic delivery date if not manually set
      const projectWithDelivery = {
        ...projectData,
        deadline: projectData.deadline || calculateDeliveryDate(new Date()).toISOString().split('T')[0]
      };
      
      // Create project (backend automatically creates default tasks)
      const projectResponse = await api.post('/pm/projects', projectWithDelivery, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // After project is created, fetch its tasks and apply complexity timings
      if (projectResponse.data && projectResponse.data.id) {
        const projectId = projectResponse.data.id;
        await applyComplexityTimingsToProject(projectId, projectWithDelivery.complexity);
      }
      
      fetchProjects();
    } catch (err) {
      console.error('Failed to create project with tasks:', err);
      showErrorModal('Failed to create project');
    }
  };

  const applyComplexityTimingsToProject = async (projectId, complexity) => {
    try {
      console.log(`=== Starting to apply ${complexity} timings to project ${projectId} ===`);
      
      // Fetch all tasks for this project
      const tasksResponse = await api.get(`/pm/projects/${projectId}/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const tasks = tasksResponse.data;
      console.log(`Found ${tasks.length} tasks in project`, tasks);
      
      const complexityData = complexityConfig[complexity];
      console.log(`Complexity config for "${complexity}":`, complexityData);
      
      if (!complexityData) {
        console.error(`ERROR: No timing config found for complexity: ${complexity}`);
        console.error('Available complexities:', Object.keys(complexityConfig));
        alert(`Error: No timing configuration found for complexity "${complexity}". Please check Admin > Complexity Time Config.`);
        return;
      }
      
      if (typeof complexityData !== 'object' || Array.isArray(complexityData)) {
        console.error(`ERROR: Complexity data is not an object:`, complexityData);
        alert(`Error: Invalid complexity configuration format for "${complexity}". Expected object with task names.`);
        return;
      }
      
      let successCount = 0;
      let failureCount = 0;
      
      // Update each task with the timing from the complexity config
      for (const task of tasks) {
        const taskName = task.name;
        const estimatedMinutes = complexityData[taskName];
        
        console.log(`Processing task: "${taskName}", estimated minutes from config: ${estimatedMinutes}`);
        
        if (estimatedMinutes !== undefined) {
          try {
            const updatePayload = {
              complexity: complexity,
              complexity_minutes: estimatedMinutes,
              estimated_hours: Math.round((estimatedMinutes / 60) * 100) / 100
            };
            
            console.log(`Updating task ${task.id} (${taskName}) with:`, updatePayload);
            
            const response = await api.put(`/pm/tasks/${task.id}`, updatePayload, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            console.log(`✓ Updated ${taskName} with ${complexity} complexity and ${estimatedMinutes} minutes`);
            successCount++;
          } catch (err) {
            console.error(`✗ Failed to update task ${taskName}:`, err.response?.data || err.message);
            failureCount++;
          }
        } else {
          console.warn(`Task "${taskName}" not found in complexity config for "${complexity}"`);
        }
      }
      
      console.log(`=== Completed: ${successCount} successful, ${failureCount} failed ===`);
      if (successCount > 0) {
        alert(`Applied ${complexity} timing to ${successCount} tasks successfully!`);
        // Refresh the tasks to show the updated complexity
        if (selectedProject) {
          fetchProjectTasks(selectedProject.id);
        }
      }
    } catch (err) {
      console.error('Failed to apply complexity timings:', err);
      alert('Error applying complexity timings: ' + (err.response?.data?.error || err.message));
    }
  };

  const fetchShiftSummary = async () => {
    try {
      const response = await api.get('/user/shift-summary', {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Shift summary updated:', response.data);
      setShiftSummary(response.data);
    } catch (err) {
      console.error('Failed to fetch shift summary', err);
    }
  };

  // Submit the reason for the break overrun. On success the block is released
  // for the rest of today and the TL/Manager notification is sent.
  const submitBreakReason = async () => {
    const reason = breakReason.trim();
    if (reason.length < 5) {
      setBreakReasonError('Please give a little more detail.');
      return;
    }
    setBreakReasonSaving(true);
    setBreakReasonError('');
    try {
      await api.post('/user/break-override', { reason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBreakReason('');
      // Re-read the summary so the overlay closes on the server's answer rather
      // than on an optimistic guess.
      await fetchShiftSummary();
    } catch (err) {
      setBreakReasonError(
        err.response?.data?.error || 'Could not submit the reason. Please try again.'
      );
    } finally {
      setBreakReasonSaving(false);
    }
  };

  const submitLateReason = async () => {
    const reason = lateReason.trim();
    if (reason.length < 5) {
      setLateReasonError('Please give a little more detail.');
      return;
    }
    setLateReasonSaving(true);
    setLateReasonError('');
    try {
      await api.post('/user/late-reason', {
        reason,
        minutes_late: lateModal.minutes || 0,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setLateModal({ open: false });
      setLateReason('');
    } catch (err) {
      setLateReasonError(
        err.response?.data?.error || 'Could not submit the reason. Please try again.'
      );
    } finally {
      setLateReasonSaving(false);
    }
  };

  // Asked at clock-out when the shift ran past its limit. The shift has
  // already ended by this point — this only collects the explanation, which
  // goes into the next morning's report.
  const [longShiftModal, setLongShiftModal] = useState({ open: false });
  const [longShiftReason, setLongShiftReason] = useState('');
  const [longShiftError, setLongShiftError] = useState('');
  const [longShiftSaving, setLongShiftSaving] = useState(false);

  const submitLongShiftReason = async () => {
    const reason = longShiftReason.trim();
    if (reason.length < 5) {
      setLongShiftError('Please give a little more detail.');
      return;
    }
    setLongShiftSaving(true);
    setLongShiftError('');
    try {
      await api.post('/user/long-shift-reason', { reason },
        { headers: { Authorization: `Bearer ${token}` } });
      setLongShiftModal({ open: false });
      setLongShiftReason('');
    } catch (err) {
      setLongShiftError(
        err.response?.data?.error || 'Could not submit the reason. Please try again.'
      );
    } finally {
      setLongShiftSaving(false);
    }
  };

  const handleInsertCMDTask = async (payload = {}) => {
    if (!selectedProject) {
      showErrorModal('No project selected');
      return;
    }

    // Fast path only. projectTasks is filtered to the viewer's team, so a
    // non-CMD user may not see an active CMD at all — the backend enforces
    // this properly and its error is surfaced below.
    const activeCmdTaskExists = projectTasks.some(t => t.name === 'CMD' && t.status !== 'completed' && t.status !== 'rejected');
    if (activeCmdTaskExists) {
      showErrorModal('An active CMD task already exists in this project');
      return;
    }

    // Whether a CMD was already completed is decided by the BACKEND, not by
    // reading projectTasks. That list is filtered to the viewer's own team, so
    // a prepress or QC user never sees the CMD task and would skip the
    // question entirely — then get rejected. The backend answers with
    // `needs_cmd_kind` and we open the dialog from that.
    try {
      const response = await api.post(`/pm/projects/${selectedProject.id}/insert-cmd`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Insert CMD response:', response.data);
      
      if (response.data.ok) {
        // Ensure the new CMD step is included in the project's enabled_tasks
        if (response.data.step_order) {
          upsertEnabledTaskStep(response.data.step_order);
        }
        // Refresh tasks to show the new CMD task
        await fetchProjectTasks(selectedProject.id);
        setError(null);
        setCmdKindModal({ open: false, kind: 'correction', reason: '', saving: false, previous: null });
        alert(response.data.cmd_kind === 'correction'
          ? 'CMD correction inserted and assigned to you. Logged against the previous CMD.'
          : 'CMD task inserted and assigned to you!');
      }
    } catch (err) {
      const data = err.response?.data || {};
      // Not an error to show the user — the backend is asking the question.
      if (data.needs_cmd_kind) {
        setCmdKindModal({
          open: true,
          kind: 'correction',
          reason: '',
          saving: false,
          previous: data.previous_cmd || null,
        });
        return;
      }
      console.error('Failed to insert CMD task:', err);
      setCmdKindModal((m) => ({ ...m, saving: false }));
      showErrorModal(data.error || err.message || 'Failed to insert CMD task');
    }
  };

  const upsertEnabledTaskStep = (stepOrder) => {
    if (!stepOrder) return;
    setSelectedProject((prev) => {
      if (!prev) return prev;
      const enabled = Array.isArray(prev.enabled_tasks) ? prev.enabled_tasks : [];
      if (enabled.length === 0) return prev;
      if (enabled.includes(stepOrder)) return prev;
      return { ...prev, enabled_tasks: [...enabled, stepOrder].sort((a, b) => a - b) };
    });
  };

  const handleInsertDieCreationTask = async () => {
    if (!selectedProject) {
      showErrorModal('No project selected');
      return;
    }

    const activeExists = projectTasks.some(t => t.name === 'Die creation' && t.status !== 'completed' && t.status !== 'rejected');
    if (activeExists) {
      showErrorModal('An active Die creation task already exists in this project');
      return;
    }

    try {
      const response = await api.post(`/pm/projects/${selectedProject.id}/insert-die-creation`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.ok) {
        upsertEnabledTaskStep(response.data.step_order);
        await fetchProjectTasks(selectedProject.id);
        setError(null);
        alert('Die creation task inserted and assigned to you!');
      }
    } catch (err) {
      console.error('Failed to insert Die creation task:', err);
      showErrorModal(err.response?.data?.error || err.message || 'Failed to insert Die creation task');
    }
  };

  const handleInsertRecreationTask = async () => {
    if (!selectedProject) {
      showErrorModal('No project selected');
      return;
    }

    const activeExists = projectTasks.some(t => t.name === 'Recreation' && t.status !== 'completed' && t.status !== 'rejected');
    if (activeExists) {
      showErrorModal('An active Recreation task already exists in this project');
      return;
    }

    try {
      const response = await api.post(`/pm/projects/${selectedProject.id}/insert-recreation`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.ok) {
        upsertEnabledTaskStep(response.data.step_order);
        await fetchProjectTasks(selectedProject.id);
        setError(null);
        alert('Recreation task inserted and assigned to you!');
      }
    } catch (err) {
      console.error('Failed to insert Recreation task:', err);
      showErrorModal(err.response?.data?.error || err.message || 'Failed to insert Recreation task');
    }
  };

  const handleInsertScanningTask = async () => {
    if (!selectedProject) {
      showErrorModal('No project selected');
      return;
    }

    const activeExists = projectTasks.some(
      (t) => t.name === 'Scanning task' && t.status !== 'completed' && t.status !== 'rejected'
    );
    if (activeExists) {
      showErrorModal('An active Scanning task already exists in this project');
      return;
    }

    try {
      const response = await api.post(
        `/pm/projects/${selectedProject.id}/insert-scanning-task`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.ok) {
        upsertEnabledTaskStep(response.data.step_order);
        await fetchProjectTasks(selectedProject.id);
        setError(null);
        alert('Scanning task inserted and assigned to you!');
      }
    } catch (err) {
      console.error('Failed to insert Scanning task:', err);
      showErrorModal(err.response?.data?.error || err.message || 'Failed to insert Scanning task');
    }
  };

  const handleInsertPiPreparingTask = async () => {
    if (!selectedProject) {
      showErrorModal('No project selected');
      return;
    }

    const activeExists = projectTasks.some(
      (t) => t.name === 'PI preparing' && t.status !== 'completed' && t.status !== 'rejected'
    );
    if (activeExists) {
      showErrorModal('An active PI preparing task already exists in this project');
      return;
    }

    try {
      const response = await api.post(
        `/pm/projects/${selectedProject.id}/insert-pi-preparing`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.ok) {
        upsertEnabledTaskStep(response.data.step_order);
        await fetchProjectTasks(selectedProject.id);
        setError(null);
        alert('PI preparing task inserted and assigned to you!');
      }
    } catch (err) {
      console.error('Failed to insert PI preparing task:', err);
      showErrorModal(err.response?.data?.error || err.message || 'Failed to insert PI preparing task');
    }
  };

  const handleInsertImageExportTask = async () => {
    if (!selectedProject) {
      showErrorModal('No project selected');
      return;
    }

    const activeExists = projectTasks.some(
      (t) => t.name === 'Image Export' && t.status !== 'completed' && t.status !== 'rejected'
    );
    if (activeExists) {
      showErrorModal('An active Image Export task already exists in this project');
      return;
    }

    try {
      const response = await api.post(
        `/pm/projects/${selectedProject.id}/insert-image-export`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.ok) {
        upsertEnabledTaskStep(response.data.step_order);
        await fetchProjectTasks(selectedProject.id);
        setError(null);
        alert('Image Export task inserted and assigned to you!');
      }
    } catch (err) {
      console.error('Failed to insert Image Export task:', err);
      showErrorModal(err.response?.data?.error || err.message || 'Failed to insert Image Export task');
    }
  };

  /**
   * Insert any registered flexible task by name.
   *
   * Uses the generic endpoint rather than adding yet another per-task handler
   * and route — the backend keeps one registry of insertable tasks, so a new
   * one needs a button here and a dictionary entry there, nothing more.
   */
  const handleInsertFlexibleTask = async (taskName) => {
    if (!selectedProject) {
      showErrorModal('No project selected');
      return;
    }
    const activeExists = projectTasks.some(
      (t) => t.name === taskName && t.status !== 'completed' && t.status !== 'rejected'
    );
    if (activeExists) {
      showErrorModal(`An active ${taskName} task already exists in this project`);
      return;
    }
    try {
      const response = await api.post(
        `/pm/projects/${selectedProject.id}/insert-task`,
        { task_name: taskName, assign_to_me: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.ok) {
        upsertEnabledTaskStep(response.data.step_order);
        await fetchProjectTasks(selectedProject.id);
        setError(null);
        alert(`${taskName} task inserted and assigned to you!`);
      }
    } catch (err) {
      console.error(`Failed to insert ${taskName} task:`, err);
      showErrorModal(err.response?.data?.error || err.message || `Failed to insert ${taskName} task`);
    }
  };

  const handleInsertMountingRIPQCTask = async () => {
    if (!selectedProject) {
      showErrorModal('No project selected');
      return;
    }

    const activeExists = projectTasks.some(
      (t) => t.name === 'Mounting or RIP & Send QC' && t.status !== 'completed' && t.status !== 'rejected'
    );
    if (activeExists) {
      showErrorModal('An active Mounting or RIP & Send QC task already exists in this project');
      return;
    }

    try {
      const response = await api.post(
        `/pm/projects/${selectedProject.id}/insert-mounting-rip-qc`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.ok) {
        upsertEnabledTaskStep(response.data.step_order);
        await fetchProjectTasks(selectedProject.id);
        setError(null);
        alert('Mounting or RIP & Send QC task inserted and assigned to you!');
      }
    } catch (err) {
      console.error('Failed to insert Mounting or RIP & Send QC task:', err);
      showErrorModal(err.response?.data?.error || err.message || 'Failed to insert Mounting or RIP & Send QC task');
    }
  };

  const handleInsertCMDPathTask = async () => {
    if (!selectedProject) {
      showErrorModal('No project selected');
      return;
    }

    const activeExists = projectTasks.some(
      (t) => t.name === 'CMD Path' && t.status !== 'completed' && t.status !== 'rejected'
    );
    if (activeExists) {
      showErrorModal('An active CMD Path task already exists in this project');
      return;
    }

    try {
      const response = await api.post(
        `/pm/projects/${selectedProject.id}/insert-cmd-path`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.ok) {
        upsertEnabledTaskStep(response.data.step_order);
        await fetchProjectTasks(selectedProject.id);
        setError(null);
        alert('CMD Path task inserted and assigned to you!');
      }
    } catch (err) {
      console.error('Failed to insert CMD Path task:', err);
      showErrorModal(err.response?.data?.error || err.message || 'Failed to insert CMD Path task');
    }
  };

  const getWorkflowSortKey = (task) => {
    if (!task) return 9999;
    const name = (task.name || '').toLowerCase();

    // Special positioning: for Die creation and Recreation, prefer
    // the stable anchor captured when the task was first seen so
    // they stay next to the Preflight round active at insertion.
    if (name === 'die creation' || name === 'recreation') {
      const anchorKey = String(task.id);
      if (specialTaskAnchors[anchorKey] != null) {
        return specialTaskAnchors[anchorKey];
      }
    }

    // Prefer explicit step_order from backend so other rework tasks
    // (e.g. Preflight R2 after rejected approvals) follow backend order.
    if (typeof task.step_order === 'number') {
      return task.step_order;
    }

    // Fallback for any legacy tasks that might be missing step_order.
    if (name === 'preflight') return 1;
    if (name === 'die creation') return 1.1;
    if (name === 'recreation') return 1.2;
    return 9999;
  };

  const handleApprovalRejection = async (taskId, rejectedStep) => {
    const taskToReject = projectTasks.find(t => t.id === taskId);
    if (!taskToReject) return;

    setRejectionModal({
      isOpen: true,
      taskId,
      taskName: taskToReject.name,
      reason: '',
      internalCategories: [],
      externalCategories: []
    });
    
    // Fetch error categories when modal opens
    fetchErrorCategories();
  };

  const fetchErrorCategories = async () => {
    try {
      setErrorCategoriesLoading(true);
      const response = await api.get('/admin/config/error-categories', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const raw = Array.isArray(response.data) ? response.data : [];
      // Ensure every category has a type; default legacy records to 'internal'
      const normalized = raw.map((cat) => ({
        ...cat,
        type: cat.type || 'internal'
      }));
      setErrorCategories(normalized);
    } catch (err) {
      console.error('Failed to fetch error categories:', err);
      setErrorCategories([]);
    } finally {
      setErrorCategoriesLoading(false);
    }
  };

  const toggleErrorCategory = (categoryId, categoryName, categoryType = 'internal') => {
    setRejectionModal((prev) => {
      const key = categoryType === 'external' ? 'externalCategories' : 'internalCategories';
      const existing = Array.isArray(prev[key]) ? prev[key] : [];
      const isSelected = existing.some(c => c === categoryId || c === categoryName);
      
      if (isSelected) {
        return {
          ...prev,
          [key]: existing.filter(c => c !== categoryId && c !== categoryName)
        };
      } else {
        return {
          ...prev,
          [key]: [...existing, categoryId || categoryName]
        };
      }
    });
  };

  const closeRejectionModal = () => {
    setRejectionModal({
      isOpen: false,
      taskId: null,
      taskName: '',
      reason: '',
      internalCategories: [],
      externalCategories: [],
      files: []
    });
  };

  const handleOpenViewAttachments = async (task, projectId) => {
    setViewAttachmentsModal({ isOpen: true, projectId, taskId: task.id, taskName: task.name, files: [], loading: true });
    try {
      const res = await api.get(`/projects/${projectId}/rejection-files`, {
        params: { task_id: task.id },
        headers: { Authorization: `Bearer ${token}` }
      });
      setViewAttachmentsModal(prev => ({ ...prev, files: res.data.files || [], loading: false }));
    } catch (err) {
      console.error('Failed to load attachments:', err);
      setViewAttachmentsModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDownloadRejectionFile = async (fileId, filename) => {
    try {
      const res = await api.get(`/rejection-files/${fileId}`, {
        params: { download: '1' },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const handleRejectionSubmit = async () => {
    const taskId = rejectionModal.taskId;
    const taskName = rejectionModal.taskName;
    const reason = (rejectionModal.reason || '').trim();
    const internalCategories = Array.isArray(rejectionModal.internalCategories) ? rejectionModal.internalCategories : [];
    const externalCategories = Array.isArray(rejectionModal.externalCategories) ? rejectionModal.externalCategories : [];
    const allCategories = [...internalCategories, ...externalCategories];

    if (!taskId) return;
    if (!reason) {
      alert('Reason is required');
      return;
    }
    if (allCategories.length === 0) {
      alert('Please select at least one error category');
      return;
    }
    // Evidence is mandatory. Checked here as well as on the button so the
    // rejection cannot go through if the modal state is reached another way.
    if ((rejectionModal.files || []).length === 0) {
      alert('Please attach at least one file showing the error before rejecting.');
      return;
    }

    let confirmMessage = `Are you sure you want to reject ${taskName}? This will add previous tasks back to the project.`;

    if (taskName === 'Prepress , QC (self QC)') {
      confirmMessage = `Are you sure you want to reject ${taskName}? This will add a Preflight task again next to this Prepress QC task.`;
    } else if (taskName === 'Plate and Despatch') {
      confirmMessage = `Are you sure you want to reject ${taskName}? This will add Prepress , QC (self QC), RIP QC, and Plate and Despatch again next to the rejected task.`;
    }

    if (!window.confirm(confirmMessage)) return;

    try {
      // Upload the evidence FIRST. Attachments are mandatory, so a rejection must
      // never be recorded without them — previously the upload ran afterwards and
      // a failure was swallowed, leaving a rejection with no evidence at all.
      const filesToUpload = rejectionModal.files || [];
      if (!selectedProject?.id) {
        showErrorModal('No project selected — cannot attach the rejection files.');
        return;
      }
      try {
        const formData = new FormData();
        formData.append('task_id', taskId);
        filesToUpload.forEach(f => formData.append('files', f));
        await api.post(`/projects/${selectedProject.id}/rejection-files`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
      } catch (uploadErr) {
        // Surface the real cause. A rejected file type or an oversized file comes
        // back as JSON, but a proxy limit (413) or a network drop does not — the
        // generic wording alone made those indistinguishable.
        const status = uploadErr.response?.status;
        const serverMsg = uploadErr.response?.data?.error;
        let detail = serverMsg;
        if (!detail) {
          if (status === 413) {
            detail = 'The file is too large for the server to accept. Try a smaller file, or zip it.';
          } else if (status) {
            detail = `The server rejected the upload (error ${status}).`;
          } else {
            detail = 'The upload did not reach the server — check your connection and try again.';
          }
        }
        showErrorModal(`${detail}\n\nThe task was NOT rejected.`);
        return;   // abort — no rejection without evidence
      }

      const taskToReject = projectTasks.find(t => t.id === taskId);
      // Skip time event for approval tasks — they do not affect shift working status.
      // This mirrors the isApprovalTask guard in handleTaskStatusChange.
      const isApprovalTask = taskToReject?.task_type === 'approval';
      if (taskToReject && !isApprovalTask) {
        const jobCode = selectedProject?.job_code || '';
        const projectName = selectedProject?.title || '';
        const currentTaskName = taskToReject?.name || '';
        const description = projectName || jobCode || currentTaskName ? `${projectName} (${jobCode}) - ${currentTaskName}`.trim() : '';
        const resolveCategoryLabel = (value) => {
          const found = errorCategories.find(cat => cat.id === value || cat._id === value || cat.name === value);
          return found ? found.name : value;
        };
        const displayCategories = allCategories.map(resolveCategoryLabel);
        const notes = `Categories: ${displayCategories.join(', ')}\nReason: ${reason}`;
        await api.post('/time/event', {
          event: 'task_rejected',
          task_id: taskId,
          description: description,
          notes: notes
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      await api.post('/lead/reject-approval', {
        task_id: taskId,
        reason,
        // Send split internal/external lists while also providing combined list for
        // backwards compatibility with any legacy processing.
        internal_error_categories: internalCategories,
        external_error_categories: externalCategories,
        error_categories: allCategories
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      closeRejectionModal();

      const updatedProjects = await fetchProjects();
      if (updatedProjects) {
        const refreshed = updatedProjects.find(p => p.id === selectedProject.id);
        if (refreshed) {
          setSelectedProject(refreshed);
        }
      }
      await fetchProjectTasks(selectedProject.id);
      alert('Task rejected successfully. Previous tasks have been added back to the workflow.');
    } catch (err) {
      console.error('Failed to reject task:', err);
      showErrorModal(err.response?.data?.error || 'Failed to reject task');
    }
  };

  const handleGlobalEvent = async (eventType, subType = null) => {
    if (eventType === 'shift_start') {
      const input = window.prompt("Type 'start' to confirm starting your shift:");
      if (input !== 'start') return;
    }
    if (eventType === 'shift_end') {
      const input = window.prompt("Type 'end' to confirm ending your shift:");
      if (input !== 'end') return;
    }

    try {
      const payload = {
        event: eventType
      };

      if (eventType === 'break_start') payload.break_type = subType;
      if (eventType === 'break_end') payload.break_type = subType;
      if (eventType === 'idle_start') payload.idle_type = subType;
      if (eventType === 'idle_end') payload.idle_type = subType;

      console.log('Sending payload:', payload);

      // If starting a break/idle or ending shift, pause any active task first.
      // Approval tasks are excluded — they don't need to be paused for breaks.
      if ((eventType === 'break_start' || eventType === 'idle_start' || eventType === 'shift_end') && shiftSummary.active_task_id) {
        const activeTaskInfo = findTaskAndProjectById(shiftSummary.active_task_id);
        const isApproval = activeTaskInfo?.task?.task_type === 'approval';
        if (!isApproval) {
          await handleTaskStatusChange(shiftSummary.active_task_id, 'pause', { silent: true });
        }
      }

      const response = await api.post('/time/event', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Event response:', response.data);

      if (eventType === 'shift_start' || eventType === 'shift_end') {
        setUsedBreakTypes([]);
      }

      // Clocked in after the shift start plus grace — ask for the reason, which
      // is emailed to the TL/PM immediately once submitted.
      if (eventType === 'shift_start' && response.data?.late?.is_late) {
        setLateModal({ open: true, ...response.data.late });
        setLateReason('');
        setLateReasonError('');
      } else if (eventType === 'shift_start') {
        // Shift just started with nothing running, so ask what they want to
        // work on. Skipped when they are late: the late-reason dialog comes
        // first and two stacked modals would be confusing.
        openWhatNext();
      }

      // Shift ran past the limit — ask why now, while they remember.
      if (eventType === 'shift_end' && response.data?.long_shift?.is_long) {
        setLongShiftModal({ open: true, ...response.data.long_shift });
        setLongShiftReason('');
        setLongShiftError('');
      }

      if (eventType === 'break_start' && subType) {
        setUsedBreakTypes((prev) => (
          prev.includes(subType) ? prev : [...prev, subType]
        ));
      }

      // If the cafeteria meal service issued a PIN for this meal break, show it
      // so the worker can present it to the cafeteria staff.
      if (eventType === 'break_start' && response.data?.meal_pin) {
        setMealPinModal({
          open: true,
          pin: String(response.data.meal_pin),
          meal: response.data.meal || subType || '',
        });
      }

      // Wait for shift summary to update
      await fetchShiftSummary();
    } catch (err) {
      console.error('Failed to update status:', err);
      console.error('Error response:', err.response?.data);
      const backendMessage = err?.response?.data?.error || err?.response?.data?.message;
      showErrorModal(backendMessage || 'Failed to update status');
    }
  };

  const fetchTaskTimings = async (taskId) => {
    if (!taskId) return;
    try {
      const response = await api.get(`/pm/tasks/${taskId}/timings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const timings = calculateTimeBreakdown(response.data);
      setTaskTimings(prev => ({
        ...prev,
        [taskId]: timings
      }));
    } catch (err) {
      console.error(`Failed to fetch timings for task ${taskId}`, err);
    }
  };

  const calculateTimeBreakdown = (events) => {
    let yesterday = 0;
    let today = 0;
    let total = 0;
    let currentStart = null;
    let firstStart = null;
    let lastEventTime = null;
    
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    
    const yesterdayDate = new Date(todayDate);
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);

    // Sort events by timestamp just in case
    const sortedEvents = [...events].sort((a, b) => new Date(a.ts) - new Date(b.ts));

    for (const event of sortedEvents) {
      const eventTime = new Date(event.ts);
      lastEventTime = eventTime;
      
      if (event.event === 'start' || event.event === 'resume') {
        currentStart = eventTime;
        if (!firstStart) firstStart = eventTime; // capture first start/resume
      } else if ((event.event === 'pause' || event.event === 'hold' || event.event === 'end' || event.event === 'query' || event.event === 'task_rejected') && currentStart) {
        const duration = (eventTime - currentStart) / (1000 * 60 * 60); // in hours
        
        // Check if the session belongs to today or yesterday (simplified logic: based on end time)
        // A more accurate logic would split the duration if it crosses midnight, but for now:
        if (eventTime >= todayDate) {
          today += duration;
        } else if (eventTime >= yesterdayDate && eventTime < todayDate) {
          yesterday += duration;
        }
        
        total += duration;
        currentStart = null;
      }
    }

    // If currently running (started but not stopped), add time until now
    if (currentStart) {
      const now = new Date();
      const duration = (now - currentStart) / (1000 * 60 * 60);
      
      if (now >= todayDate) {
        today += duration;
      } else if (now >= yesterdayDate && now < todayDate) {
        yesterday += duration;
      }
      total += duration;
      lastEventTime = now;
    }

    return {
      yesterday: yesterday.toFixed(2),
      today: today.toFixed(2),
      total: total.toFixed(2),
      startTime: firstStart ? firstStart.toISOString() : null,
      endTime: lastEventTime ? lastEventTime.toISOString() : null
    };
  };

  const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined) return '-';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return `${h}h ${m}m ${s}s`;
  };

  const handleViewActivities = async (task) => {
    setCurrentTaskTitle(getDisplayTaskName(task));
    try {
      const [timingsRes, taskRes] = await Promise.all([
        api.get(`/pm/tasks/${task.id}/timings`, { headers: { Authorization: `Bearer ${token}` } }),
        api.get(`/pm/projects/${task.project_id || selectedProject?.id}/tasks`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      
      const events = timingsRes.data;
      const processedEvents = [];
      
      for (let i = 0; i < events.length; i++) {
        const event = events[i];
        let duration = null;
        
        // Calculate duration for closing events
        if (['pause', 'hold', 'query', 'end'].includes(event.event)) {
             const prevEvent = events[i-1];
             if (prevEvent && ['start', 'resume'].includes(prevEvent.event)) {
                 const start = new Date(prevEvent.ts);
                 const end = new Date(event.ts);
                 duration = (end - start) / 1000;
             }
        }

        processedEvents.push({
          ...event,
          duration: duration
        });
      }

      // Merge activity_log entries (hold/pause reasons) into the matching time events.
      // Instead of adding them as separate rows, find the time event with the same
      // event type and nearest timestamp (within 5 seconds) and attach the reason there.
      const taskDoc = Array.isArray(taskRes.data) ? taskRes.data.find((t) => t.id === task.id) : null;
      const activityLogEntries = (taskDoc?.activity_log || task.activity_log || []);

      activityLogEntries.forEach((entry) => {
        const entryTs = new Date(entry.at).getTime();
        // Find the closest matching time event by event type + timestamp
        let bestMatch = null;
        let bestDiff = Infinity;
        processedEvents.forEach((ev) => {
          if (ev.event === entry.event) {
            const diff = Math.abs(new Date(ev.ts).getTime() - entryTs);
            if (diff < bestDiff && diff < 5000) { // within 5 seconds
              bestDiff = diff;
              bestMatch = ev;
            }
          }
        });
        if (bestMatch) {
          bestMatch.reason = entry.reason;
        }
      });

      // Sort newest first — no duplicate rows
      const allEvents = [...processedEvents].sort(
        (a, b) => new Date(b.ts) - new Date(a.ts)
      );
      
      setCurrentTaskActivities(allEvents);
      setActivityModalOpen(true);
    } catch (err) {
      console.error("Failed to fetch activities", err);
      showErrorModal("Failed to fetch activity log");
    }
  };

  const handleOpenDialog = (project = null) => {
    if (project) {
      // Editing: honour what was actually saved on the project. A PM may have
      // removed Epson for this job even though the client normally wants it, and
      // re-applying the client default here would silently undo that.
      setEpsonTaskOverride(Array.isArray(project.enabled_tasks) && project.enabled_tasks.includes(7));
      setPlateTaskOverride(Array.isArray(project.enabled_tasks) && project.enabled_tasks.includes(8));
      setCurrentProject({
        ...project,
        job_location: project.job_location || '',
        job_nature: project.job_nature || '',
        job_type: project.job_type || '',
        priority: project.priority || 'normal',
        complexity: project.complexity || 'simple',  // Include complexity
        enabled_tasks: project.enabled_tasks || [1, 2, 3, 4, 5, 6, 7, 8]
      });
      setIsEditingProject(true);
    } else {
      setEpsonTaskOverride(false);
      setPlateTaskOverride(false);
      setCurrentProject({
        title: '',
        client: '',
        job_code: '',
        job_location: '',
        job_nature: '',
        job_type: '',
        priority: 'normal',
        complexity: 'simple',  // Default complexity
        deadline: '',
        description: '',
        enabled_tasks: [1, 2, 3, 4, 5, 6, 7, 8]
      });
      setIsEditingProject(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEpsonTaskOverride(false);
    setPlateTaskOverride(false);
    setClientDropdownOpen(false);
    setClientDropdownFilter('');
    setCurrentProject({
      title: '',
      client: '',
      job_code: '',
      job_location: '',
      job_nature: '',
      job_type: '',
      priority: 'normal',
      complexity: 'simple',  // Default complexity
      deadline: '',
      description: '',
      enabled_tasks: [1, 2, 3, 4, 5, 6, 7, 8]
    });
  };

  const handleSaveProject = async () => {
    try {
      // Auto-set enabled_tasks based on
      // while still allowing manual overrides in the UI.
      const normalizedNature = (currentProject.job_nature || '').toLowerCase();
      let enabledTasks = getEnabledTasksForClient(currentProject.client, currentProject.job_nature);

      // Epson Printout (step 7) — opt-in for all project types
      if (epsonTaskOverride) {
        if (!enabledTasks.includes(7)) enabledTasks = [...enabledTasks, 7];
      } else {
        enabledTasks = enabledTasks.filter((step) => step !== 7);
      }

      // Plate and Despatch (step 8) — opt-in for all project types
      if (plateTaskOverride) {
        if (!enabledTasks.includes(8)) enabledTasks = [...enabledTasks, 8];
      } else {
        enabledTasks = enabledTasks.filter((step) => step !== 8);
      }

      const { tasks: _tasks, id: _id, ...projectFields } = currentProject;
      const projectToSave = {
        ...projectFields,
        enabled_tasks: enabledTasks
      };
      
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };
      
      if (isEditingProject) {
        // When editing, just update project metadata (title, client, complexity, etc.)
        // without creating a new version
        try {
          await api.put(`/pm/projects/${currentProject.id}`, projectToSave, config);
          fetchProjects();
          handleCloseDialog();
        } catch (err) {
          console.error('Error updating project:', err);
          showErrorModal('Failed to update project');
        }
      } else {
        // Creating a new project
        await createProjectWithTasks(projectToSave);
        handleCloseDialog();
      }
    } catch (err) {
      console.error('Error saving project:', err);
      showErrorModal('Failed to save project');
    }
  };

  const handleDeleteProject = async (id) => {
    if (window.confirm('Are you sure you want to delete this project? This will also delete all associated tasks.')) {
      try {
        await api.delete(`/pm/projects/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchProjects();
        if (selectedProject && selectedProject.id === id) {
          setSelectedProject(null);
          setProjectTasks([]);
        }
      } catch (err) {
        showErrorModal('Failed to delete project');
      }
    }
  };

  const handleOpenCancelProject = (project) => {
    setCancelProjectModal({ isOpen: true, projectId: project.id, projectTitle: project.title, comment: '' });
  };

  const handleCancelProject = async () => {
    const { projectId, comment } = cancelProjectModal;
    if (!comment.trim()) return;
    try {
      await api.post(`/pm/projects/${projectId}/cancel`, { comment }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCancelProjectModal({ isOpen: false, projectId: null, projectTitle: '', comment: '' });
      fetchProjects();
      if (selectedProject && selectedProject.id === projectId) {
        setSelectedProject(null);
        setProjectTasks([]);
      }
    } catch (err) {
      showErrorModal(err?.response?.data?.error || 'Failed to cancel project');
    }
  };

  const handleOpenHoldPause = (taskId, action) => {
    setHoldPauseModal({ isOpen: true, taskId, action, reason: '' });
  };

  const handleHoldPauseSubmit = async () => {
    const { taskId, action, reason } = holdPauseModal;
    if (!reason.trim()) return;
    try {
      // Re-use the existing handleTaskStatusChange but pass the reason through
      // by closing modal first then delegating the actual status update.
      setHoldPauseModal({ isOpen: false, taskId: null, action: '', reason: '' });
      await handleTaskStatusChange(taskId, action, { reason });
    } catch (err) {
      showErrorModal(err?.response?.data?.error || `Failed to ${action} task`);
    }
  };

  const handleCreateVersion2 = async (originalProject) => {
    // Check if Plate and Despatch task (step 8) has started
    const projectTasks = allProjectTasks[originalProject.id];
    if (!projectTasks) {
      showErrorModal('Unable to check task status. Please try again.');
      return;
    }

    const plateTask = projectTasks.find(task => task.name === 'Plate and Despatch');
    if (!plateTask || plateTask.status !== 'not_started') {
      showErrorModal('Cannot create a new version: Plate and Despatch has started or is missing.');
      return;
    }

    // Require that at least one task has been started before allowing a new version
    const hasStartedTask = projectTasks.some(task => task.status && !['not_started', 'pending'].includes(task.status));
    if (!hasStartedTask) {
      showErrorModal('Start at least one task before creating a new version.');
      return;
    }

    // Compute next version labels up front for confirmation and payload reuse
    const nextTitleInfo = getNextVersionLabel(originalProject.title, false);
    const nextJobCodeInfo = getNextVersionLabel(originalProject.job_code || '', true);
    // For project title, drop the version suffix entirely (keep base name only)
    const nextTitleLabel = nextTitleInfo.base || originalProject.title;
    const nextVersionNumber = nextTitleInfo.nextVersion || 2;

    if (!window.confirm(`Create New Version of "${nextTitleLabel}"? This will create a new project with the same settings and complete all tasks in the original version.`)) {
      return;
    }

    try {
      // First, complete all tasks in the original project (except completed/rejected ones)
      const tasksToComplete = projectTasks.filter(task => 
        task.status !== 'completed' && task.status !== 'rejected'
      );

      console.log(`Completing ${tasksToComplete.length} tasks in original project...`);
      
      for (const task of tasksToComplete) {
        try {
          await api.post(`/pm/task-status/${task.id}`, 
            { action: 'complete' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          console.log(`Completed task: ${task.name}`);
        } catch (err) {
          console.error(`Failed to complete task ${task.name}:`, err);
          // Continue with other tasks even if one fails
        }
      }

      // Refresh project tasks to confirm completion
      await fetchProjectTasks(originalProject.id);
      console.log('Original project tasks completed');

      // Create next version with incremented V number (V2, V3, ...)
      // Only include essential fields to avoid validation issues
      const version2Data = {
        title: nextTitleLabel,
        job_code: nextJobCodeInfo.nextLabel,
        client: originalProject.client,
        job_location: originalProject.job_location || '',
        job_nature: originalProject.job_nature || '',
        job_type: originalProject.job_type || '',
        priority: originalProject.priority || 'normal',
        complexity: originalProject.complexity || 'simple',
        description: originalProject.description || '',
        // Re-apply client/job-nature specific enabled tasks (e.g., disable
        // Single-up approval if client config says so, and adjust for
        // Corrugation/Rotary job nature defaults).
        enabled_tasks: getEnabledTasksForClient(originalProject.client, originalProject.job_nature)
      };

      console.log('Creating version 2 project...');
      console.log('Version 2 data:', version2Data);
      
      const createResponse = await api.post('/pm/projects', version2Data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Version 2 created:', createResponse.data);

      fetchProjects();
      closeErrorModal();
      alert('Version 2 created successfully! All tasks in the original project have been completed.');
    } catch (err) {
      console.error('Failed to create version 2:', err);
      console.error('Error response:', err.response?.data);
      
      // Display more helpful error message
      const errorMessage = err.response?.data?.error || err.response?.data?.details || 'Failed to create project version 2';
      showErrorModal(`Failed to create Version 2: ${JSON.stringify(errorMessage)}`);
    }
  };

  const handleOpenAssignDialog = (task) => {
    setSelectedTaskForAssign(task);
    setSelectedUserForAssign(task.assigned_to || '');
    setAssignModalOpen(true);
  };

  const handleAssignTask = async () => {
    if (!selectedUserForAssign) return;
    
    try {
      // Always assign the selected task first
      await api.post('/lead/assign-task', {
        task_id: selectedTaskForAssign.id,
        user_id: selectedUserForAssign
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Optional linked approval assignments:
      // 1) When assigning Preflight, optionally also assign "Single-up approval" to same user
      // 2) When assigning Prepress , QC (self QC), optionally also assign "Layout approval" to same user
      if (selectedProject && Array.isArray(projectTasks) && selectedTaskForAssign?.name) {
        let linkedTaskName = null;
        let confirmationMessage = '';

        if (selectedTaskForAssign.name === 'Preflight') {
          linkedTaskName = 'Single-up approval';
          confirmationMessage = 'Also assign "Single-up approval" to this user?';
        } else if (selectedTaskForAssign.name === 'Prepress , QC (self QC)') {
          linkedTaskName = 'Layout approval';
          confirmationMessage = 'Also assign "Layout approval" to this user?';
        }

        if (linkedTaskName && window.confirm(confirmationMessage)) {
          const linkedTask = projectTasks.find(t => 
            t.name === linkedTaskName &&
            t.status !== 'completed' &&
            t.status !== 'rejected'
          );

          if (linkedTask) {
            try {
              await api.post('/lead/assign-task', {
                task_id: linkedTask.id,
                user_id: selectedUserForAssign
              }, {
                headers: { Authorization: `Bearer ${token}` }
              });
            } catch (err) {
              console.error('Failed to assign linked approval task', err);
              // Do not block main assignment on this failure
            }
          }
        }
      }

      setAssignModalOpen(false);

      // Refresh tasks after assignment. Epson roles rely on tasks embedded in
      // the project list rather than the per-project /tasks API, so handle
      // their refresh separately to avoid clearing the task list.
      if (user?.role === 'epson_lead') {
        const updatedProjects = await fetchProjects();
        if (updatedProjects && selectedProject && selectedProject.id) {
          const updatedProject = updatedProjects.find((p) => p.id === selectedProject.id);
          if (updatedProject) {
            setSelectedProject(updatedProject);
            if (Array.isArray(updatedProject.tasks)) {
              setProjectTasks(updatedProject.tasks);
            }
          }
        }
      } else if (selectedProject && selectedProject.id) {
        await fetchProjectTasks(selectedProject.id);
      }
    } catch (err) {
      showErrorModal('Failed to assign task');
    }
  };

  const handleUnassignTask = async (task) => {
    if (!task) return;
    if (!selectedProject || !selectedProject.id) {
      showErrorModal('No project selected');
      return;
    }

    const confirmed = window.confirm('Remove assignment from this task?');
    if (!confirmed) return;

    try {
      await api.post('/lead/assign-task', {
        task_id: task.id,
        user_id: null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await fetchProjectTasks(selectedProject.id);
    } catch (err) {
      showErrorModal('Failed to unassign task');
    }
  };

  const findTaskAndProjectById = (taskId) => {
    if (!taskId) return null;

    // Search across all loaded project tasks for the active task
    for (const [projectId, tasks] of Object.entries(allProjectTasks || {})) {
      const task = (tasks || []).find(t => t.id === taskId);
      if (task) {
        const project = projects.find(p => String(p.id) === String(projectId));
        return { task, project };
      }
    }

    return null;
  };

  const handleTaskStatusChange = async (id, action, options = {}) => {
    const { bypassQrCheck = false, qrCodeOverride = null, reason = null } = options;

    // Hold, Pause and Query all require a reason. If none was supplied, open the
    // reason modal which calls this function again with the reason filled in.
    // Query was previously exempt, which left "why is this on query?" unanswerable
    // on the Priority Projects board.
    if ((action === 'hold' || action === 'pause' || action === 'query') && !reason) {
      handleOpenHoldPause(id, action);
      return;
    }

    // Block task actions when the user has exceeded their break time limit.
    if (shiftSummary.break_exceeded) {
      const breakName = typeof shiftSummary.break_exceeded === 'object'
        ? (shiftSummary.break_exceeded.type || 'break')
        : 'break';
      showErrorModal(`Your ${breakName} break has exceeded the allowed time. Please contact your Team Leader or Manager to resume work.`);
      return;
    }

    // Block all task state changes while the user is on a break.
    if (shiftSummary.current_status === 'break') {
      showErrorModal('You are currently on a break. Please end your break before performing task actions.');
      return;
    }

    try {
      let taskToUpdate = projectTasks.find(t => t.id === id);
      let projectContext = selectedProject || null;
      let tasksContext = projectTasks;

      // If the task is not in the currently selected project's tasks (for example,
      // when pausing an "active" task from the global break/idle controls),
      // fall back to searching across all loaded projects.
      if (!taskToUpdate) {
        const found = findTaskAndProjectById(id);
        if (!found || !found.project || !found.task) {
          // Task not in loaded projects. For pause/hold of the user's own active
          // task, fetch the project tasks from the backend so we can still build
          // a valid PUT payload and unblock the user.
          const projId = shiftSummary.active_project_id;
          if (projId && (action === 'pause' || action === 'hold') && id === shiftSummary.active_task_id) {
            try {
              const projTasksRes = await api.get(`/pm/projects/${projId}/tasks`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              const fetchedTasks = projTasksRes.data || [];
              const fetchedTask = fetchedTasks.find(t => t.id === id);
              if (!fetchedTask) {
                showErrorModal('Task not found. It may have already been paused or completed.');
                fetchShiftSummary();
                return;
              }
              const fetchedProject = projects.find(p => String(p.id) === String(projId))
                || { id: projId, title: shiftSummary.active_project_title, job_code: shiftSummary.active_project_job_code };
              taskToUpdate = fetchedTask;
              projectContext = fetchedProject;
              tasksContext = fetchedTasks;
            } catch (_fetchErr) {
              showErrorModal('Could not load task details. Please refresh the page.');
              return;
            }
          } else {
            return;
          }
        } else {
          taskToUpdate = found.task;
          projectContext = found.project;
          tasksContext = allProjectTasks?.[projectContext.id] || [];
        }
      }

      // Block all task actions (except by admin/PM) when the task is already completed.
      // This prevents the auto-pause mechanism from reverting a completed task back to
      // paused when a user's stale shift summary still points to it as the active task.
      if (taskToUpdate.status === 'completed' && action !== 'complete') {
        if (user?.role !== 'admin' && user?.role !== 'pm') {
          // Silently refresh shift summary so the stale pointer clears itself.
          fetchShiftSummary();
          return;
        }
      }

      // Confirm before completing a task, similar to shift end.
      // For QR-guarded tasks, only ask after QR has been successfully
      // verified to avoid double prompts.
      if (action === 'complete') {
        const needsQr = requiresQrBeforeCompletion(taskToUpdate);
        const hasQrVerified = !!qrVerifiedTasks[id];

        if (!needsQr || hasQrVerified) {
          const input = window.prompt("Type 'end' to confirm completing this task:");
          if (input !== 'end') {
            return;
          }
        }
      }

      // Enforce assignment before starting any task: a user cannot start a
      // task unless it is explicitly assigned to them.
      //
      // Business exceptions for Epson/Production roles:
      // - Epson roles (epson, epson_lead) can start "Epson Printout" even if it is
      //   unassigned or assigned to someone else.
      // - Production roles (production, production_lead) can start "Plate and Despatch"
      //   even if it is unassigned or assigned to someone else.
      if (action === 'start') {
        const isLeadException =
          (taskToUpdate.name === 'Epson Printout' && (user?.role === 'epson_lead' || user?.role === 'epson')) ||
          (taskToUpdate.name === 'Plate and Despatch' && (user?.role === 'production_lead' || user?.role === 'production'));

        if (!isLeadException) {
          if (!taskToUpdate.assigned_to || taskToUpdate.assigned_to !== user?.id) {
            showErrorModal('You cannot start this task until it is assigned to you.');
            return;
          }
        }

        // When Epson Printout exists for this project, Plate and Despatch
        // must not start until Epson Printout has been fully completed.
        if (taskToUpdate.name === 'Plate and Despatch') {
          const jobNature = (projectContext?.job_nature || '').toLowerCase();
          const isCorrugation = jobNature === 'corrugation';
          const isRotary = jobNature === 'rotary';
          const isNotebook = jobNature === 'notebook';

          // For Corrugation, Rotary, or Notebook jobs, allow Plate and
          // Despatch to start without Epson dependency.
          if (!isCorrugation && !isRotary && !isNotebook) {
            const projectId = projectContext?.id;
            const fullTasks = projectId
              ? (allProjectTasks[projectId] || tasksContext || [])
              : (tasksContext || []);
            const epsonTask = (fullTasks || []).find((t) => t.name === 'Epson Printout');
            if (epsonTask && epsonTask.status !== 'completed') {
              showErrorModal('Epson Printout must be completed before starting Plate and Despatch.');
              return;
            }
          }
        }
      }

      if (action === 'complete' && taskToUpdate.name === 'Plate and Despatch') {
        const jobNature = (projectContext?.job_nature || '').toLowerCase();
        const isCorrugation = jobNature === 'corrugation';
        const isRotary = jobNature === 'rotary';
        const isNotebook = jobNature === 'notebook';

        // For Corrugation, Rotary, or Notebook jobs, do not require Epson Printout
        // to be completed before Plate and Despatch.
        if (!isCorrugation && !isRotary && !isNotebook) {
          // Check if Epson Printout task is already completed by anyone
          const projectId = projectContext?.id;
          const fullTasks = projectId
            ? (allProjectTasks[projectId] || tasksContext || [])
            : (tasksContext || []);
          const epsonTask = (fullTasks || []).find((t) => t.name === 'Epson Printout');
          if (epsonTask && epsonTask.status !== 'completed') {
            showErrorModal('Epson Printout must be completed before completing Plate and Despatch.');
            return;
          }
        }
      }

      if (action === 'complete' && !bypassQrCheck && requiresQrBeforeCompletion(taskToUpdate)) {
        if (!qrVerifiedTasks[id]) {
          setPendingCompletionTask(taskToUpdate);
          setQrModalOpen(true);
          setManualQrCode('');
          setQrScanError('');
          setQrScanResult('');
          return;
        }
      }

      // Check if Prepress QC task completion is blocked by incomplete CMD tasks
      // Only check for the explicit "CMD" task (step 3), NOT Epson Printout
      if (action === 'complete' && taskToUpdate.name === 'Prepress , QC (self QC)') {
        const cmdTasks = tasksContext.filter(t => t.name === 'CMD');
        const incompleteCmdTasks = cmdTasks.filter(t => !['completed', 'rejected'].includes(t.status));
        
        if (incompleteCmdTasks.length > 0) {
          const taskNames = incompleteCmdTasks.map(t => t.name).join(', ');
          showErrorModal(`Cannot complete Prepress QC. The following CMD tasks must be completed first: ${taskNames}`);
          return;
        }
      }

      // CMD QC can only start after all CMD tasks for this project are completed.
      if (action === 'start' && taskToUpdate.name === 'CMD QC') {
        const cmdTasks = tasksContext.filter(t => t.name === 'CMD');
        const incompleteCmdTasks = cmdTasks.filter(t => !['completed', 'rejected'].includes(t.status));
        if (incompleteCmdTasks.length > 0) {
          showErrorModal('Cannot start CMD QC until all CMD tasks are completed first.');
          return;
        }
      }

      // Validate that previous task is completed before allowing start.
      // Runs for ALL task states (not_started, pending, paused, on_hold) so
      // a user cannot resume a paused task out of order when a new round of
      // an earlier step was inserted by a rejection.
      if (action === 'start') {
        const _seqFlexNames = new Set([
          'CMD', 'CMD QC', 'CMD Path', 'Die creation', 'Recreation',
          'Scanning', 'Scanning task', 'PI preparing', 'Image Export',
          'Image Duplication',
          'Mounting or RIP & Send QC', 'Mounting RIP QC'
        ]);
        const isFlexibleTask =
          _seqFlexNames.has(taskToUpdate.name) ||
          taskToUpdate.team === 'cmd' ||
          taskToUpdate.is_flexible === true;

        if (!isFlexibleTask && (taskToUpdate.step_order || 0) > 1) {
          // Use allProjectTasks (full list from project embed) rather than
          // tasksContext which is role-filtered and may omit predecessor tasks
          // that belong to a different team (e.g. Preflight not visible to a
          // prepress-only user).
          const allTasksForProject =
            (projectContext?.id && allProjectTasks[projectContext.id])
              ? allProjectTasks[projectContext.id]
              : tasksContext;

          // Find the immediate predecessor: task with the highest step_order
          // that is still less than the current task's step_order, excluding
          // flexible/CMD tasks. This works correctly after rejections shift
          // step_orders — no hardcoded step numbers needed.
          const predecessorCandidates = allTasksForProject.filter(
            t => typeof t.step_order === 'number'
              && t.step_order < taskToUpdate.step_order
              && !_seqFlexNames.has(t.name)
              && t.team !== 'cmd'
              && t.is_flexible !== true
          );
          const previousTask = predecessorCandidates.length > 0
            ? predecessorCandidates.reduce((a, b) => a.step_order > b.step_order ? a : b)
            : null;

          if (previousTask && !['completed', 'rejected'].includes(previousTask.status)) {
            showErrorModal(`Cannot start this task. "${previousTask.name}" must be completed first.`);
            return;
          }
        }
      }

      // Prevent starting a new task if another one is already active (except for admins).
      //
      // Business exception:
      // - CMD (and other explicitly flexible tasks) can run in parallel with
      //   other tasks because they are designed to be independent.
      // - Client approval steps (Single-up approval, Layout approval) are
      //   also treated as flexible so that users can continue working on
      //   other tasks while waiting for client feedback.
      // A user can only have one task in progress at a time — EXCEPT for approval tasks.
      // Approval tasks (Single-up approval, Layout approval) represent waiting for a client
      // response, not active work, so a user may have multiple approval tasks running
      // simultaneously alongside each other or alongside normal tasks.
      // shiftSummary.active_task_id is the current user's own active task, so if it is set
      // here it always means THIS user is trying to start a second task.
      // Different users running tasks on the same project are unaffected because their
      // shiftSummary is independent.
      if (action === 'start' && shiftSummary.active_task_id && shiftSummary.active_task_id !== id && user?.role !== 'admin') {
        const isStartingApprovalTask =
          taskToUpdate?.task_type === 'approval' ||
          taskToUpdate?.approval_type === 'Single-up approval' ||
          taskToUpdate?.approval_type === 'Layout approval';

        if (!isStartingApprovalTask) {
          const activeInfo = findTaskAndProjectById(shiftSummary.active_task_id);
          if (activeInfo && activeInfo.task && activeInfo.project) {
            const activeJobCode = activeInfo.project.job_code || '';
            const activeProjectTitle = activeInfo.project.title || '';
            if (activeJobCode) {
              showErrorModal(`You are already working on job "${activeJobCode}" in project "${activeProjectTitle}". Please pause or complete that task before starting a new one.`);
            } else {
              showErrorModal(`You are already working on "${activeInfo.task.name}" in project "${activeProjectTitle}". Please pause or complete that task before starting this task.`);
            }
          } else {
            showErrorModal('You already have another task in progress (possibly in a different project). Please pause or complete it before starting this task.');
          }
          return;
        }
      }

      // Secondary concurrent-task guard: scan all loaded project tasks for any
      // non-approval, non-flexible task this user already has in_progress.
      // This catches the gap where active_task_id is null/stale — e.g. after
      // an approval task completes (approval tasks fire no time events, so
      // active_task_id can be null even while the user has a running task).
      if (action === 'start') {
        const _isApproval =
          taskToUpdate?.task_type === 'approval' ||
          taskToUpdate?.approval_type === 'Single-up approval' ||
          taskToUpdate?.approval_type === 'Layout approval';
        if (!_isApproval && !privilegedRunningTaskBypassRoles.includes(user?.role)) {
          const _flexNames = new Set(['CMD', 'Die creation', 'Recreation', 'Scanning',
            'PI preparing', 'Image Export', 'Image Duplication']);
          let _conflict = null;
          for (const _tasks of Object.values(allProjectTasks || {})) {
            if (_conflict) break;
            _conflict = _tasks.find(
              (_t) =>
                String(_t.assigned_to) === String(user?.id) &&
                _t.status === 'in_progress' &&
                _t.task_type !== 'approval' &&
                _t.approval_type !== 'Single-up approval' &&
                _t.approval_type !== 'Layout approval' &&
                !_flexNames.has(_t.name) &&
                String(_t.id) !== String(id)
            ) || null;
          }
          if (_conflict) {
            const _info = findTaskAndProjectById(_conflict.id);
            const _msg = _info?.project
              ? `You are already working on "${_conflict.name}" in "${_info.project.title}". Please pause or complete it before starting this task.`
              : `You already have "${_conflict.name}" in progress. Please pause or complete it first.`;
            showErrorModal(_msg);
            return;
          }
        }
      }

      let newStatus = taskToUpdate.status;
      let eventType = '';

      switch (action) {
        case 'start':
          newStatus = 'in_progress';
          eventType = taskToUpdate.status === 'on_hold' || taskToUpdate.status === 'paused' ? 'resume' : 'start';
          break;
        case 'pause':
          newStatus = 'paused';
          eventType = 'pause';
          break;
        case 'hold':
          newStatus = 'on_hold';
          eventType = 'hold';
          break;
        case 'query':
          newStatus = 'query';
          eventType = 'query';
          break;
        case 'complete':
          newStatus = 'completed';
          eventType = 'end';
          break;
        default:
          return;
      }

      // Update task status
      const qrCodeToPersist = qrCodeOverride || getStoredQrForTask(id);
      const putPayload = {
        ...taskToUpdate,
        status: newStatus,
        qr_code: qrCodeToPersist || undefined
      };
      // Carry the reason for hold/pause/query so the backend records it in
      // activity_log — that is what the Priority Projects badge reads.
      if ((action === 'hold' || action === 'pause' || action === 'query') && reason) {
        putPayload.hold_pause_reason = reason;
      }
      await api.put(`/pm/tasks/${id}`, putPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Optimistic update: reflect new status in the UI immediately without
      // waiting for the full fetchProjectTasks round-trip.
      setProjectTasks(prev => prev.map(t => t.id === id ? { ...t, status: newStatus } : t));
      if (projectContext?.id) {
        setAllProjectTasks(prev => ({
          ...prev,
          [projectContext.id]: (prev[projectContext.id] || []).map(t => t.id === id ? { ...t, status: newStatus } : t)
        }));
      }

      // Log time event with project and task details in description
      // Skip time events for approval tasks - they should not affect shift status (idle/working)
      const isApprovalTask = taskToUpdate?.task_type === 'approval';
      
      if (!isApprovalTask) {
        const jobCode = projectContext?.job_code || '';
        const projectName = projectContext?.title || '';
        const taskName = taskToUpdate?.name || '';
        const description = projectName || jobCode || taskName ? `${projectName} (${jobCode}) - ${taskName}`.trim() : '';
        
        console.log('Task event - Project:', projectName, 'JobCode:', jobCode, 'Task:', taskName, 'Description:', description);
        
        await api.post('/time/event', {
          event: eventType,
          task_id: id,
          description: description
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      // If completing a task, check if next task should be activated
      if (action === 'complete') {
        await handleTaskCompletion(taskToUpdate);
      }

      // Refresh tasks for this project. Epson leads rely on the cached
      // tasks embedded in the project list (allProjectTasks) instead of
      // the per-project tasks API, which may filter out Epson tasks.
      if (projectContext?.id) {
        if (user?.role === 'epson_lead') {
          const cachedTasks = allProjectTasks[projectContext.id] || tasksContext || [];
          const updatedTasks = cachedTasks.map((t) =>
            t.id === id ? { ...t, status: newStatus } : t
          );

          setAllProjectTasks((prev) => ({
            ...prev,
            [projectContext.id]: updatedTasks
          }));
          setProjectTasks(updatedTasks);
        } else {
          fetchProjectTasks(projectContext.id);
        }
      }
      fetchTaskTimings(id);
      fetchShiftSummary();

      // The operator just stopped working on something, so they now have
      // nothing running. Ask what they want to do next — but only for users
      // an admin opted in, and never for approval steps, which do not occupy
      // the operator at all.
      // `silent` is set when this pause is a side effect of going on a break,
      // going idle or clocking out. Asking "need a new job?" as someone leaves
      // for lunch would be absurd.
      if (!isApprovalTask
          && !options.silent
          && ['pause', 'hold', 'query', 'complete'].includes(action)) {
        openWhatNext();
      }
    } catch (err) {
      console.error(err);
      const apiMessage = err?.response?.data?.error || err?.response?.data?.details;
      showErrorModal(apiMessage || 'Failed to update task status');
    }
  };

  const handleTaskCompletion = async (completedTask) => {
    try {
      // Get enabled tasks for this project
      const enabledTasks = selectedProject?.enabled_tasks || [];
      const filteredTasks = projectTasks.filter(t => enabledTasks.includes(t.step_order));
      
      // Find the next task in the workflow (only among enabled tasks)
      const nextTask = filteredTasks.find(t => 
        t.step_order === completedTask.step_order + 1 && 
        t.status === 'not_started'
      );
      
      if (nextTask) {
        // Activate the next task
        await api.put(`/pm/tasks/${nextTask.id}`, {
          ...nextTask,
          status: 'pending'
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (err) {
      console.error('Failed to activate next task:', err);
    }
  };

  // Handle complexity change modal
  const handleOpenComplexityModal = (task = null) => {
    if (!selectedProject) {
      showErrorModal('No project selected');
      return;
    }

    // Get enabled tasks for this project and, if Preflight is enabled,
    // enforce the "Preflight must be started" rule. If Preflight is not
    // enabled for this project/client, skip that rule entirely.
    const enabledTasks = selectedProject?.enabled_tasks || [];
    const isPreflightEnabled = enabledTasks.includes(1);

    let requiresReason = false;

    if (isPreflightEnabled) {
      const filteredTasks = projectTasks.filter(t => enabledTasks.includes(t.step_order));
      const preflightTask = filteredTasks.find(t => t.step_order === 1);
      const isPreflightStarted = preflightTask && ['in_progress', 'paused', 'on_hold', 'query', 'completed'].includes(preflightTask.status);

      if (!isPreflightStarted) {
        showErrorModal('Please start the preflight task first before setting project complexity');
        return;
      }

      const isPreflightStarter = preflightTask && preflightTask.preflight_started_by === user?.id;
      const isAdmin = user?.role === 'admin';
      requiresReason = !isAdmin && !isPreflightStarter;
    }

    setComplexityModal({
      isOpen: true,
      taskId: selectedProject.id, // use project id in modal
      taskName: selectedProject.title,
      currentComplexity: getComplexityDisplayName(selectedProject?.complexity || 'simple'),
      // Pre-fill with the project's current complexity so the
      // update button is enabled and the user can confirm or change.
      newComplexity: selectedProject?.complexity || 'simple_simple',
      reason: '',
      requiresReason: requiresReason
    });
  };

  const handleCloseComplexityModal = () => {
    setComplexityModal({
      isOpen: false,
      taskId: null,
      taskName: '',
      currentComplexity: '',
      newComplexity: '',
      reason: '',
      requiresReason: false
    });
  };

  const handleComplexityChange = async () => {
    try {
      if (!selectedProject) return;
      const payload = {
        complexity: complexityModal.newComplexity
      };

      if (complexityModal.reason && complexityModal.reason.trim()) {
        payload.reason = complexityModal.reason.trim();
      }

      if (complexityModal.requiresReason && !payload.reason) {
        showErrorModal('Reason is required for complexity changes');
        return;
      }

      await api.put(`/pm/projects/${selectedProject.id}/complexity`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh projects and update the currently selected project with the latest data
      const updatedProjects = await fetchProjects();

      if (updatedProjects && selectedProject && selectedProject.id) {
        const updatedProject = updatedProjects.find(p => p.id === selectedProject.id);
        if (updatedProject) {
          setSelectedProject(updatedProject);
          // Also refresh this project's tasks to reflect any backend-side changes
          await fetchProjectTasks(updatedProject.id);
        } else {
          // Fallback: still refresh tasks with existing id
          await fetchProjectTasks(selectedProject.id);
        }
      }

      handleCloseComplexityModal();
      closeErrorModal();
    } catch (err) {
      console.error('Failed to update complexity:', err);
      showErrorModal(err.response?.data?.error || 'Failed to update complexity');
    }
  };

  const isProjectVisibleToUser = (project) => {
    if (!user) return false;

    // Admin can see all projects
    if (user.role === 'admin') return true;

    const userRole = user.role;
    const projectTasks = allProjectTasks[project.id];

    // If no tasks loaded yet, show it (optimistic)
    if (!projectTasks || projectTasks.length === 0) {
      console.debug('Project visibility: no tasks loaded yet', { projectId: project.id, title: project.title, userRole });
      return true;
    }

    // Helper: map certain task names to an "effective" team (e.g. Epson Printout)
    const getEffectiveTeam = (t) => {
      const name = (t.name || '').toLowerCase();
      if (name.includes('epson printout')) return 'epson';
      return t.team || '';
    };

    // Helper: treats both "completed" and "rejected" as "closed" states for
    // visibility purposes. Any other status means the task is still pending.
    const isTaskClosed = (status) => status === 'completed' || status === 'rejected';

    // Helper: does this team have any tasks in this project that are NOT closed?
    const hasPendingTeamTasks = (team) => {
      const teamTasks = projectTasks.filter((t) => getEffectiveTeam(t) === team);
      if (teamTasks.length === 0) return false;
      return teamTasks.some((t) => !isTaskClosed(t.status));
    };

    // Helper: are all tasks for this team closed (completed or rejected), or no tasks?
    const areTeamTasksCompleted = (team) => {
      const teamTasks = projectTasks.filter((t) => getEffectiveTeam(t) === team);
      if (teamTasks.length === 0) return true;

      // For preflight, treat flexible extras (Die creation, Recreation) as
      // non-blocking for downstream teams. Only the core preflight workflow
      // (Preflight, Single-up approval, etc.) needs to be fully closed before
      // prepress/QC/production can see the project.
      if (team === 'preflight') {
        const coreTasks = teamTasks.filter(
          (t) => t.name !== 'Die creation' && t.name !== 'Recreation'
        );
        if (coreTasks.length > 0) {
          return coreTasks.every((t) => isTaskClosed(t.status));
        }
      }

      return teamTasks.every((t) => isTaskClosed(t.status));
    };

    // PM can always see all projects
    if (userRole === 'pm') {
      console.debug('Project visibility: PM role, always visible', { projectId: project.id, title: project.title, userRole });
      return true;
    }

    // Universal rule: if the user has any task assigned to them that is not
    // yet closed, always show the project — regardless of their role or team.
    const hasAssignedPendingTask = () =>
      projectTasks.some((t) => String(t.assigned_to) === String(user.id) && !isTaskClosed(t.status));

    if (hasAssignedPendingTask()) {
      console.debug('Project visibility: user has assigned pending task', { projectId: project.id, title: project.title, userRole });
      return true;
    }

    // Preflight TL: team view — show when there is any pending preflight work.
    if (userRole === 'preflight_lead') {
      const preflightTasks = projectTasks.filter((t) => 
        getEffectiveTeam(t) === 'preflight' || 
        t.name === 'Die creation' || 
        t.name === 'Recreation' ||
        (t.task_type === 'approval' && t.approval_type === 'Single-up approval')
      );
      const visible = preflightTasks.some((t) => !isTaskClosed(t.status));
      console.debug('Project visibility: preflight_lead role', { projectId: project.id, title: project.title, userRole, visible });
      return visible;
    }

    // Normal CMD workers: fallback handled by the universal assignment check above.
    if (userRole === 'cmd') {
      console.debug('Project visibility: CMD worker (no team tasks)', { projectId: project.id, title: project.title, userRole });
      return false;
    }

    // CMD TL: flexible – can see projects whenever any CMD work is pending.
    if (userRole === 'cmd_lead') {
      const visible = hasPendingTeamTasks('cmd');
      console.debug('Project visibility: CMD lead role (flexible, pending-only)', { projectId: project.id, title: project.title, userRole, visible });
      return visible;
    }

    // CMD QC: fallback handled by the universal assignment check above.
    if (userRole === 'cmd_qc') {
      console.debug('Project visibility: CMD QC worker (no team tasks)', { projectId: project.id, title: project.title, userRole });
      return false;
    }
    // Epson lead: only see projects whose current stage is Epson Printout
    if (userRole === 'epson_lead') {
      const currentStage = getProjectCurrentStage(project.id) || project.current_stage;
      const visible = currentStage === 'Epson Printout';
      console.debug('Project visibility: Epson lead role (current stage Epson Printout only)', {
        projectId: project.id,
        title: project.title,
        userRole,
        currentStage,
        visible
      });
      return visible;
    }

    // Combined Preflight & Prepress: fallback handled by the universal assignment check above.
    if (userRole === 'preflight_prepress') {
      console.debug('Project visibility: preflight_prepress worker (no team tasks)', { projectId: project.id, title: project.title, userRole });
      return false;
    }

    // Normal prepress workers: fallback handled by the universal assignment check above.
    if (userRole === 'prepress') {
      console.debug('Project visibility: prepress worker (no team tasks)', { projectId: project.id, title: project.title, userRole });
      return false;
    }

    // Prepress TL: team view — show immediately for shared tasks (Die creation,
    // Recreation) so both preflight_lead and prepress_lead see them at the same
    // time. For all other prepress work, require preflight to be done first.
    if (userRole === 'prepress_lead') {
      const sharedPending = projectTasks.some(
        (t) => (t.name === 'Die creation' || t.name === 'Recreation') && !isTaskClosed(t.status)
      );
      if (sharedPending) {
        console.debug('Project visibility: prepress_lead, shared task (Die creation/Recreation) pending', { projectId: project.id, title: project.title, userRole });
        return true;
      }
      if (!areTeamTasksCompleted('preflight')) {
        console.debug('Project visibility: prepress_lead, preflight not completed', { projectId: project.id, title: project.title, userRole });
        return false;
      }
      const prepressTasks = projectTasks.filter((t) => 
        getEffectiveTeam(t) === 'prepress' || 
        t.name === 'Scanning task' || 
        t.name === 'PI preparing' ||
        t.name === 'Recreation' ||
        t.name === 'Mounting or RIP & Send QC'
      );
      const visible = prepressTasks.some((t) => !isTaskClosed(t.status));
      console.debug('Project visibility: prepress_lead role', { projectId: project.id, title: project.title, userRole, visible });
      return visible;
    }

    // Normal QC workers: fallback handled by the universal assignment check above.
    if (userRole === 'qc') {
      console.debug('Project visibility: QC worker (no team tasks)', { projectId: project.id, title: project.title, userRole });
      return false;
    }

    // QC TL: team view — only after preflight + prepress work done.
    if (userRole === 'qc_lead') {
      const preflightDone = areTeamTasksCompleted('preflight');

      const prepressTasks = projectTasks.filter((t) => getEffectiveTeam(t) === 'prepress');
      const allPrepressWorkDone = prepressTasks
        .filter((t) => t.task_type !== 'approval')
        .every((t) => isTaskClosed(t.status));

      if (!preflightDone || !allPrepressWorkDone) {
        console.debug('Project visibility: qc_lead, earlier stages not completed', {
          projectId: project.id,
          title: project.title,
          userRole,
          preflightDone,
          allPrepressWorkDone
        });
        return false;
      }

      const visible = hasPendingTeamTasks('qc');
      console.debug('Project visibility: qc_lead role', { projectId: project.id, title: project.title, userRole, visible });
      return visible;
    }

    // Normal production workers: fallback handled by the universal assignment check above.
    if (userRole === 'production') {
      console.debug('Project visibility: production worker (no team tasks)', { projectId: project.id, title: project.title, userRole });
      return false;
    }

    // Production TL: team view with location matching; no upstream stage gate.
    if (userRole === 'production_lead') {
      const productionTasks = projectTasks.filter(
        (t) => getEffectiveTeam(t) === 'production' || t.name === 'Plate and Despatch' || t.step_order === 8
      );
      if (productionTasks.length === 0) {
        console.debug('Project visibility: production_lead, no production tasks in project', { projectId: project.id, title: project.title, userRole });
        return false;
      }

      const hasPendingProduction = productionTasks.some((t) => !isTaskClosed(t.status));
      if (!hasPendingProduction) {
        console.debug('Project visibility: production_lead, no pending production tasks', { projectId: project.id, title: project.title, userRole });
        return false;
      }

      const userId = user.id;
      if (!userId || !Array.isArray(teams) || teams.length === 0) {
        console.debug('Project visibility: production_lead without team/location data, allowing by pending production only', {
          projectId: project.id,
          title: project.title,
          userRole
        });
        return true;
      }

      const userTeams = teams.filter((team) => {
        if (team.type !== 'production') return false;
        const members = Array.isArray(team.members) ? team.members : [];
        const leadIds = Array.isArray(team.lead_ids) ? team.lead_ids : [];
        const legacyLeadId = team.lead_id;
        return (
          members.includes(userId) ||
          leadIds.includes(userId) ||
          legacyLeadId === userId
        );
      });

      const locationIdSet = new Set();
      userTeams.forEach((team) => {
        (Array.isArray(team.location_ids) ? team.location_ids : []).forEach((locId) => {
          if (locId) locationIdSet.add(String(locId));
        });
      });

      // Map location IDs to names from productionLocations
      const idToName = new Map();
      (productionLocations || []).forEach((loc) => {
        const id = loc.id || loc._id;
        if (id && loc.name) {
          idToName.set(String(id), String(loc.name));
        }
      });

      const allowedLocationNames = Array.from(locationIdSet)
        .map((id) => idToName.get(id))
        .filter((name) => typeof name === 'string' && name.trim().length > 0)
        .map((name) => name.trim().toLowerCase());

      const projectLocation = (project.job_location || '').trim().toLowerCase();

      // If no locations configured yet, allow as long as production has pending work
      if (allowedLocationNames.length === 0 || !projectLocation) {
        console.debug('Project visibility: production_lead without configured locations, allowing by pending production only', {
          projectId: project.id,
          title: project.title,
          userRole,
          projectLocation
        });
        return true;
      }

      const matchesLocation = allowedLocationNames.includes(projectLocation);
      console.debug('Project visibility: production_lead with location match', {
        projectId: project.id,
        title: project.title,
        userRole,
        projectLocation,
        allowedLocationNames,
        matchesLocation
      });
      return matchesLocation;
    }

    // Epson workers: fallback handled by the universal assignment check above.
    if (userRole === 'epson') {
      console.debug('Project visibility: epson worker (no team tasks)', { projectId: project.id, title: project.title, userRole });
      return false;
    }

    // Fallback: hide for unknown roles
    console.debug('Project visibility: unknown role, hiding project', { projectId: project.id, title: project.title, userRole });
    return false;
  };

  const isTaskVisibleToUser = (task) => {
    // Admin and PM can see all tasks
    if (user?.role === 'admin' || user?.role === 'pm') {
      return true;
    }
    
    const taskTeam = task.team;
    const userRole = user?.role;

    // Universal rule: if this task is assigned to the user, always show it —
    // regardless of team or role.
    if (String(task.assigned_to) === String(user?.id)) {
      return true;
    }

    if (!taskTeam) return true; // If no team assigned, show to everyone

    // Normal (non-lead) workers: only show tasks assigned directly to them.
    const leadRoles = [
      'preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead',
      'production_lead', 'epson_lead'
    ];
    if (!leadRoles.includes(userRole)) {
      return task.assigned_to === user?.id;
    }

    // Special-case: Epson Printout should be handled by the Epson lead role,
    // not by the generic 'cmd' team/lead mapping.
    const taskName = (task.name || '').toLowerCase();
    if (taskName.includes('epson printout')) {
      if (userRole === 'epson_lead') return true;
      return false;
    }

    // Die creation / Recreation are shared between Preflight and Prepress.
    // Both teams (and the combined preflight_prepress role) can view these
    // tasks so TLs can decide who will actually do the work.
    if (taskName === 'die creation' || taskName === 'recreation') {
      if (
        userRole === 'preflight' ||
        userRole === 'preflight_lead' ||
        userRole === 'prepress' ||
        userRole === 'prepress_lead' ||
        userRole === 'preflight_prepress'
      ) {
        return true;
      }
    }

    // Mounting or RIP & Send QC has team='production' but is assignable to
    // prepress / qc team members. All prepress and qc leads (and preflight_prepress)
    // should be able to see it so they can manage it.
    if (task.name === 'Mounting or RIP & Send QC') {
      if (
        ['prepress', 'prepress_lead', 'preflight_prepress', 'qc', 'qc_lead'].includes(userRole)
      ) {
        return true;
      }
    }

    // CMD QC workers can see CMD and CMD QC tasks (both are on cmd team).
    // cmd_lead already passes via the generic `userRole === taskTeam + '_lead'` check.
    if (userRole === 'cmd_qc' && taskTeam === 'cmd') {
      return true;
    }

    // Check if user's role matches the task's team or is a lead for that team
    if (userRole === taskTeam || userRole === `${taskTeam}_lead`) {
      return true;
    }

    // Combined Preflight & Prepress role can see both preflight and prepress tasks
    if (userRole === 'preflight_prepress' && (taskTeam === 'preflight' || taskTeam === 'prepress')) {
      return true;
    }
    
    // Special case for prepress team - only show to assigned team members based on client
    if (taskTeam === 'prepress' && task.name === 'Prepress , QC (self QC)') {
      // This check will be handled by backend filtering, so we show it to prepress/prepress_lead
      if (userRole === 'prepress' || userRole === 'prepress_lead') {
        return true;
      }
    }
    
    return false;
  };

  // Effective team helper (used across multiple visibility checks)
  const getEffectiveTeam = (t) => {
    const name = (t && t.name) ? String(t.name).toLowerCase() : '';
    if (name.includes('epson printout')) return 'epson';
    return (t && t.team) ? t.team : '';
  };

  const truncateText = (text, maxLength = 15) => {
    if (!text) return '';
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + '...';
    }
    return text;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-200 text-green-900';
      case 'cancelled': return 'bg-stone-200 text-stone-800';
      case 'in_progress': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      // Two shades of red: paused is the stronger one, on hold the softer.
      case 'paused': return 'bg-red-200 text-red-900';
      case 'on_hold': return 'bg-red-100 text-red-800';
      case 'query': return 'bg-purple-100 text-purple-800';
      case 'rejected': return 'bg-red-300 text-red-900';
      case 'not_started': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Whole-row tint driven by the current task status, so a row's state is
  // readable straight across the table. Kept deliberately pale — every column's
  // text sits on top of it. `hover` is a slightly deeper step of the same hue so
  // hovering never flips a row to a different colour.
  const getRowStatusColor = (status) => {
    switch (status) {
      case 'in_progress': return 'bg-green-50 hover:bg-green-100';
      case 'pending':     return 'bg-yellow-50 hover:bg-yellow-100';
      case 'paused':      return 'bg-red-100 hover:bg-red-200';
      case 'on_hold':     return 'bg-red-50 hover:bg-red-100';
      case 'query':       return 'bg-purple-50 hover:bg-purple-100';
      case 'rejected':    return 'bg-red-200 hover:bg-red-300';
      case 'completed':   return 'bg-green-100 hover:bg-green-200';
      case 'cancelled':   return 'bg-stone-100 hover:bg-stone-200';
      case 'not_started': return 'bg-white hover:bg-gray-50';
      default:            return 'bg-white hover:bg-gray-50';
    }
  };

  // ── Stage colours ───────────────────────────────────────────────────
  // Every workflow stage gets its own colour so the Current Stage column can be
  // read at a glance. Hues are grouped by team — Preflight blues, CMD teals,
  // Prepress violets, QC indigo, Epson rose, Production green, flexible extras
  // warm — so neighbouring stages of the same team look related without being
  // confusable. The status badge keeps its own colour on top.
  // `badge` is a solid pill that has to stay legible on top of the row tint, so
  // these use the 100/200 shades rather than 50.
  const STAGE_COLORS = {
    // Preflight team
    'Preflight':                  { badge: 'bg-blue-200 text-blue-900 border-blue-400' },
    'Single-up approval':         { badge: 'bg-sky-200 text-sky-900 border-sky-400' },
    // CMD team
    'CMD':                        { badge: 'bg-cyan-200 text-cyan-900 border-cyan-400' },
    'CMD QC':                     { badge: 'bg-teal-200 text-teal-900 border-teal-400' },
    'CMD Path':                   { badge: 'bg-emerald-200 text-emerald-900 border-emerald-400' },
    // Prepress team
    'Prepress , QC (self QC)':    { badge: 'bg-violet-200 text-violet-900 border-violet-400' },
    'Image Export':               { badge: 'bg-fuchsia-200 text-fuchsia-900 border-fuchsia-400' },
    'Image Duplication':          { badge: 'bg-violet-200 text-violet-900 border-violet-400' },
    'Mounting or RIP & Send QC':  { badge: 'bg-pink-200 text-pink-900 border-pink-400' },
    'Layout approval':            { badge: 'bg-purple-200 text-purple-900 border-purple-400' },
    // QC team
    'RIP QC':                     { badge: 'bg-indigo-200 text-indigo-900 border-indigo-400' },
    // Epson / Production
    'Epson Printout':             { badge: 'bg-rose-200 text-rose-900 border-rose-400' },
    'Plate and Despatch':         { badge: 'bg-green-200 text-green-900 border-green-400' },
    // Flexible / inserted steps
    'Die creation':               { badge: 'bg-amber-200 text-amber-900 border-amber-400' },
    'Recreation':                 { badge: 'bg-orange-200 text-orange-900 border-orange-400' },
    'Scanning':                   { badge: 'bg-lime-200 text-lime-900 border-lime-500' },
    'PI preparing':               { badge: 'bg-stone-200 text-stone-900 border-stone-400' },
  };

  const STAGE_COLOR_FALLBACK = { badge: 'bg-slate-200 text-slate-900 border-slate-400' };

  // Stage labels can carry a round suffix ("Preflight (R2)") or a naming variant
  // ("Scanning task", "Mounting RIP QC"), so normalise before looking up.
  const normalizeStageName = (name) => {
    let s = String(name || '').replace(/\s*\(.*?\)\s*$/, '').trim();
    if (s === 'Scanning task') s = 'Scanning';
    if (s === 'Mounting RIP QC') s = 'Mounting or RIP & Send QC';
    return s;
  };

  const getStageColors = (stageName) => {
    const key = normalizeStageName(stageName);
    if (STAGE_COLORS[key]) return STAGE_COLORS[key];
    // Prefix match catches decorated labels the normaliser doesn't know about.
    const hit = Object.keys(STAGE_COLORS).find((k) => key.startsWith(k));
    return hit ? STAGE_COLORS[hit] : STAGE_COLOR_FALLBACK;
  };

  const getTaskTypeColor = (taskType) => {
    switch (taskType) {
      case 'approval': return 'bg-purple-100 text-purple-800';
      case 'standard': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDisplayTaskName = (task) => {
    if (!task) return '';
    const baseName = task.name || '';

    // Helper: compute the sequential round number for a given
    // Preflight task based on its position among all Preflight
    // tasks for the current project.
    const getPreflightRound = (preflightTask) => {
      const allPreflights = (projectTasks || [])
        .filter(t => (t.name || '').toLowerCase() === 'preflight')
        .sort((a, b) => (a.step_order || 0) - (b.step_order || 0));

      const index = allPreflights.findIndex(t => t.id === preflightTask.id);
      if (index >= 0) {
        return index + 1; // First Preflight = round 1, next = round 2, etc.
      }
      return preflightTask.round || 1;
    };

    let round = task.round || 1;
    const lowerName = baseName.toLowerCase();

    // For Preflight itself, derive the visual round based on its position
    // among all Preflight tasks.
    if (lowerName === 'preflight') {
      round = getPreflightRound(task);
    }

    // For Single-up approval and Prepress QC, trust the backend-provided
    // `round` field so that each rejection-created round (R2, R3, ...) is
    // displayed accurately.

    return round > 1 ? `${baseName} (R${round})` : baseName;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'fast_track': return 'bg-red-100 text-red-800';
      case 'normal': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getComplexityColor = (complexity) => {
    switch (complexity) {
      case 'simple_simple': return 'bg-green-100 text-green-800';
      case 'simple': return 'bg-green-100 text-green-800';
      case 'medium_simple': return 'bg-yellow-100 text-yellow-800';
      case 'medium': return 'bg-orange-100 text-orange-800';
      case 'complex': return 'bg-red-100 text-red-800';
      case 'super_complex': return 'bg-purple-100 text-purple-800';
      case 'combined_job': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatComplexityTime = (minutes) => {
    if (!minutes && minutes !== 0) return '';
    if (typeof minutes === 'object') return ''; // Skip if object instead of number
    if (minutes < 60) {
      return `${minutes}min`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMins = minutes % 60;
      return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours}h`;
    }
  };

  const getTaskComplexityTiming = (taskName, complexity) => {
    const complexityData = complexityConfig[complexity];
    if (!complexityData || typeof complexityData !== 'object') {
      return null;
    }
    return complexityData[taskName];
  };

  const getComplexityDisplayName = (complexity) => {
    if (!complexity) return '';

    const predefined = {
      'simple_simple': 'Simple Simple',
      'simple': 'Simple',
      'medium_simple': 'Medium Simple',
      'medium': 'Medium',
      'complex': 'Complex',
      'super_complex': 'Super Complex',
      'combined_job': 'Combined Job'
    };

    if (predefined[complexity]) {
      return predefined[complexity];
    }

    // For any new/custom complexity keys, generate a human-readable label:
    // - replace underscores with spaces
    // - title-case the words
    const label = String(complexity)
      .replace(/_/g, ' ')
      .trim()
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    return label || complexity;
  };

  // Calculate automatic delivery date based on creation time
  const calculateDeliveryDate = (creationDate) => {
    const created = new Date(creationDate);
    const deliveryDate = new Date(created);
    
    // If created after 4:30 PM (16:30), deliver next day
    if (created.getHours() > 16 || (created.getHours() === 16 && created.getMinutes() >= 30)) {
      deliveryDate.setDate(deliveryDate.getDate() + 1);
    } else {
      // If created before 4:30 PM, deliver same day
      deliveryDate.setDate(deliveryDate.getDate());
    }
    
    // Set delivery time to end of day (6:00 PM)
    deliveryDate.setHours(18, 0, 0, 0);
    
    return deliveryDate;
  };

  // Derive the next version label for titles/job codes (avoids doubling "-V2")
  const getNextVersionLabel = (label, isJobCode = false) => {
    if (!label) {
      return { base: '', nextVersion: 2, nextLabel: '' };
    }

    const regex = isJobCode ? /(.*?)(-V)(\d+)$/i : /(.*?)(?: - V)(\d+)$/i;
    const match = label.match(regex);
    const base = match ? match[1].trim() : label.trim();
    const parsedVersion = match ? parseInt(match[3], 10) : NaN;
    const currentVersion = Number.isFinite(parsedVersion) ? parsedVersion : 1;
    const nextVersion = currentVersion + 1;
    const nextLabel = isJobCode
      ? (base ? `${base}-V${nextVersion}` : '')
      : (base ? `${base} - V${nextVersion}` : '');

    return { base, nextVersion, nextLabel };
  };

  // Calculate hours elapsed since project creation
  const getHoursSinceCreation = (createdAt) => {
    if (!createdAt) return null;
    
    const now = new Date();
    const createdDate = new Date(createdAt);
    const diffMs = now - createdDate;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return { hours: diffHours, minutes: diffMinutes };
  };

  // Format hours since creation display
  const formatHoursSinceCreation = (timeElapsed) => {
    if (!timeElapsed) return null;
    
    if (timeElapsed.hours > 0) {
      return `${timeElapsed.hours}h ${timeElapsed.minutes}m`;
    } else {
      return `${timeElapsed.minutes}m`;
    }
  };

  // ── Active filters ──────────────────────────────────────────────────
  // Client filter, quick-filter tabs and the My/Team scope are all applied to
  // the project list but used to survive the "Clear" button, so a search could
  // return nothing with no visible reason. They are included here so that Clear
  // really clears, and so the empty state can say what is hiding the results.

  const activeFilterList = () => {
    const out = [];
    if (projectSearch) out.push({ key: 'search', label: `Search: "${projectSearch}"` });
    if (priorityFilter) out.push({ key: 'priority', label: `Priority: ${priorityFilter}` });
    if (statusFilter) out.push({ key: 'status', label: `Status: ${statusFilter}` });
    if (currentStageFilter) out.push({ key: 'stage', label: `Stage: ${currentStageFilter}` });
    if (jobLocationFilter) out.push({ key: 'location', label: `Location: ${jobLocationFilter}` });
    if ((clientFilter || []).length > 0) {
      out.push({ key: 'client', label: `Client: ${clientFilter.length === 1 ? clientFilter[0] : `${clientFilter.length} selected`}` });
    }
    if (dateFrom || dateTo) out.push({ key: 'date', label: `Booked ${dateFrom || '…'} → ${dateTo || '…'}` });
    if (assignedUserFilter) {
      const u = (users || []).find((x) => String(x.id || x._id) === String(assignedUserFilter));
      out.push({ key: 'assigned', label: `Assigned to: ${u?.name || 'selected user'}` });
    }
    if (quickFilter) out.push({ key: 'tab', label: `Tab: ${quickFilter.replace(/_/g, ' ')}` });
    if (myProjectsScope === 'my') out.push({ key: 'scope', label: 'Scope: My Projects only' });
    return out;
  };

  const hasActiveFilters = () => activeFilterList().length > 0;

  const clearAllFilters = () => {
    setProjectSearch('');
    setPriorityFilter('');
    setStatusFilter('');
    setCurrentStageFilter('');
    setJobLocationFilter('');
    setAssignedUserFilter('');
    setDateFrom('');
    setDateTo('');
    setClientFilter([]);        // was not cleared before
    setQuickFilter('');         // was not cleared before
    setMyProjectsScope('team'); // was not cleared before
    setCurrentPage(1);
  };

  // ── Backlog ─────────────────────────────────────────────────────────
  // Backlog = whole hours a project is past its delivery date. Completed work
  // never counts as backlog, however late it was.

  // End of the working day used as the deadline when only a date is stored.
  // Must match calculateDeliveryDate, which sets 18:00 local.
  const DELIVERY_END_HOUR = 18;

  /**
   * Resolve a project's delivery deadline to a real point in time.
   *
   * The deadline is chosen with a date picker, so it carries no time of day.
   * The backend stores it and serialises it as "2026-08-24T00:00:00Z" —
   * midnight UTC, which is 05:30 IST. Taken literally, a job due *today* looked
   * overdue from half past five in the morning, which is what produced the
   * spurious "9h" backlog on jobs booked minutes earlier.
   *
   * A date-only deadline means "by the end of that day", so both the bare date
   * and the midnight-UTC form are resolved to 18:00 local — matching
   * calculateDeliveryDate. A deadline carrying a real time is used as given.
   */
  const getDeliveryDeadline = (project) => {
    const raw = project?.deadline;
    if (raw) {
      const s = String(raw).trim();

      // "2026-08-24"
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        const [y, m, d] = s.split('-').map(Number);
        // Built from parts, so this is local time rather than UTC.
        return new Date(y, m - 1, d, DELIVERY_END_HOUR, 0, 0, 0);
      }

      const parsed = new Date(s);
      if (Number.isNaN(parsed.getTime())) return null;

      // "2026-08-24T00:00:00Z" — a date-only deadline after serialisation.
      // Exact midnight UTC means no time was ever chosen, so treat the UTC
      // calendar date as the intended day and use the end of it.
      const isMidnightUtc =
        parsed.getUTCHours() === 0 && parsed.getUTCMinutes() === 0 &&
        parsed.getUTCSeconds() === 0 && parsed.getUTCMilliseconds() === 0;
      if (isMidnightUtc) {
        return new Date(
          parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate(),
          DELIVERY_END_HOUR, 0, 0, 0,
        );
      }
      return parsed;
    }
    if (!project?.created_at) return null;
    return calculateDeliveryDate(new Date(project.created_at));
  };

  const getBacklogHours = (project) => {
    if (!project || project.current_status === 'completed') return 0;
    if (!project.created_at) return null;   // still loading
    const deliveryDate = getDeliveryDeadline(project);
    if (!deliveryDate || Number.isNaN(deliveryDate.getTime())) return 0;
    const diffH = Math.floor((Date.now() - deliveryDate.getTime()) / (1000 * 60 * 60));
    return diffH > 0 ? diffH : 0;
  };

  // Escalating red bands. The thresholds are the lower bound of each band, so
  // "8" means 8h up to but not including 12h.
  const BACKLOG_BANDS = [
    { min: 48, label: '48h+', cls: 'bg-red-900 text-white border-red-900' },
    { min: 24, label: '24h+', cls: 'bg-red-600 text-white border-red-600' },
    { min: 12, label: '12h+', cls: 'bg-red-400 text-white border-red-400' },
    { min: 8,  label: '8h+',  cls: 'bg-red-200 text-red-900 border-red-300' },
    { min: 1,  label: '<8h',  cls: 'bg-orange-100 text-orange-800 border-orange-200' },
  ];

  const getBacklogBand = (hours) => BACKLOG_BANDS.find((b) => hours >= b.min) || null;

  const formatBacklog = (hours) => {
    if (hours < 24) return `${hours}h`;
    const d = Math.floor(hours / 24);
    const h = hours % 24;
    return h ? `${d}d ${h}h` : `${d}d`;
  };

  // Calculate current stage from project tasks (more reliable than backend field)
  const getProjectCurrentStage = (projectId) => {
    const projectTasks = allProjectTasks[projectId];
    if (!projectTasks || projectTasks.length === 0) return null;
    
    // Find the first task that is NOT completed and NOT rejected
    // Sort by step_order, but keep Die creation/Recreation/Scanning/PI preparing sticky to their preflight anchor
    const currentTask = projectTasks
      .sort((a, b) => {
        const aOrder = a.step_order || 0;
        const bOrder = b.step_order || 0;
        if (aOrder !== bOrder) return aOrder - bOrder;
        // Secondary sort: flexible tasks after standard tasks at same level
        const aIsFlexible = ['Die creation', 'Recreation', 'Scanning task', 'PI preparing'].includes(a.name);
        const bIsFlexible = ['Die creation', 'Recreation', 'Scanning task', 'PI preparing'].includes(b.name);
        return aIsFlexible - bIsFlexible;
      })
      .find(t => t.status && !['completed', 'rejected'].includes(t.status));
    
    return currentTask ? currentTask.name : null;
  };

  // Get current task with assigned user info
  const getProjectCurrentTask = (projectId) => {
    const projectTasks = allProjectTasks[projectId];
    if (!projectTasks || projectTasks.length === 0) return null;
    
    // Find the first task that is NOT completed and NOT rejected
    const currentTask = projectTasks
      .sort((a, b) => (a.step_order || 0) - (b.step_order || 0))
      .find(t => t.status && !['completed', 'rejected'].includes(t.status));
    
    return currentTask || null;
  };

  const availableClients = Array.from(
    new Set(
      projects
        .map((p) => (p.client || '').trim())
        .filter((name) => name.length > 0)
    )
  ).sort();

  const handleClientFilterChange = async (newFilter) => {
    setClientFilter(newFilter);
    setCurrentPage(1);

    if (!token || !user) return;
    if (user.role !== 'pm' && user.role !== 'admin') return;

    try {
      await api.put(
        '/user/preferences',
        { client_filter: newFilter },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.error('[Jobs] Failed to save client filter preferences', err);
    }
  };

  const getPageTitle = () => {
    switch (variant) {
      case 'my-projects':
        return 'My Projects';
      case 'hold-projects':
        return 'Hold Projects';
      case 'on-approval-projects':
        return 'On Approval Projects';
      case 'completed-projects':
        return 'Completed Projects';
      default:
        return 'Active Projects';
    }
  };

  const normalizeBreakType = (value) => String(value || '').trim().toLowerCase();

  const getUsedBreakTypes = () => {
    const explicitList = shiftSummary?.breaks_taken || shiftSummary?.break_types || shiftSummary?.breaks_used;
    if (Array.isArray(explicitList) && explicitList.length > 0) {
      return explicitList;
    }

    const explicitMap = shiftSummary?.breaks;
    if (explicitMap && typeof explicitMap === 'object') {
      return Object.keys(explicitMap).filter((key) => explicitMap[key]);
    }

    const events = shiftSummary?.events || shiftSummary?.time_events || shiftSummary?.activities || [];
    if (Array.isArray(events) && events.length > 0) {
      return events
        .filter((evt) => evt && (evt.event === 'break_start' || evt.event === 'break_end'))
        .map((evt) => evt.break_type)
        .filter(Boolean);
    }

    return usedBreakTypes;
  };

  const isBreakWindowOpen = (type, now = new Date()) => {
    const minutes = now.getHours() * 60 + now.getMinutes();

    switch (type) {
      case 'lunch':
        return minutes >= 13 * 60 && minutes <= 15 * 60 + 30;
      case 'snacks':
        return minutes >= 18 * 60 && minutes <= 18 * 60 + 30;
      case 'dinner':
        return minutes >= 20 * 60 + 30 && minutes <= 22 * 60 + 30;
      default:
        return false;
    }
  };

  const privilegedRunningTaskBypassRoles = [
    'admin',
    'pm',
    'preflight_lead',
    'prepress_lead',
    'qc_lead',
    'production_lead',
    'cmd_lead',
    'epson_lead',
  ];

  // Approval tasks (Single-up approval, Layout approval) are passive client-wait steps
  // and do not count as "running" for the purpose of blocking new task starts.
  //
  // Primary signal: shiftSummary.active_task_id — set by the backend whenever
  // a non-approval task is started; always accurate regardless of which page of
  // projects is currently loaded. Approval tasks do NOT set active_task_id because
  // they skip the time-event call, so this naturally excludes them.
  //
  // Fallback: scan allProjectTasks (covers tasks loaded on the current page,
  // in case the shift summary hasn't refreshed yet).
  const hasRunningTaskForUser =
    !privilegedRunningTaskBypassRoles.includes(user?.role) &&
    (
      (shiftSummary.current_status === 'working' && !!shiftSummary.active_task_id) ||
      Object.values(allProjectTasks).some((tasks) =>
        tasks.some(
          (t) =>
            String(t.assigned_to) === String(user?.id) &&
            t.status === 'in_progress' &&
            t.task_type !== 'approval' &&
            t.approval_type !== 'Single-up approval' &&
            t.approval_type !== 'Layout approval'
        )
      )
    );

  return (
    <div className="p-6 relative">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center z-40 pointer-events-none">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{getPageTitle()}</h1>
        {(user?.role === 'admin' || user?.role === 'pm') && (
          <button
            onClick={() => handleOpenDialog()}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Project
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 border-l-4 border-red-500 text-red-700 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {/* Shift & Status Control Panel */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-start mb-2">
          {/* Shift status and start/end controls */}
          <div className="md:col-span-1">
            <h2 className="text-lg font-semibold text-gray-800">
              Shift Status: <span className="capitalize text-blue-600">{shiftSummary.current_status}</span>
            </h2>
            {shiftSummary.current_status !== 'offline' && (
              <div className="flex flex-wrap gap-4 text-sm text-gray-500 mt-1">
                <span>{shiftSummary.shift_start ? new Date(shiftSummary.shift_start).toLocaleString() : '-'}</span>
              </div>
            )}
            
          </div>

          {/* Activities — hidden while a task is actively running to prevent accidental idle/no-jobs starts */}
          {shiftSummary.current_status !== 'offline' && (shiftSummary.current_status !== 'working' || ['pm', 'admin', 'preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead', 'production_lead', 'epson_lead'].includes(user?.role)) && (
            <div className="md:col-span-2">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Activities</h3>
              <div className="flex flex-wrap gap-2">
                {[
                    { id: 'meeting', label: 'Meeting', icon: Users },
                    { id: 'call', label: 'Call', icon: Phone },
                    { id: 'training', label: 'Training', icon: Monitor },
                    { id: 'no_jobs', label: 'No Jobs', icon: Briefcase }
                  ].map(item => {
                    const isCurrentActivity = shiftSummary.current_status === 'idle' && shiftSummary.current_activity === item.id;
                    const isAnotherActivityRunning = shiftSummary.current_status === 'idle' && shiftSummary.current_activity && shiftSummary.current_activity !== item.id;
                    
                    return (
                      <button
                        key={item.id}
                        onClick={() => {
                          if (isCurrentActivity) {
                            handleGlobalEvent('idle_end', item.id);
                          } else {
                            handleGlobalEvent('idle_start', item.id);
                          }
                        }}
                        disabled={isAnotherActivityRunning}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center ${
                          isCurrentActivity
                            ? 'bg-blue-100 text-blue-700 border border-blue-200'
                            : isAnotherActivityRunning
                            ? 'bg-gray-50 text-gray-400 border border-gray-200 cursor-not-allowed'
                            : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                        }`}
                        title={isAnotherActivityRunning ? 'Another activity is in progress' : `${item.label}`}
                      >
                        <item.icon className="w-3 h-3 mr-1.5" />
                        {item.label}
                      </button>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Active Task Info — shown instead of Activities/Breaks while a task is running */}
          {shiftSummary.current_status === 'working' && shiftSummary.active_task_id && !['pm', 'admin', 'preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead', 'production_lead', 'epson_lead'].includes(user?.role) && (() => {
            const activeInfo = findTaskAndProjectById(shiftSummary.active_task_id);
            const activeTask = activeInfo?.task;
            const activeProject = activeInfo?.project;
            // Fall back to shift-summary enrichment when the task's project is not
            // currently loaded (filtered out, different page, etc.)
            const displayCode = activeProject?.job_code || activeProject?.title
              || shiftSummary.active_project_job_code || shiftSummary.active_project_title
              || 'Unknown Project';
            const displayName = activeTask?.name || shiftSummary.active_task_name || 'Unknown Task';
            return (
              <div className="md:col-span-2 bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="text-sm text-green-900 font-medium">
                  <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse mr-3"></span>
                  {displayCode} - {displayName} - Task In Progress
                </div>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-green-600 italic">Pause or complete the task before starting an activity or break.</p>
                  <button
                    onClick={() => handleOpenHoldPause(shiftSummary.active_task_id, 'pause')}
                    className="ml-3 px-2 py-1 text-xs font-medium text-orange-700 bg-orange-100 border border-orange-200 rounded hover:bg-orange-200 flex-shrink-0"
                  >
                    Pause Task
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Breaks — hidden while a task is actively running */}
          {shiftSummary.current_status !== 'offline' && (shiftSummary.current_status !== 'working' || ['pm', 'admin', 'preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead', 'production_lead', 'epson_lead'].includes(user?.role)) && (
            <div>
              {(() => {
                  const breakTypes = ['lunch', 'snacks', 'dinner'];
                  // Normalise so case/whitespace differences never hide the Stop button
                  const rawActiveType = shiftSummary.current_status === 'break'
                    ? normalizeBreakType(shiftSummary.current_activity)
                    : null;
                  const activeType = rawActiveType || null;
                  const activeTypeKnown = activeType && breakTypes.includes(activeType);

                  const usedBreaks = new Set(
                    getUsedBreakTypes().map((type) => normalizeBreakType(type)).filter(Boolean)
                  );
                  const isDinnerBlocked = usedBreaks.has('lunch');

                  // Always include the active break so the Stop button shows even
                  // after the time window has closed.
                  const visibleBreaks = [
                    ...(activeTypeKnown ? [activeType] : []),
                    ...breakTypes.filter((type) =>
                      type !== activeType &&
                      isBreakWindowOpen(type) &&
                      !(type === 'dinner' && isDinnerBlocked)
                    ),
                  ];

                  const showDinnerBlockedMessage =
                    isDinnerBlocked && isBreakWindowOpen('dinner') && activeType !== 'dinner';

                  // Fallback: user is on an unrecognised break type (e.g. restroom)
                  const showUnknownBreakStop = shiftSummary.current_status === 'break' && !activeTypeKnown;

                  if (visibleBreaks.length === 0 && !showDinnerBlockedMessage && !showUnknownBreakStop) return null;

                  return (
                    <>
                      <h3 className="text-sm font-medium text-gray-700 mb-2">Breaks</h3>
                      <div className="flex flex-wrap gap-2">
                        {visibleBreaks.map((type) => {
                          const isCurrentBreak = type === activeType;
                          const label = isCurrentBreak
                            ? `Stop ${type}`
                            : type;

                          return (
                            <button
                              key={type}
                              onClick={() => isCurrentBreak
                                ? handleGlobalEvent('break_end', type)
                                : handleGlobalEvent('break_start', type)}
                              disabled={shiftSummary.current_status === 'break' && activeType !== type}
                              className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize flex items-center ${
                                isCurrentBreak
                                  ? 'bg-orange-100 text-orange-700 border border-orange-200'
                                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                              }`}
                            >
                              <Coffee className="w-3 h-3 mr-1.5" />
                              {label}
                            </button>
                          );
                        })}
                        {showUnknownBreakStop && (
                          <button
                            key="unknown-break-end"
                            onClick={() => handleGlobalEvent('break_end', rawActiveType || '')}
                            className="px-3 py-1.5 rounded-md text-sm font-medium capitalize flex items-center bg-orange-100 text-orange-700 border border-orange-200"
                          >
                            <Coffee className="w-3 h-3 mr-1.5" />
                            {rawActiveType ? `Stop ${rawActiveType}` : 'Stop Break'}
                          </button>
                        )}
                      </div>
                      {showDinnerBlockedMessage && (
                        <div className="text-xs text-gray-500 mt-2">
                          You already used the lunch break.
                        </div>
                      )}
                    </>
                  );
                  })()}
              </div>
            )}

          <div className="text-sm text-red-600 font-medium">
              {shiftSummary.current_status === 'offline' ? (
                <button
                  onClick={() => handleGlobalEvent('shift_start')}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Start Shift
                </button>
              ) : (
                shiftSummary.total_office_hours < 15 && (
                  <button
                    onClick={() => handleGlobalEvent('shift_end')}
                    disabled={shiftSummary.current_status === 'working'}
                    title={shiftSummary.current_status === 'working' ? 'Pause or complete your active task before ending shift' : ''}
                    className={`mt-3 pt-3 px-4 py-2 rounded-lg text-white font-medium ${shiftSummary.current_status === 'working' ? 'bg-red-300 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'}`}
                  >
                    End Shift
                  </button>
                )
              )}
          </div>

        </div>

          {/* Shift exceeded message only at the bottom */}
          {shiftSummary.current_status !== 'offline' && shiftSummary.total_office_hours >= 15 && (
            <div className="mt-3 pt-3 border-t border-gray-100 text-sm text-red-600 font-medium">
              Shift exceeded 15 hours. Please ask admin to end the last started shift.
            </div>
          )}

            
      </div>
      
      {/* Quick filter tab bar */}
      {shiftSummary.current_status !== 'offline' && (() => {
        const isTL = user && [
          'preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead', 'production_lead', 'epson_lead'
        ].includes(user.role);
        const isPM = user && (user.role === 'pm' || user.role === 'admin');
        const isNormalUser = user && !['pm', 'admin', 'preflight_lead', 'cmd_lead', 'prepress_lead',
          'qc_lead', 'production_lead', 'epson_lead'].includes(user.role);

        const tabBtn = (label, value) => (
          <button
            key={value}
            onClick={() => { setQuickFilter(prev => prev === value ? '' : value); setCurrentPage(1); }}
            className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
              quickFilter === value
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {label}
          </button>
        );

        if (isNormalUser || isTL || isPM) {
          return (
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {/* TL/PM/Admin: My Projects / Team Projects toggle */}
              {(isTL || isPM) && (
                <div className="flex rounded-md border border-gray-300 overflow-hidden">
                  <button
                    onClick={() => setMyProjectsScope('my')}
                    className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                      myProjectsScope === 'my'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    My Projects
                  </button>
                  <button
                    onClick={() => setMyProjectsScope('team')}
                    className={`px-3 py-1.5 text-sm font-medium border-l border-gray-300 transition-colors ${
                      myProjectsScope === 'team'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Team Projects
                  </button>
                </div>
              )}

              {tabBtn('In Progress', 'in_progress')}
              {tabBtn('On Approval', 'on_approval')}
              {tabBtn('On Hold', 'on_hold')}
              {tabBtn('Paused', 'paused')}
              {tabBtn('On Query', 'on_query')}
              {tabBtn('Not Started', 'not_started')}
            </div>
          );
        }
        return null;
      })()}

      {(() => {
        const hasBlockingActivity =
          (shiftSummary.current_status === 'idle' && !!shiftSummary.current_activity) ||
          shiftSummary.current_status === 'break';

        if (shiftSummary.current_status === 'offline' && user?.role !== 'admin') {
          return (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <Briefcase className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Shift Not Started</h3>
              <p className="text-gray-500 mb-6">Please start your shift to view and manage your assigned projects and tasks.</p>
              <button
                onClick={() => handleGlobalEvent('shift_start')}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                Start Shift Now
              </button>
            </div>
          );
        }

        if (hasBlockingActivity) {
          return (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <Briefcase className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">On Activity / Break</h3>
              <p className="text-gray-500 mb-6">
                An activity or break is currently running. Please end it to view projects and to start or resume tasks.
              </p>
            </div>
          );
        }

        return (
        <div className="space-y-6">
          {/* Projects List */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className={`p-4 border-b border-gray-200 flex items-center justify-between ${(projectSearch || priorityFilter || statusFilter || currentStageFilter || jobLocationFilter || assignedUserFilter || dateFrom || dateTo) ? 'bg-sky-200' : ''}`}>
              <h2 className="text-lg font-semibold ">Projects</h2>
              <div className="flex items-center space-x-3">

                {/* Clear Filters Button */}
                {hasActiveFilters() && (
                  <button
                    onClick={clearAllFilters}
                    className="px-3 py-2 text-sm text-white bg-red-500 hover:bg-red-700 hover:text-white rounded-md border border-gray-300 transition-colors"
                    title="Clear all filters"
                  >
                    Clear
                  </button>
                )}

                {/* Search Input */}
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={projectSearch}
                  onChange={(e) => { setProjectSearch(e.target.value); setCurrentPage(1); }}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {/* Priority Filter */}
                <select
                  value={priorityFilter}
                  onChange={(e) => { setPriorityFilter(e.target.value); setCurrentPage(1); }}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Priorities</option>
                  <option value="normal">Normal</option>
                  <option value="fast_track">Fast Track</option>
                </select>
                {/* Status Filter */}
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="not_started">Not Started</option>
                  <option value="in_progress">In Progress</option>
                  <option value="on_hold">On Hold</option>
                  <option value="paused">Paused</option>
                  <option value="query">Query</option>
                </select>
                {/* Current Stage Filter */}
                <select
                  value={currentStageFilter}
                  onChange={(e) => { setCurrentStageFilter(e.target.value); setCurrentPage(1); }}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Stages</option>
                  <option value="Preflight">Preflight</option>
                  <option value="Single-up approval">Single-up approval</option>
                  <option value="Die creation">Die creation</option>
                  <option value="Recreation">Recreation</option>
                  <option value="Prepress , QC (self QC)">Prepress & QC</option>
                  <option value="Scanning task">Scanning task</option>
                  <option value="PI preparing">PI preparing</option>
                  <option value="Layout approval">Layout approval</option>
                  <option value="CMD">CMD</option>
                  <option value="CMD QC">CMD QC</option>
                  <option value="Epson Printout">Epson Printout</option>
                  <option value="RIP QC">RIP QC</option>
                  <option value="Plate and Despatch">Plate and Despatch</option>
                </select>
                {/* Job Location Filter */}
                <select
                  value={jobLocationFilter}
                  onChange={(e) => { setJobLocationFilter(e.target.value); setCurrentPage(1); }}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Locations</option>
                  {allJobLocations.map((loc) => (
                    <option key={loc} value={loc}>
                      {loc}
                    </option>
                  ))}
                </select>
                {/* Date Range Filter (created_at) */}
                <label className="text-sm text-gray-600 whitespace-nowrap">From:</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <label className="text-sm text-gray-600 whitespace-nowrap">To:</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {/* Assigned User Filter (PM/Admin/Team Leads) */}
                {isPrivileged && users.length > 0 && (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setAssignedUserDropdownOpen(s => !s)}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between min-w-[140px]"
                    >
                      <span className="truncate">
                        {assignedUserFilter
                          ? (users.find(u => String(u.id || u._id) === assignedUserFilter)?.name || 'Selected User')
                          : 'All Users'}
                      </span>
                      <svg className="w-4 h-4 text-gray-500 ml-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {assignedUserDropdownOpen && (
                      <div className="absolute right-0 z-10 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto min-w-[200px]">
                        <div className="p-2">
                          <input
                            type="text"
                            placeholder="Filter by user name..."
                            value={assignedUserDropdownFilter}
                            onChange={(e) => setAssignedUserDropdownFilter(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            autoFocus
                          />
                        </div>
                        <div className="divide-y divide-gray-100">
                          <button
                            type="button"
                            onClick={() => { setAssignedUserFilter(''); setCurrentPage(1); setAssignedUserDropdownOpen(false); setAssignedUserDropdownFilter(''); }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${!assignedUserFilter ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-500 italic'}`}
                          >
                            All Users
                          </button>
                          {users
                            .filter(u => assignedUserDropdownFilter === '' || (u.name || '').toLowerCase().includes(assignedUserDropdownFilter.toLowerCase()))
                            .map(u => (
                              <button
                                key={u.id || u._id}
                                type="button"
                                onClick={() => { setAssignedUserFilter(String(u.id || u._id)); setCurrentPage(1); setAssignedUserDropdownOpen(false); setAssignedUserDropdownFilter(''); }}
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${assignedUserFilter === String(u.id || u._id) ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                              >
                                {u.name}
                              </button>
                            ))}
                          {users.filter(u => assignedUserDropdownFilter === '' || (u.name || '').toLowerCase().includes(assignedUserDropdownFilter.toLowerCase())).length === 0 && (
                            <div className="px-3 py-3 text-sm text-gray-500">No users match.</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* Client Filter Button (PM/Admin) — turns red while a filter is
                    applied, since an unnoticed client filter silently hides
                    every project belonging to other clients. */}
                {(user?.role === 'pm' || user?.role === 'admin') && (() => {
                  const count = (clientFilter || []).length;
                  const active = count > 0;
                  return (
                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={() => {
                          setTempClientFilter(clientFilter);
                          setClientFilterDialogOpen(true);
                        }}
                        title={active
                          ? `Filtering to ${count === 1 ? clientFilter[0] : `${count} clients`} — projects for other clients are hidden`
                          : 'Filter projects by client'}
                        className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                          active
                            ? 'text-white bg-red-600 hover:bg-red-700 border-red-600 font-medium rounded-r-none'
                            : 'text-blue-700 bg-blue-50 hover:bg-blue-100 border-blue-300'
                        }`}
                      >
                        Client Filter
                        {active && (
                          <span className="ml-2 px-1.5 py-0.5 rounded-full bg-white text-red-700 text-xs font-bold">
                            {count}
                          </span>
                        )}
                      </button>
                      {active && (
                        <button
                          type="button"
                          onClick={() => { setClientFilter([]); setCurrentPage(1); }}
                          title="Remove the client filter"
                          className="px-2 py-2 text-sm text-white bg-red-600 hover:bg-red-700 border border-l-0 border-red-600 rounded-r-md transition-colors"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-gray-700 text-sm font-semibold uppercase tracking-wider">
                    <th className="px-6 py-3 font-medium">Job Code</th>
                    <th className="px-6 py-3 font-medium">Project Details</th>
                    <th className="px-6 py-3 font-medium">Job Location</th>
                    <th className="px-6 py-3 font-medium">Current Stage</th>
                    <th className="px-6 py-3 font-medium">Priority</th>
                    <th className="px-6 py-3 font-medium">Timeline</th>
                    <th className="px-6 py-3 font-medium">Backlog hrs</th>
                    {/* <th className="px-6 py-3 font-medium">Progress</th> */}
                    <th className="px-6 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(() => {
                    const role = user?.role;
                    const myUserId = user?.id;
                    const leadTeam = role ? getLeadTeamRole(role) : '';

                    let baseProjects = projects || [];

                    if (variant === 'my-projects') {
                      // Show projects where user has tasks assigned with: pending, in_progress, query, paused status
                      const allowedTaskStatuses = ['pending', 'in_progress', 'paused', 'query'];

                      const subset = (projects || []).filter((project) => {
                        if (!project || !Array.isArray(project.tasks)) return false;
                        if (project.status === 'completed') return false;
                        if (!myUserId) return false;

                        // Find tasks assigned to user with allowed statuses
                        const relevantTasks = project.tasks.filter(
                          (t) =>
                            allowedTaskStatuses.includes(t.status) &&
                            String(t.assigned_to) === String(myUserId)
                        );

                        // Show project only if user has at least one matching task
                        return relevantTasks.length > 0;
                      });

                      baseProjects = subset;
                    } else if (variant === 'hold-projects') {
                      // Role-based filtering for on_hold projects
                      const subset = (projects || []).filter((project) => {
                        if (!project) return false;

                        const tasks = Array.isArray(project.tasks) ? project.tasks : [];
                        const onHoldTasks = tasks.filter((t) => t.status === 'on_hold');
                        
                        // If no on_hold tasks at all, don't show project
                        if (onHoldTasks.length === 0) return false;

                        // PM/Admin: show all projects with on_hold tasks
                        if (role === 'pm' || role === 'admin') return true;

                        // Team Leads: show projects with on_hold tasks from their team members
                        if (
                          ['preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead', 'production_lead'].includes(
                            role || ''
                          )
                        ) {
                          const userTeam = getLeadTeamRole(role);
                          if (!userTeam) return false;
                          // Show if any on_hold task belongs to their team
                          return onHoldTasks.some((t) => getEffectiveTeam(t) === userTeam);
                        }

                        // Regular users: show only their own on_hold tasks
                        if (!myUserId) return false;
                        return onHoldTasks.some(
                          (t) => String(t.assigned_to) === String(myUserId)
                        );
                      });

                      baseProjects = subset;
                    } else if (variant === 'on-approval-projects') {
                      const subset = (projects || []).filter((project) => {
                        // Only show Layout approval or Single-up approval stages
                        const approvalStages = ['Layout approval', 'Single-up approval'];
                        if (!approvalStages.includes(project.current_stage)) return false;

                        const tasks = Array.isArray(project.tasks) ? project.tasks : [];
                        const approvalTasks = tasks.filter((t) => t.task_type === 'approval');
                        if (approvalTasks.length === 0) return false;

                        const hasStartedApproval = approvalTasks.some((t) =>
                          ['in_progress', 'pending', 'paused', 'on_hold', 'query'].includes(t.status)
                        );

                        if (!hasStartedApproval) return false;

                        if (role === 'pm' || role === 'admin') return true;

                        if (
                          ['preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead', 'production_lead'].includes(
                            role || ''
                          )
                        ) {
                          const team = getLeadTeamRole(role);
                          if (!team) return false;
                          return approvalTasks.some((t) => getEffectiveTeam(t) === team);
                        }

                        if (myUserId) {
                          return approvalTasks.some(
                            (t) => String(t.assigned_to) === String(myUserId)
                          );
                        }

                        return false;
                      });

                      baseProjects = subset;
                    }

                    // Apply quick filter tabs
                    const isTL = role && [
                      'preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead', 'production_lead', 'epson_lead'
                    ].includes(role);
                    const isPM = role && (role === 'pm' || role === 'admin');

                    // TL/PM/Admin "My Projects" scope: pre-filter to only projects where the user personally has a task
                    if ((isTL || isPM) && myProjectsScope === 'my') {
                      const allowedStatuses = ['pending', 'in_progress', 'paused', 'query', 'on_hold'];
                      baseProjects = baseProjects.filter((project) => {
                        const tasks = Array.isArray(project.tasks) ? project.tasks : [];
                        return myUserId && tasks.some(
                          (t) => allowedStatuses.includes(t.status) && String(t.assigned_to) === String(myUserId)
                        );
                      });
                    }

                    if (quickFilter === 'my_projects') {
                      // Non-TL "My Projects" button: show projects with the user's own active tasks
                      const allowedStatuses = ['pending', 'in_progress', 'paused', 'query', 'on_hold'];
                      baseProjects = baseProjects.filter((project) => {
                        const tasks = Array.isArray(project.tasks) ? project.tasks : [];
                        return myUserId && tasks.some(
                          (t) => allowedStatuses.includes(t.status) && String(t.assigned_to) === String(myUserId)
                        );
                      });
                    } else if (quickFilter === 'on_approval') {
                      if (isTL) {
                        const teamRole = getLeadTeamRole(role);
                        baseProjects = baseProjects.filter((project) => {
                          const tasks = Array.isArray(project.tasks) ? project.tasks : [];
                          if (myProjectsScope === 'my') {
                            return myUserId && tasks.some(
                              (t) => t.task_type === 'approval' && t.status === 'in_progress' && String(t.assigned_to) === String(myUserId)
                            );
                          }
                          return tasks.some(
                            (t) => t.task_type === 'approval' && t.status === 'in_progress' && getEffectiveTeam(t) === teamRole
                          );
                        });
                      } else {
                        // PM/Admin: myProjectsScope already pre-filtered above; just show all on-approval
                        baseProjects = baseProjects.filter((project) => {
                          const tasks = Array.isArray(project.tasks) ? project.tasks : [];
                          return tasks.some((t) => t.task_type === 'approval' && t.status === 'in_progress');
                        });
                      }
                    } else if (quickFilter === 'on_hold') {
                      if (isTL) {
                        const teamRole = getLeadTeamRole(role);
                        baseProjects = baseProjects.filter((project) => {
                          const tasks = Array.isArray(project.tasks) ? project.tasks : [];
                          if (myProjectsScope === 'my') {
                            return myUserId && tasks.some(
                              (t) => t.status === 'on_hold' && String(t.assigned_to) === String(myUserId)
                            );
                          }
                          return tasks.some((t) => t.status === 'on_hold' && getEffectiveTeam(t) === teamRole);
                        });
                      } else {
                        // PM/Admin: myProjectsScope already pre-filtered above; show all on-hold
                        baseProjects = baseProjects.filter((project) => {
                          const tasks = Array.isArray(project.tasks) ? project.tasks : [];
                          return tasks.some((t) => t.status === 'on_hold');
                        });
                      }
                    } else if (quickFilter === 'paused') {
                      if (isTL) {
                        const teamRole = getLeadTeamRole(role);
                        baseProjects = baseProjects.filter((project) => {
                          const tasks = Array.isArray(project.tasks) ? project.tasks : [];
                          if (myProjectsScope === 'my') {
                            return myUserId && tasks.some(
                              (t) => t.status === 'paused' && String(t.assigned_to) === String(myUserId)
                            );
                          }
                          return tasks.some((t) => t.status === 'paused' && getEffectiveTeam(t) === teamRole);
                        });
                      } else {
                        // PM/Admin: myProjectsScope already pre-filtered above; show all paused
                        baseProjects = baseProjects.filter((project) => {
                          const tasks = Array.isArray(project.tasks) ? project.tasks : [];
                          return tasks.some((t) => t.status === 'paused');
                        });
                      }
                    } else if (quickFilter === 'on_query') {
                      if (isTL) {
                        const teamRole = getLeadTeamRole(role);
                        baseProjects = baseProjects.filter((project) => {
                          const tasks = Array.isArray(project.tasks) ? project.tasks : [];
                          if (myProjectsScope === 'my') {
                            return myUserId && tasks.some(
                              (t) => t.status === 'query' && String(t.assigned_to) === String(myUserId)
                            );
                          }
                          return tasks.some((t) => t.status === 'query' && getEffectiveTeam(t) === teamRole);
                        });
                      } else {
                        // PM/Admin: myProjectsScope already pre-filtered above; show all on-query
                        baseProjects = baseProjects.filter((project) => {
                          const tasks = Array.isArray(project.tasks) ? project.tasks : [];
                          return tasks.some((t) => t.status === 'query');
                        });
                      }
                    } else if (quickFilter === 'not_started') {
                      baseProjects = baseProjects.filter((project) => {
                        const tasks = Array.isArray(project.tasks) ? project.tasks : [];
                        const allNotStarted = tasks.length > 0 && tasks.every(
                          (t) => t.status === 'not_started' || t.status === 'pending'
                        );
                        return allNotStarted;
                      });
                    } else if (quickFilter === 'in_progress') {
                      if (isTL) {
                        const teamRole = getLeadTeamRole(role);
                        baseProjects = baseProjects.filter((project) => {
                          const tasks = Array.isArray(project.tasks) ? project.tasks : [];
                          if (myProjectsScope === 'my') {
                            return myUserId && tasks.some(
                              (t) => t.status === 'in_progress' && t.task_type !== 'approval' && String(t.assigned_to) === String(myUserId)
                            );
                          }
                          return tasks.some(
                            (t) => t.status === 'in_progress' && t.task_type !== 'approval' && getEffectiveTeam(t) === teamRole
                          );
                        });
                      } else {
                        // PM/Admin: myProjectsScope already pre-filtered above; show all in-progress
                        baseProjects = baseProjects.filter((project) => {
                          const tasks = Array.isArray(project.tasks) ? project.tasks : [];
                          return tasks.some((t) => t.status === 'in_progress' && t.task_type !== 'approval');
                        });
                      }
                    }

                    // Server already applied search/priority/status/stage/location/client filters.
                    // Only apply role-based visibility and exclude completed projects here.
                    const filteredProjects = (baseProjects || []).filter((project) => {
                      if (project.status === 'completed') return false;
                      // Legacy fallback for old data without status field
                      if (!project.status && project.allTasksCompleted) return false;
                      return isProjectVisibleToUser(project);
                    });

                    return (
                      <>
                      {filteredProjects.map((project) => (
                      <tr
                        key={project.id}
                        // Row tint comes from the current task status. The selected
                        // row keeps its own highlight so the selection is never lost
                        // against a coloured row.
                        className={`cursor-pointer transition-colors ${
                          selectedProject && selectedProject.id === project.id
                            ? 'bg-blue-100 ring-2 ring-inset ring-blue-500'
                            : getRowStatusColor(project.current_status || 'not_started')
                        }`}
                        onClick={() => {
                          setSelectedProject(project);
                          setTaskDialogOpen(true);
                          setCmdStatus({ cmd_tasks: [], count: 0, all_completed: false });
                          setCmdAck({ open: false, tasks: [] });
                          api.get(`/pm/projects/${project.id}/cmd-status`, { headers: { Authorization: `Bearer ${token}` } })
                            .then((res) => {
                              const data = res.data || { cmd_tasks: [], count: 0, all_completed: false };
                              setCmdStatus(data);
                              // Warn (blocking) only when the project has CMD work that isn't finished yet.
                              if ((data.cmd_tasks || []).length > 0 && !data.all_completed) {
                                setCmdAck({ open: true, tasks: data.cmd_tasks });
                              }
                            })
                            .catch(() => {});
                        }}
                      >

                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1">
                            <span className="text-base font-semibold text-gray-900">
                              {project.job_code || 'N/A'}
                            </span>
                            {(project.job_nature || project.job_type) && (
                              <span className="text-sm text-gray-500">
                                {[project.job_nature, project.job_type]
                                  .filter((v) => (v || '').trim().length > 0)
                                  .join(' | ')}
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="px-6 py-4 max-w-xs">
                          <div>
                            <div className="text-base font-medium text-gray-900 break-all whitespace-normal" title={project.title}>{project.title}</div>
                            <div className="text-sm text-gray-600 break-all whitespace-normal" title={project.client}>{project.client}</div>
                          </div>
                        </td>

                        <td className="px-6 py-4 text-base text-gray-700">
                          {project.job_location || '-'}
                        </td>

                        {(() => {
                          const stage = project.current_stage || 'Not Started';
                          const sc = getStageColors(stage);
                          return (
                            <td className="px-6 py-4">
                              <div className="flex flex-col items-start gap-1.5">
                                <span className={`inline-block text-base font-semibold px-3 py-1.5 rounded-lg border ${sc.badge}`}>
                                  {stage}
                                </span>
                                <span className="text-sm text-gray-700">
                                  {(() => {
                                    const currentTask = getProjectCurrentTask(project.id);
                                    return currentTask ? (currentTask.assigned_user_name || 'Unassigned') : '-';
                                  })()}
                                </span>
                                <span className={`text-sm font-semibold px-2.5 py-1 rounded-full w-fit ${getStatusColor(project.current_status || 'not_started')}`}>
                                  {(project.current_status || 'not_started').replace('_', ' ')}
                                </span>
                              </div>
                            </td>
                          );
                        })()}

                        <td className="px-6 py-4">
                          <span className={`px-2.5 py-1 rounded-full text-sm font-medium ${getPriorityColor(project.priority)}`}>
                            {project.priority}
                          </span>
                        </td>

                        <td className="px-6 py-4 text-sm">
                          <div className="space-y-2">
                            <div className="flex items-center text-gray-600">
                              {project.created_at ? new Date(project.created_at).toLocaleString() : 'N/A'}
                            </div>
                            <div>
                              {(() => {
                                // Show loading state if created_at is not available yet
                                if (!project.created_at) {
                                  return <span className="text-sm text-gray-400 italic">Loading...</span>;
                                }
                                
                                // Show hours since creation for all projects
                                const timeSinceCreation = getHoursSinceCreation(project.created_at);
                                const createdDateStr = new Date(project.created_at).toLocaleDateString();
                                
                                // Backlog moved to its own column; the Timeline
                                // column now only reports booking time and status.
                                const isOverdue = getBacklogHours(project) > 0;

                                return (
                                  <div className="flex flex-col space-y-1">
                                    <div className="text-sm text-gray-600">
                                      <span className="font-semibold">Hours from booking:</span> {formatHoursSinceCreation(timeSinceCreation)}
                                    </div>
                                    {!isOverdue && (
                                      <div className="text-sm text-green-600 font-medium">
                                        ✓ On Time
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </td>

                        {/* Backlog hrs — escalating red bands at 8 / 12 / 24 / 48 hours */}
                        <td className="px-6 py-4">
                          {(() => {
                            const hours = getBacklogHours(project);
                            if (hours === null) {
                              return <span className="text-sm text-gray-400 italic">Loading...</span>;
                            }
                            if (hours <= 0) {
                              return <span className="text-sm text-gray-400">—</span>;
                            }
                            const band = getBacklogBand(hours);
                            return (
                              <span
                                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border text-sm font-bold whitespace-nowrap ${band.cls}`}
                                title={`${hours} hour${hours !== 1 ? 's' : ''} past the delivery date (${band.label} band)`}
                              >
                                {formatBacklog(hours)}
                              </span>
                            );
                          })()}
                        </td>

                        {/* <td className="px-6 py-4">
                          {(() => {
                            // Check if project is fully completed (all tasks completed or rejected)
                            const projectTaskList = allProjectTasks[project.id] || [];
                            
                            // Filter tasks based on enabled_tasks to match visibility
                            const enabledTasks = project.enabled_tasks || [];
                            const applicableTasks = projectTaskList.filter(t => 
                              enabledTasks.length === 0 || enabledTasks.includes(t.step_order)
                            );
                            
                            let completionPercentage = project.completion_percentage || 0;
                            
                            // Recalculate based on applicable tasks
                            if (applicableTasks.length > 0) {
                              const completedCount = applicableTasks.filter(t => t.status === 'completed' || t.status === 'rejected').length;
                              if (completedCount === applicableTasks.length) {
                                completionPercentage = 100;
                              } else {
                                completionPercentage = Math.round((completedCount / applicableTasks.length) * 100 * 10) / 10;
                              }
                            }
                            
                            return (
                              <>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${completionPercentage}%` }}
                                  ></div>
                                </div>
                                <div className="text-xs text-gray-500 mt-1">{completionPercentage}% complete</div>
                              </>
                            );
                          })()}
                        </td> */}
                        
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end space-x-2" onClick={(e) => e.stopPropagation()}>
                            {/* Version 2 Creation - Only available if Plate and Despatch task hasn't started */}
                            {(() => {
                              // Check if this project has Plate and Despatch task and if it hasn't started
                              const hasPlateTask = project.enabled_tasks?.includes(8) ?? true;
                              const projectTaskList = allProjectTasks[project.id];
                              const plateTask = projectTaskList?.find(t => t.name === 'Plate and Despatch');
                              // Only allow next version creation if Plate and Despatch exists AND is in 'not_started' status
                              const canCreateNextVersion = hasPlateTask && plateTask && plateTask.status === 'not_started' && isPrivileged;
                              
                              return canCreateNextVersion && (
                                <button
                                  onClick={() => handleCreateVersion2(project)}
                                  className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-full"
                                  title="Create next version (only available before Plate and Despatch starts)"
                                >
                                  <Plus className="w-4 h-4" />
                                </button>
                              );
                            })()}
                            
                            {canEditProject && (
                              <button
                                onClick={() => handleOpenDialog(project)}
                                className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-full"
                                title="Edit Project"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                            )}
                            {(user?.role === 'pm' || user?.role === 'admin') && project.status !== 'cancelled' && (
                              <button
                                onClick={() => handleOpenCancelProject(project)}
                                className="p-1.5 text-orange-600 hover:bg-orange-50 rounded-full"
                                title="Cancel Project"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            )}
                            {user?.role === 'admin' && (
                              <button
                                onClick={() => handleDeleteProject(project.id)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-full"
                                title="Delete Project"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredProjects.length === 0 && (
                      <tr>
                        <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                          {hasActiveFilters() ? (
                            <div className="max-w-xl mx-auto">
                              <p className="font-medium text-gray-700">No projects match the current filters.</p>
                              <div className="flex flex-wrap justify-center gap-2 mt-3">
                                {activeFilterList().map((f) => (
                                  <span key={f.key} className="px-2 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                                    {f.label}
                                  </span>
                                ))}
                              </div>
                              <button
                                onClick={clearAllFilters}
                                className="mt-4 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                              >
                                Clear all filters
                              </button>
                            </div>
                          ) : (
                            <>No projects found. {isPrivileged ? 'Create a new project to get started.' : 'Contact your project manager to have projects assigned.'}</>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                );
                  })()}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls — hidden when a quick filter is active (all records loaded at once) */}
            <div className={`px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50 ${quickFilter ? 'hidden' : ''}`}>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">
                  Items per page:
                </span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing items per page
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              <div className="flex items-center gap-4">
                {(() => {
                  const totalPages = Math.ceil(totalProjectsCount / itemsPerPage) || 1;
                  const startItem = (currentPage - 1) * itemsPerPage + 1;
                  const endItem = Math.min(currentPage * itemsPerPage, totalProjectsCount);

                  return (
                    <>
                      <span className="text-sm text-gray-600">
                        Showing {totalProjectsCount > 0 ? startItem : 0} to {endItem} of {totalProjectsCount}
                      </span>

                      <div className="flex gap-2">
                        <button
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          ← Previous
                        </button>

                        <div className="flex items-center gap-1">
                          {(() => {
                            const pages = [];
                            const startPage = Math.max(1, currentPage - 3);
                            const endPage = Math.min(totalPages, currentPage + 3);

                            for (let page = startPage; page <= endPage; page += 1) {
                              pages.push(page);
                            }

                            return pages.map((page) => (
                              <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                  page === currentPage
                                    ? 'bg-blue-600 text-white'
                                    : 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                                }`}
                              >
                                {page}
                              </button>
                            ));
                          })()}
                        </div>

                        <button
                          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                          disabled={currentPage === totalPages}
                          className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Next →
                        </button>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Client Filter Dialog (PM/Admin) */}
      {clientFilterDialogOpen && (user?.role === 'pm' || user?.role === 'admin') && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Client Filter</h3>
              <button
                onClick={() => setClientFilterDialogOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <p className="text-sm text-gray-600 mb-3">
                Select one or more clients to filter active projects. Leave all unchecked to see projects for all clients.
              </p>
              <div className="flex items-center gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setTempClientFilter(availableClients)}
                  className="px-3 py-1.5 text-xs border border-gray-300 rounded-md bg-gray-50 hover:bg-gray-100 text-gray-700"
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={() => setTempClientFilter([])}
                  className="px-3 py-1.5 text-xs border border-gray-300 rounded-md bg-gray-50 hover:bg-gray-100 text-gray-700"
                >
                  Clear All
                </button>
              </div>
              {availableClients.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No clients found from active projects.
                </p>
              ) : (
                <div className="space-y-2">
                  {availableClients.map((name) => (
                    <label
                      key={name}
                      className="flex items-center gap-2 text-sm text-gray-700"
                    >
                      <input
                        type="checkbox"
                        checked={tempClientFilter.includes(name)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setTempClientFilter((prev) =>
                              prev.includes(name) ? prev : [...prev, name]
                            );
                          } else {
                            setTempClientFilter((prev) =>
                              prev.filter((c) => c !== name)
                            );
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>{name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={() => setClientFilterDialogOpen(false)}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  handleClientFilterChange(tempClientFilter);
                  setClientFilterDialogOpen(false);
                }}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Task Management Modal */}
      {taskDialogOpen && selectedProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-8xl max-h-[95vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800" title={selectedProject.title}>
                  {selectedProject.job_code} ( {selectedProject.title} )
                </h3>
                <button onClick={() => setTaskDialogOpen(false)} className="text-gray-500 hover:text-gray-700">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* CMD status banner — so prepress users know whether the CMD
                  work on this project is done before they start prepress. */}
              {(() => {
                const cmdTasks = cmdStatus.cmd_tasks || [];
                if (cmdTasks.length === 0) return null;
                const statusStyle = (s) => ({
                  completed:   'bg-green-100 text-green-700 border-green-200',
                  in_progress: 'bg-blue-100 text-blue-700 border-blue-200',
                  paused:      'bg-orange-100 text-orange-700 border-orange-200',
                  on_hold:     'bg-red-100 text-red-700 border-red-200',
                  query:       'bg-purple-100 text-purple-700 border-purple-200',
                  rejected:    'bg-red-100 text-red-700 border-red-200',
                }[s] || 'bg-gray-100 text-gray-600 border-gray-200');
                const allDone = cmdTasks.every(t => t.status === 'completed');
                return (
                  <div className={`mt-3 rounded-lg border px-4 py-2.5 ${allDone ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-gray-700">CMD status:</span>
                      {cmdTasks.map((t, i) => (
                        <span key={t.id || i} className="inline-flex items-center gap-1.5">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusStyle(t.status)}`}>
                            {(t.status || 'not started').replace(/_/g, ' ')}
                          </span>
                          {t.assigned_user_name && (
                            <span className="text-xs text-gray-500">· {t.assigned_user_name}</span>
                          )}
                        </span>
                      ))}
                      {!allDone && (
                        <span className="text-xs text-amber-700 ml-1">CMD work is not yet complete on this project.</span>
                      )}
                    </div>
                  </div>
                );
              })()}

              {(canInsertFlexibleTasks || canInsertPrepressExtras || canInsertCMDPath || canInsertMountingRIPQC) && (
                <div className="flex flex-wrap justify-center gap-2 mt-3">
                  {canInsertFlexibleTasks && (
                    <>
                      {canInsertDieCreationTask && (
                        <button
                          onClick={handleInsertDieCreationTask}
                          disabled={projectTasks.some(
                            (t) =>
                              t.name === 'Die creation' &&
                              t.status !== 'completed' &&
                              t.status !== 'rejected'
                          ) || hasRunningTaskForUser}
                          title={hasRunningTaskForUser
                            ? 'Pause, hold, end, or query your current task before adding Die creation'
                            : 'Insert Die creation task (flexible workflow)'}
                          className="px-3 py-1.5 bg-orange-600 text-white rounded-md text-sm font-medium hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Insert Die creation
                        </button>
                      )}
                      <button
                        onClick={handleInsertRecreationTask}
                        disabled={projectTasks.some(
                          (t) =>
                            t.name === 'Recreation' &&
                            t.status !== 'completed' &&
                            t.status !== 'rejected'
                        ) || hasRunningTaskForUser}
                        title={hasRunningTaskForUser
                          ? 'Pause, hold, end, or query your current task before adding Recreation'
                          : 'Insert Recreation task (flexible workflow)'}
                        className="px-3 py-1.5 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Insert Recreation
                      </button>
                    </>
                  )}
                  {canInsertPrepressExtras && (
                    <>
                      <button
                        onClick={handleInsertScanningTask}
                        disabled={projectTasks.some(t => t.name === 'Scanning task' && t.status !== 'completed' && t.status !== 'rejected') || hasRunningTaskForUser}
                        title={hasRunningTaskForUser
                          ? 'Pause, hold, end, or query your current task before adding Scanning task'
                          : 'Insert Scanning task (flexible prepress workflow)'}
                        className="px-3 py-1.5 bg-teal-600 text-white rounded-md text-sm font-medium hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Insert Scanning task
                      </button>
                      <button
                        onClick={handleInsertPiPreparingTask}
                        disabled={projectTasks.some(t => t.name === 'PI preparing' && t.status !== 'completed' && t.status !== 'rejected') || hasRunningTaskForUser}
                        title={hasRunningTaskForUser
                          ? 'Pause, hold, end, or query your current task before adding PI preparing'
                          : 'Insert PI preparing task (flexible prepress workflow)'}
                        className="px-3 py-1.5 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Insert PI preparing
                      </button>
                      <button
                        onClick={handleInsertImageExportTask}
                        disabled={projectTasks.some(t => t.name === 'Image Export' && t.status !== 'completed' && t.status !== 'rejected') || hasRunningTaskForUser}
                        title={hasRunningTaskForUser
                          ? 'Pause, hold, end, or query your current task before adding Image Export'
                          : 'Insert Image Export task (flexible prepress workflow)'}
                        className="px-3 py-1.5 bg-pink-600 text-white rounded-md text-sm font-medium hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Insert Image Export
                      </button>
                      <button
                        onClick={() => handleInsertFlexibleTask('Image Duplication')}
                        disabled={projectTasks.some(t => t.name === 'Image Duplication' && t.status !== 'completed' && t.status !== 'rejected') || hasRunningTaskForUser}
                        title={hasRunningTaskForUser
                          ? 'Pause, hold, end, or query your current task before adding Image Duplication'
                          : 'Insert Image Duplication task (flexible prepress workflow)'}
                        className="px-3 py-1.5 bg-fuchsia-600 text-white rounded-md text-sm font-medium hover:bg-fuchsia-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Insert Image Duplication
                      </button>
                    </>
                  )}
                  {canInsertFlexibleTasks && (
                    <button
                      onClick={() => handleInsertCMDTask()}
                      disabled={projectTasks.some(t => t.name === 'CMD' && t.status !== 'completed') || hasRunningTaskForUser}
                      title={hasRunningTaskForUser
                        ? 'Pause, hold, end, or query your current task before adding CMD'
                        : 'Insert CMD task at any time (flexible workflow)'}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Insert CMD
                    </button>
                  )}
                  {canInsertCMDPath && (
                    <button
                      onClick={handleInsertCMDPathTask}
                      disabled={projectTasks.some(t => t.name === 'CMD Path' && t.status !== 'completed' && t.status !== 'rejected') || hasRunningTaskForUser}
                      title={hasRunningTaskForUser
                        ? 'Pause, hold, end, or query your current task before adding CMD Path'
                        : 'Insert CMD Path task (flexible CMD workflow)'}
                      className="px-3 py-1.5 bg-emerald-600 text-white rounded-md text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Insert CMD Path
                    </button>
                  )}
                  {canInsertMountingRIPQC && (
                    <button
                      onClick={handleInsertMountingRIPQCTask}
                      disabled={projectTasks.some(t => t.name === 'Mounting or RIP & Send QC' && t.status !== 'completed' && t.status !== 'rejected') || hasRunningTaskForUser}
                      title={hasRunningTaskForUser
                        ? 'Pause, hold, end, or query your current task before adding Mounting or RIP & Send QC'
                        : 'Insert Mounting or RIP & Send QC task (appears before RIP QC)'}
                      className="px-3 py-1.5 bg-rose-600 text-white rounded-md text-sm font-medium hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Insert Mounting / RIP & Send QC
                    </button>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 min-h-0">
              {/* Project Summary */}
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-lg font-semibold text-gray-800">Project Summary</h4>
                  {user && ['preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead', 'production_lead', 'epson_lead', 'pm', 'admin'].includes(user.role) && (
                    <button 
                      onClick={() => handleOpenComplexityModal()}
                      className="flex items-center text-sm text-blue-600 hover:text-blue-800 font-medium bg-white px-2 py-1 rounded border border-blue-200 shadow-sm transition-colors"
                      title="Update project complexity based on file analysis"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Change Complexity
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {(() => {
                    const enabledTasks = selectedProject?.enabled_tasks || [];
                    const filteredTasks = projectTasks.filter(t => enabledTasks.includes(t.step_order));
                    
                    // Exclude approval tasks (Single-up approval and Layout approval) from calculations
                    const nonApprovalTasks = filteredTasks.filter(task => 
                      task.task_type !== 'approval'
                    );
                    
                    const totalActualHours = nonApprovalTasks.reduce((sum, task) => {
                      return sum + parseFloat(taskTimings[task.id]?.total || 0);
                    }, 0);
                    
                    // Project-level complexity - calculate total expected minutes from all tasks
                    const projectComplexityKey = selectedProject?.complexity || 'simple';
                    const complexityTaskData = complexityConfig[projectComplexityKey];
                    
                    let totalExpectedMinutes = 0;
                    if (complexityTaskData && typeof complexityTaskData === 'object') {
                      // New format: nested object with task names as keys
                      nonApprovalTasks.forEach(task => {
                        const taskMinutes = complexityTaskData[task.name];
                        if (taskMinutes !== undefined) {
                          totalExpectedMinutes += taskMinutes;
                        }
                      });
                    } else if (typeof complexityTaskData === 'number') {
                      // Old format: just a number
                      totalExpectedMinutes = nonApprovalTasks.length * (complexityTaskData || 0);
                    }
                    
                    const totalExpectedHours = totalExpectedMinutes / 60;
                    
                    const overallEfficiency = totalExpectedHours > 0 ? 
                      ((totalExpectedHours / totalActualHours) * 100) : 0;
                    
                    const efficiencyColor = overallEfficiency >= 100 ? 
                      (overallEfficiency >= 120 ? 'text-green-600' : 'text-blue-600') : 
                      (overallEfficiency >= 80 ? 'text-yellow-600' : 'text-red-600');

                    // Aggregate internal/external error counts across all tasks in the project
                    let projectInternalErrors = 0;
                    let projectExternalErrors = 0;

                    projectTasks.forEach((task) => {
                      if (Array.isArray(task.error_reports)) {
                        task.error_reports.forEach((report) => {
                          if (Array.isArray(report.internal_error_categories)) {
                            projectInternalErrors += report.internal_error_categories.length;
                          }
                          if (Array.isArray(report.external_error_categories)) {
                            projectExternalErrors += report.external_error_categories.length;
                          }
                        });
                      }
                    });
                    
                    return (
                      <>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-gray-800">
                              {(() => {
                                const totalSeconds = Math.round(totalActualHours * 3600);
                                const hours = Math.floor(totalSeconds / 3600);
                                const minutes = Math.floor((totalSeconds % 3600) / 60);
                                const seconds = totalSeconds % 60;
                                return `${hours}h ${minutes}m ${seconds}s`;
                              })()}
                            </div>
                            <div className="text-sm text-gray-500">Actual Hours</div>
                          </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-gray-800"> {formatHours(totalExpectedHours)}</div>
                          {/* <div className="text-2xl font-bold text-gray-800">{totalExpectedHours.toFixed(2)}</div>
                          <div className="text-sm text-gray-500">{formatHours(totalExpectedHours)}</div> */}
                          <div className="text-sm text-gray-500">Expected Hours</div>
                        </div>
                        <div className="text-center">
                          <div className={`text-2xl font-bold ${efficiencyColor}`}>
                            {totalActualHours > 0 ? overallEfficiency.toFixed(1) : '0.0'}%
                          </div>
                          <div className="text-sm text-gray-500">Overall Efficiency</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-gray-800">
                            {projectInternalErrors} / {projectExternalErrors}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">Internals / Externals</div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
              
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider sticky top-0">
                      <th className="px-6 py-3 font-medium">Step</th>
                      <th className="px-6 py-3 font-medium">Task</th>
                      <th className="px-6 py-3 font-medium">Type</th>
                      <th className="px-6 py-3 font-medium">Complexity</th>
                      <th className="px-6 py-3 font-medium">Status</th>
                      <th className="px-6 py-3 font-medium">Assigned To</th>
                      <th className="px-6 py-3 font-medium">Start Time</th>
                      <th className="px-6 py-3 font-medium">End Time</th>
                      <th className="px-6 py-3 font-medium">Working Time</th>
                      <th className="px-6 py-3 font-medium">Efficiency</th>
                      <th className="px-6 py-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {projectTasks
                      .filter(task =>
                        // If no enabled_tasks configured, show all tasks
                        !selectedProject.enabled_tasks ||
                        selectedProject.enabled_tasks.length === 0 ||
                        // Show tasks whose step_order is enabled
                        selectedProject.enabled_tasks.includes(task.step_order) ||
                        // Always show flexible tasks (e.g. CMD, Die creation, Recreation)
                        task.is_flexible ||
                        // Die creation / Recreation are shared between preflight and prepress;
                        // always show them regardless of enabled_tasks or team.
                        task.name === 'Die creation' ||
                        task.name === 'Recreation' ||
                        // Always show later-round tasks (R2, R3...) created after rejection;
                        // their step_order is dynamic and won't be in enabled_tasks
                        (task.round && task.round > 1)
                      )
                      .filter(task => isTaskVisibleToUser(task))
                      .filter(task => variant === 'on-approval-projects' ? task.task_type === 'approval' : true)
                      .sort((a, b) => getWorkflowSortKey(a) - getWorkflowSortKey(b))
                      .map((task, index) => {
                        const taskQr = getStoredQrForTask(task.id);
                        // getDisplayTaskName already appends (R2), (R3) etc. for round > 1
                        const displayTaskName = getDisplayTaskName(task);
                        return (
                      <tr key={task.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <span className="w-8 h-8 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-sm font-medium">
                              {index + 1}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">{displayTaskName}</div>
                          {taskQr && (
                            <div className="text-xs text-gray-500 mt-1">QR: {taskQr}</div>
                          )}
                          {task.command && (
                            <div className="text-xs text-gray-700 mt-1">Command: {task.command}</div>
                          )}
                          {Array.isArray(task.error_reports) && task.error_reports.length > 0 && (() => {
                            const lastReport = task.error_reports[task.error_reports.length - 1] || {};
                            const internalList = Array.isArray(lastReport.internal_error_categories) ? lastReport.internal_error_categories : [];
                            const externalList = Array.isArray(lastReport.external_error_categories) ? lastReport.external_error_categories : [];

                            const internalCount = internalList.length;
                            const externalCount = externalList.length;

                            // Fallback to combined count if split data is missing
                            if (internalCount === 0 && externalCount === 0) {
                              return (
                                <div className="text-xs text-red-600 mt-1">
                                  Errors: {task.error_reports.length}
                                  {lastReport.rejection_reason ? `  ${lastReport.rejection_reason}` : ''}
                                </div>
                              );
                            }

                            const stringify = (values) => values
                              .map((val) => {
                                if (!val) return '';
                                if (!Array.isArray(errorCategories)) return String(val);

                                // Match against how we store categories elsewhere: by id, _id or name
                                const found = errorCategories.find(cat => cat.id === val || cat._id === val || cat.name === val);
                                return found && found.name ? found.name : String(val);
                              })
                              .filter(Boolean)
                              .join(', ');

                            const internalText = stringify(internalList);
                            const externalText = stringify(externalList);

                            return (
                              <div className="mt-1 space-y-0.5">
                                {internalCount > 0 && (
                                  <div className="text-xs text-red-600">
                                    Internal {internalCount}
                                    {internalText ? `  ${internalText}` : ''}
                                  </div>
                                )}
                                {externalCount > 0 && (
                                  <div className="text-xs text-blue-600">
                                    External {externalCount}
                                    {externalText ? `  ${externalText}` : ''}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                          {task.status === 'rejected' && task.rejected_reason && (
                            <div className="text-xs text-red-600 mt-1">Rejected: {task.rejected_reason}</div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTaskTypeColor(task.task_type)}`}>
                            {task.task_type}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {(() => {
                            // Use project-level complexity as the effective value so that
                            // changing project complexity is reflected across all tasks.
                            const effectiveComplexity = selectedProject?.complexity || task.complexity || 'simple';

                            return (
                              <div className="flex flex-col">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getComplexityColor(effectiveComplexity)}`}>
                                  {getComplexityDisplayName(effectiveComplexity)}
                                </span>
                                {(() => {
                                  const timing = getTaskComplexityTiming(task.name, effectiveComplexity);
                                  if (timing) {
                                    return <span className="text-xs text-gray-500 mt-1">{formatComplexityTime(timing)}</span>;
                                  }
                                  return null;
                                })()}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                            {task.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          <div className="flex flex-col">
                            <span>{task.assigned_user_name || 'Unassigned'}</span>
                            {task.assigned_at && (
                              <span className="text-xs text-gray-500 mt-0.5">
                                Assigned: {new Date(task.assigned_at).toLocaleString()}
                              </span>
                            )}
                            {task.status === 'completed' && task.completed_by_name && (
                              <span className="text-xs text-green-600 mt-1">
                                Completed by: {task.completed_by_name}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                          {(() => {
                            const start = taskTimings[task.id]?.startTime;
                            return start ? new Date(start).toLocaleString() : '-';
                          })()}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                          {(() => {
                            const end = taskTimings[task.id]?.endTime;
                            return end ? new Date(end).toLocaleString() : '-';
                          })()}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          <div className="flex flex-col space-y-1">
                            {(() => {
                              // Show time as Hrs/Min
                              const totalHours = parseFloat(taskTimings[task.id]?.total || 0);
                              const totalSeconds = Math.round(totalHours * 3600);
                              const hours = Math.floor(totalSeconds / 3600);
                              const minutes = Math.floor((totalSeconds % 3600) / 60);
                              const seconds = totalSeconds % 60;
                              if (totalHours === 0) return <span className="text-sm font-bold text-gray-400">0h 0m 0s</span>;
                              return <span className="text-sm font-bold text-gray-800">{hours}h {minutes}m {seconds}s</span>;
                            })()}
                            
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {(() => {
                            // Efficiency = (expected minutes / actual minutes) * 100
                            const actualHours = parseFloat(taskTimings[task.id]?.total || 0);
                            const actualMinutes = actualHours * 60;
                            // Get expected minutes for this task (by name and complexity)
                            const effectiveComplexity = selectedProject?.complexity || task.complexity || 'simple';
                            const expectedMinutes = getTaskComplexityTiming(task.name, effectiveComplexity) || 0;
                            if (actualMinutes === 0 || expectedMinutes === 0) {
                              return <span className="text-gray-400">-</span>;
                            }
                            const efficiency = (expectedMinutes / actualMinutes) * 100;
                            const efficiencyColor = efficiency >= 100 ? 
                              (efficiency >= 120 ? 'text-green-600' : 'text-blue-600') : 
                              (efficiency >= 80 ? 'text-yellow-600' : 'text-red-600');
                            return (
                              <span className={`font-medium ${efficiencyColor}`}>
                                {efficiency.toFixed(1)}% 
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end space-x-2">
                            {/* Activity Log - Always visible */}
                            <button
                              onClick={() => handleViewActivities(task)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-full"
                              title="View Activity Log"
                            >
                              <Info className="w-5 h-5" />
                            </button>

                            {/* Rejection Attachments - shown when task has been rejected at least once */}
                            {Array.isArray(task.error_reports) && task.error_reports.length > 0 && (
                              <button
                                onClick={() => handleOpenViewAttachments(task, selectedProject?.id)}
                                className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-full"
                                title="View Rejection Attachments"
                              >
                                <Paperclip className="w-5 h-5" />
                              </button>
                            )}

                            {/* Assignment - Only for privileged users and not for completed/rejected tasks. */}
                            {isPrivileged && task.status !== 'completed' && task.status !== 'rejected' && (
                              <>
                                <button
                                  onClick={() => handleOpenAssignDialog(task)}
                                  className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-full"
                                  title="Assign Task"
                                >
                                  <UserPlus className="w-5 h-5" />
                                </button>
                                {task.assigned_to && (
                                  <button
                                    onClick={() => handleUnassignTask(task)}
                                    className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-full"
                                    title="Unassign Task"
                                  >
                                    <X className="w-5 h-5" />
                                  </button>
                                )}
                              </>
                            )}

                            {/* Task Actions - primarily for the assigned user.
                              Business exceptions:
                              - Epson roles (epson/epson_lead) can control "Epson Printout".
                              - Production roles (production/production_lead) can control
                                "Plate and Despatch".
                            */}
                            {(() => {
                              const hasBlockingActivity =
                                (shiftSummary.current_status === 'idle' && !!shiftSummary.current_activity) ||
                                shiftSummary.current_status === 'break';

                              const isEpsonController =
                                task.name === 'Epson Printout' &&
                                (user?.role === 'epson' || user?.role === 'epson_lead');

                              const isProductionController =
                                task.name === 'Plate and Despatch' &&
                                (user?.role === 'production' || user?.role === 'production_lead');

                              const canControlTask =
                                task.status !== 'completed' &&
                                task.status !== 'rejected' &&
                                (task.assigned_to === user?.id || isEpsonController || isProductionController);

                              if (!canControlTask) return null;

                              return (
                              <>
                                {/* If task not started, paused, on hold, query, or pending, show Start/Resume button */}
                                {(task.status === 'not_started' || task.status === 'pending' || task.status === 'paused' || task.status === 'on_hold' || task.status === 'query') && (
                                  (() => {
                                    const startResumeTooltip = task.status === 'not_started' ? 'Start Task' : 'Resume Task';
                                    return (
                                  <button
                                    onClick={() => handleTaskStatusChange(task.id, 'start')}
                                    disabled={hasBlockingActivity}
                                    className={`p-1.5 text-blue-600 rounded-full ${hasBlockingActivity ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-50'}`}
                                    title={hasBlockingActivity
                                      ? 'End current activity or break before starting or resuming tasks'
                                      : startResumeTooltip}
                                  >
                                    <Play className="w-5 h-5" />
                                  </button>
                                    );
                                  })()
                                )}

                                {/* If task is in progress, show Pause, Hold, Query, Complete, and Reject (for approval tasks and RIP QC) */}
                                {task.status === 'in_progress' && (
                                  <>
                                    {/* Rejection - For approval tasks, RIP QC, Prepress QC, and Plate and Despatch */}
                                    {(task.task_type === 'approval' ||
                                      task.name === 'RIP QC' ||
                                      task.name === 'Prepress , QC (self QC)' ||
                                      task.name === 'Plate and Despatch') && (
                                      <button
                                        onClick={() => handleApprovalRejection(task.id, task.step_order)}
                                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-full"
                                        title={(() => {
                                          if (task.task_type === 'approval') return 'Reject approval - will add previous tasks back';
                                          if (task.name === 'RIP QC') return 'Reject RIP QC - will add Prepress QC back';
                                          if (task.name === 'Prepress , QC (self QC)') return 'Reject Prepress QC - will add Preflight again next to this task';
                                          if (task.name === 'Plate and Despatch') return 'Reject Plate and Despatch - will add Prepress QC, RIP QC, and Plate and Despatch again';
                                          return 'Reject task and add rework';
                                        })()}
                                      >
                                        <ArrowLeft className="w-5 h-5" />
                                      </button>
                                    )}

                                    {/* Pause - NOT for approval tasks */}
                                    {task.task_type !== 'approval' && (
                                      <button
                                        onClick={() => handleTaskStatusChange(task.id, 'pause')}
                                        disabled={hasBlockingActivity}
                                        className={`p-1.5 text-orange-600 rounded-full ${hasBlockingActivity ? 'opacity-50 cursor-not-allowed' : 'hover:bg-orange-50'}`}
                                        title={hasBlockingActivity ? 'End your break before managing tasks' : 'Pause Task'}
                                      >
                                        <Pause className="w-5 h-5" />
                                      </button>
                                    )}

                                    {/* Hold - NOT for approval tasks */}
                                    {task.task_type !== 'approval' && (
                                      <button
                                        onClick={() => handleTaskStatusChange(task.id, 'hold')}
                                        disabled={hasBlockingActivity}
                                        className={`p-1.5 text-red-600 rounded-full ${hasBlockingActivity ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-50'}`}
                                        title={hasBlockingActivity ? 'End your break before managing tasks' : 'Hold Task'}
                                      >
                                        <StopCircle className="w-5 h-5" />
                                      </button>
                                    )}

                                    {/* Query - NOT for approval tasks */}
                                    {task.task_type !== 'approval' && (
                                      <button
                                        onClick={() => handleTaskStatusChange(task.id, 'query')}
                                        disabled={hasBlockingActivity}
                                        className={`p-1.5 text-purple-600 rounded-full ${hasBlockingActivity ? 'opacity-50 cursor-not-allowed' : 'hover:bg-purple-50'}`}
                                        title={hasBlockingActivity ? 'End your break before managing tasks' : 'Query'}
                                      >
                                        <HelpCircle className="w-5 h-5" />
                                      </button>
                                    )}

                                    {/* Complete */}
                                    <button
                                      onClick={() => handleTaskStatusChange(task.id, 'complete')}
                                      disabled={hasBlockingActivity}
                                      className={`p-1.5 text-green-600 rounded-full ${hasBlockingActivity ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-50'}`}
                                      title={hasBlockingActivity ? 'End your break before managing tasks' : 'Complete Task'}
                                    >
                                      <CheckCircle className="w-5 h-5" />
                                    </button>
                                  </>
                                )}
                              </>
                            );
                            })()}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                    {projectTasks
                      .filter(task =>
                        !selectedProject.enabled_tasks ||
                        selectedProject.enabled_tasks.length === 0 ||
                        selectedProject.enabled_tasks.includes(task.step_order) ||
                        task.is_flexible ||
                        task.name === 'Die creation' ||
                        task.name === 'Recreation' ||
                        (task.round && task.round > 1)
                      )
                      .sort((a, b) => getWorkflowSortKey(a) - getWorkflowSortKey(b))
                      .length === 0 && (
                      <tr>
                        <td colSpan="9" className="px-6 py-8 text-center text-gray-500">
                          No tasks found for this project.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Audit Logs Section */}
              {auditLogs.length > 0 && ['admin', 'pm', 'preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead', 'production_lead'].includes(user?.role) && (
                <div className="mt-8">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Audit Logs</h4>
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-left border-collapse">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-3 font-medium text-xs text-gray-500 uppercase">Time</th>
                          <th className="px-6 py-3 font-medium text-xs text-gray-500 uppercase">User</th>
                          <th className="px-6 py-3 font-medium text-xs text-gray-500 uppercase">Action</th>
                          <th className="px-6 py-3 font-medium text-xs text-gray-500 uppercase">Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {auditLogs.map((log) => (
                          <tr key={log.id} className="hover:bg-gray-50">
                            <td className="px-6 py-3 text-sm text-gray-600 whitespace-nowrap">
                              {new Date(log.timestamp).toLocaleString()}
                            </td>
                            <td className="px-6 py-3 text-sm font-medium text-gray-900">
                              {log.user_name}
                            </td>
                            <td className="px-6 py-3 text-sm text-gray-700">
                              {log.action}
                            </td>
                            <td className="px-6 py-3 text-sm text-gray-500">
                              <div className="max-w-xs truncate" title={JSON.stringify(log.details, null, 2)}>
                                {JSON.stringify(log.details)}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setTaskDialogOpen(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Task Modal */}
      {assignModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                Assign Task
              </h3>
              <button onClick={() => {
                setAssignModalOpen(false);
                setAssignUserFilter('');
              }} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-4">
                {(() => {
                  if (!selectedTaskForAssign) return null;
                  const name = getDisplayTaskName(selectedTaskForAssign);
                  const round = selectedTaskForAssign.round && selectedTaskForAssign.round > 1 ? ` R${selectedTaskForAssign.round}` : '';
                  return (
                    <>
                      Select a user to assign <strong>{name}{round}</strong>.
                    </>
                  );
                })()}
              </p>

              <label className="block text-sm font-medium text-gray-700 mb-1">Assign User</label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setAssignDropdownOpen((s) => !s)}
                  className="w-full text-left px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between"
                >
                  <span className="truncate">
                    {selectedUserForAssign
                      ? (users.find(u => String(u.id) === String(selectedUserForAssign) || String(u._id) === String(selectedUserForAssign))?.name || 'Selected User')
                      : 'Select User'}
                  </span>
                  <svg className="w-4 h-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
                  </svg>
                </button>

                {assignDropdownOpen && (
                  <div className=" z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                    <div className="p-2">
                      <input
                        type="text"
                        placeholder="Filter by user name..."
                        value={assignUserFilter}
                        onChange={(e) => setAssignUserFilter(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        autoFocus
                      />
                    </div>
                    <div className="divide-y divide-gray-100">
                      {users
                        .filter(u => {
                          const rawTeam = selectedTaskForAssign?.team;
                          let taskTeam = rawTeam;

                          // Epson Printout uses the virtual "epson" team for assignment
                          const taskName = (selectedTaskForAssign?.name || '').toLowerCase();
                          if (taskName.includes('epson printout')) {
                            taskTeam = 'epson';
                          }

                          // Die creation and Recreation can be assigned to both preflight and prepress
                          const isDieOrRecreation = taskName === 'die creation' || taskName === 'recreation';

                          // Mounting or RIP & Send QC: only TLs, pm, admin, and prepress/qc team
                          const isMountingRIPQC = selectedTaskForAssign?.name === 'Mounting or RIP & Send QC';

                          // PM and admin can be assigned to any task
                          const isPmOrAdmin = u.role === 'pm' || u.role === 'admin';

                          if (isMountingRIPQC) {
                            const allLeads = [
                              'preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead',
                              'production_lead', 'epson_lead',
                            ];
                            const isPrepressQC = [
                              'prepress', 'prepress_lead', 'preflight_prepress',
                              'qc', 'qc_lead',
                            ].includes(u.role);
                            const isTL = allLeads.includes(u.role);
                            const isOnShift = isPmOrAdmin || (u.on_shift === undefined ? true : !!u.on_shift);
                            const matchesFilter = assignUserFilter === '' || (u.name || '').toLowerCase().includes(assignUserFilter.toLowerCase());
                            return (isPmOrAdmin || isTL || isPrepressQC) && isOnShift && matchesFilter;
                          }

                          if (!taskTeam) return true;

                          // Allow users with role matching team or team_lead
                          let isTeamUser =
                            u.role === taskTeam ||
                            u.role === `${taskTeam}_lead`;

                          // Combined Preflight & Prepress role can be assigned to
                          // both preflight and prepress team tasks.
                          if (taskTeam === 'preflight' || taskTeam === 'prepress') {
                            isTeamUser = isTeamUser || u.role === 'preflight_prepress';
                          }

                          // For Die creation/Recreation, also allow prepress operators
                          if (isDieOrRecreation) {
                            isTeamUser = isTeamUser ||
                              u.role === 'preflight' ||
                              u.role === 'preflight_lead' ||
                              u.role === 'prepress' ||
                              u.role === 'prepress_lead' ||
                              u.role === 'preflight_prepress';
                          }

                          // Only check on-shift status for non-PM/admin users (PMs don't start shifts)
                          const isOnShift = isPmOrAdmin || (u.on_shift === undefined ? true : !!u.on_shift);

                          // Filter by search term (case-insensitive)
                          const matchesFilter = assignUserFilter === '' || (u.name || '').toLowerCase().includes(assignUserFilter.toLowerCase());

                          return (isTeamUser || isPmOrAdmin) && isOnShift && matchesFilter;
                        })
                        .map(u => {
                          const uid = u.id || u._id;
                          return (
                            <button
                              key={uid}
                              type="button"
                              onClick={() => {
                                setSelectedUserForAssign(uid);
                                setAssignDropdownOpen(false);
                              }}
                              className="w-full text-left px-3 py-2 hover:bg-gray-50"
                            >
                              <div className="text-sm font-medium text-gray-900">{u.name}</div>
                              <div className="text-xs text-gray-500">{u.role}</div>
                            </button>
                          );
                        })}
                      {users.filter(u => {
                        const rawTeam = selectedTaskForAssign?.team;
                        let taskTeam = rawTeam;

                        const taskName = (selectedTaskForAssign?.name || '').toLowerCase();
                        if (taskName.includes('epson printout')) {
                          taskTeam = 'epson';
                        }

                        // Die creation and Recreation can be assigned to both preflight and prepress
                        const isDieOrRecreation = taskName === 'die creation' || taskName === 'recreation';

                        // Mounting or RIP & Send QC: only TLs, pm, admin, and prepress/qc team
                        const isMountingRIPQC = selectedTaskForAssign?.name === 'Mounting or RIP & Send QC';
                        const isPmOrAdmin = u.role === 'pm' || u.role === 'admin';

                        if (isMountingRIPQC) {
                          const allLeads = [
                            'preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead',
                            'production_lead', 'epson_lead',
                          ];
                          const isPrepressQC = [
                            'prepress', 'prepress_lead', 'preflight_prepress',
                            'qc', 'qc_lead',
                          ].includes(u.role);
                          const isTL = allLeads.includes(u.role);
                          const isOnShift = isPmOrAdmin || (u.on_shift === undefined ? true : !!u.on_shift);
                          const matchesFilter = assignUserFilter === '' || (u.name || '').toLowerCase().includes(assignUserFilter.toLowerCase());
                          return (isPmOrAdmin || isTL || isPrepressQC) && isOnShift && matchesFilter;
                        }

                        if (!taskTeam) return true;

                        let isTeamUser =
                          u.role === taskTeam ||
                          u.role === `${taskTeam}_lead`;

                        // Combined Preflight & Prepress role can be assigned to
                        // both preflight and prepress team tasks.
                        if (taskTeam === 'preflight' || taskTeam === 'prepress') {
                          isTeamUser = isTeamUser || u.role === 'preflight_prepress';
                        }

                        // For Die creation/Recreation, also allow prepress operators
                        if (isDieOrRecreation) {
                          isTeamUser = isTeamUser ||
                            u.role === 'preflight' ||
                            u.role === 'preflight_lead' ||
                            u.role === 'prepress' ||
                            u.role === 'prepress_lead' ||
                            u.role === 'preflight_prepress';
                        }

                        const isOnShift = u.on_shift === undefined ? true : !!u.on_shift;
                        const matchesFilter =
                          assignUserFilter === '' ||
                          (u.name || '').toLowerCase().includes(assignUserFilter.toLowerCase());
                        return isTeamUser && isOnShift && matchesFilter;
                      }).length === 0 && (
                        <div className="px-3 py-3 text-sm text-gray-500">No users match.</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setAssignModalOpen(false);
                  setAssignUserFilter('');
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignTask}
                disabled={!selectedUserForAssign}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Verification Modal (hardware scanner only: no camera, no manual typing) */}
      {qrModalOpen && pendingCompletionTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[70vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                {(() => {
                  if (!pendingCompletionTask) return null;
                  const name = pendingCompletionTask.name;
                  const round = pendingCompletionTask.round && pendingCompletionTask.round > 1 ? ` R${pendingCompletionTask.round}` : '';
                  return (
                    <>Scan QR to complete: {name}{round}</>
                  );
                })()}
              </h3>
              <button onClick={handleCloseQrModal} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4 overflow-y-auto">
              <p className="text-sm text-gray-600">
                Please use the QR scanner device. The cursor is ready and waiting for the QR code input. Do not type the code manually.
              </p>

              {qrScanError && (
                <div className="p-3 rounded bg-red-50 text-red-700 text-sm border border-red-200">
                  {qrScanError}
                </div>
              )}

              <div className="space-y-4">
                {/* Hidden input that receives scanner keyboard input. */}
                <input
                  type="text"
                  autoFocus
                  value={manualQrCode}
                  onChange={(e) => setManualQrCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleScannerCode(e.currentTarget.value);
                    }
                  }}
                  className="opacity-0 pointer-events-none absolute -z-10"
                  aria-hidden="true"
                />

                {qrScanResult && (
                  <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2 w-full text-center">
                    Last scanned: <span className="font-mono font-bold tracking-wider">{qrScanResult}</span>
                  </div>
                )}

                <div className="flex justify-end space-x-2">
                  <button
                    onClick={handleCloseQrModal}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Activity Log Modal */}
      {activityModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                {(() => {
                  // Try to find the current task object by title and add round if available
                  let displayTitle = currentTaskTitle;
                  // Try to find the task in projectTasks by name and round
                  const task = Array.isArray(projectTasks) && projectTasks.find(t => {
                    if (!t || !t.name) return false;
                    // If currentTaskTitle already has R{num}, match with round
                    if (currentTaskTitle.includes(' R')) {
                      const [base, r] = currentTaskTitle.split(' R');
                      return t.name === base && t.round && `R${t.round}` === `R${r}`;
                    }
                    return t.name === currentTaskTitle;
                  });
                  if (task && task.round && task.round > 1) {
                    displayTitle = `${task.name} R${task.round}`;
                  }
                  return <>Activity Log: {displayTitle}</>;
                })()}
              </h3>
              <button onClick={() => setActivityModalOpen(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider sticky top-0">
                    <th className="px-6 py-3 font-medium">Time</th>
                    <th className="px-6 py-3 font-medium">Event</th>
                    <th className="px-6 py-3 font-medium">User</th>
                    <th className="px-6 py-3 font-medium">Duration</th>
                    <th className="px-6 py-3 font-medium">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentTaskActivities.map((activity, index) => (
                    <tr key={activity.id || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-700 font-mono">
                        {new Date(activity.ts).toLocaleString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          activity.event.includes('start') || activity.event.includes('resume') ? 'bg-green-100 text-green-800' :
                          activity.event.includes('end') || activity.event.includes('pause') ? 'bg-orange-100 text-orange-800' :
                          activity.event === 'hold' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {activity.event.replace(/_/g, ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {activity.user_name || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 font-mono">
                        {formatDuration(activity.duration)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 italic">
                        {activity.reason || '—'}
                      </td>
                    </tr>
                  ))}
                  {currentTaskActivities.length === 0 && (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                        No activities recorded yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            <div className="flex justify-end p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setActivityModalOpen(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Project Dialog */}
      {openDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                {isEditingProject ? 'Edit Project' : 'New Project'}
              </h3>
              <button onClick={handleCloseDialog} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Project Title</label>
                    <input
                      type="text"
                      value={currentProject.title}
                      onChange={(e) => setCurrentProject({ ...currentProject, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Job Code</label>
                    <input
                      type="text"
                      value={currentProject.job_code}
                      onChange={(e) => setCurrentProject({ ...currentProject, job_code: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter job code"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Job Location</label>
                    {productionLocations && productionLocations.length > 0 ? (
                      <select
                        value={currentProject.job_location || ''}
                        onChange={(e) => setCurrentProject({ ...currentProject, job_location: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select job location</option>
                        {productionLocations.map((loc) => (
                          <option key={loc.id || loc._id} value={loc.name}>
                            {loc.name}
                          </option>
                        ))}
                        {currentProject.job_location &&
                          !productionLocations.some((loc) => loc.name === currentProject.job_location) && (
                            <option value={currentProject.job_location}>
                              {currentProject.job_location} (custom)
                            </option>
                          )}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={currentProject.job_location || ''}
                        onChange={(e) => setCurrentProject({ ...currentProject, job_location: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter job location"
                      />
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Job Nature</label>
                    <select
                      value={currentProject.job_nature || ''}
                      onChange={(e) => setCurrentProject({ ...currentProject, job_nature: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Job Nature</option>
                      {jobNatureList.map((item) => (
                        <option key={item.id} value={item.name}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Job Type</label>
                    <select
                      value={currentProject.job_type || ''}
                      onChange={(e) => setCurrentProject({ ...currentProject, job_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Job Type</option>
                      {jobTypeList.map((item) => (
                        <option key={item.id} value={item.name}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setClientDropdownOpen((s) => !s)}
                        className="w-full text-left px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between"
                      >
                        <span className="truncate">
                          {currentProject.client || 'Select Client'}
                        </span>
                        <svg className="w-4 h-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
                        </svg>
                      </button>

                      {clientDropdownOpen && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                          <div className="p-2">
                            <input
                              type="text"
                              placeholder="Filter by client name..."
                              value={clientDropdownFilter}
                              onChange={(e) => setClientDropdownFilter(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              autoFocus
                            />
                          </div>
                          <div className="divide-y divide-gray-100">
                            {/* Empty option */}
                            <button
                              type="button"
                              onClick={() => {
                                setCurrentProject({ ...currentProject, client: '' });
                                setClientDropdownOpen(false);
                                setClientDropdownFilter('');
                              }}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                                !currentProject.client ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-500 italic'
                              }`}
                            >
                              No Client
                            </button>
                            {clients
                              .filter(client => {
                                const matchesFilter = clientDropdownFilter === '' || 
                                  (client.name || '').toLowerCase().includes(clientDropdownFilter.toLowerCase());
                                return matchesFilter;
                              })
                              .map(client => (
                                <button
                                  key={client.id}
                                  type="button"
                                  onClick={() => {
                                    setCurrentProject({ ...currentProject, client: client.name });
                                    // On a NEW project, adopt the client's Epson setting. On an
                                    // existing project the saved choice is left alone, so
                                    // switching client never silently changes agreed tasks.
                                    if (!isEditingProject) {
                                      setEpsonTaskOverride(client.epson_enabled === true);
                                    }
                                    setClientDropdownOpen(false);
                                    setClientDropdownFilter('');
                                    fetchClients();
                                  }}
                                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                                    currentProject.client === client.name ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                                  }`}
                                >
                                  {client.name}
                                </button>
                              ))}
                            {clients.filter(client => 
                              clientDropdownFilter === '' || 
                              (client.name || '').toLowerCase().includes(clientDropdownFilter.toLowerCase())
                            ).length === 0 && (
                              <div className="px-3 py-3 text-sm text-gray-500">No clients match.</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                      <select
                        value={currentProject.priority}
                        onChange={(e) => setCurrentProject({ ...currentProject, priority: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="normal">Normal</option>
                        <option value="fast_track">Fast Track</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Complexity</label>
                      <select
                        value={currentProject.complexity}
                        onChange={(e) => setCurrentProject({ ...currentProject, complexity: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {Object.keys(complexityConfig || {}).map((key) => (
                          <option key={key} value={key}>
                            {getComplexityDisplayName(key)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">Delivery Date</label>
                      <span className="text-xs text-blue-600">
                        Auto: {calculateDeliveryDate(new Date()).toLocaleDateString()}
                      </span>
                    </div>
                    <input
                      type="date"
                      value={currentProject.deadline ? currentProject.deadline.split('T')[0] : ''}
                      onChange={(e) => setCurrentProject({ ...currentProject, deadline: e.target.value })}
                      placeholder={calculateDeliveryDate(new Date()).toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Leave empty for automatic calculation (same day if before 4:30 PM, next day after)
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={currentProject.description}
                      onChange={(e) => setCurrentProject({ ...currentProject, description: e.target.value })}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    ></textarea>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">Project tasks</label>
                  </div>
                  <div className="space-y-2 overflow-y-auto border border-gray-200 rounded-md p-3 bg-gray-50">
                    {(() => {
                      // Build preview: base tasks (no epson/plate) + user-toggled steps
                      let enabledForClient = getEnabledTasksForClient(
                        currentProject.client,
                        currentProject.job_nature
                      );
                      if (epsonTaskOverride && !enabledForClient.includes(7)) enabledForClient = [...enabledForClient, 7];
                      if (plateTaskOverride && !enabledForClient.includes(8)) enabledForClient = [...enabledForClient, 8];

                      return (
                        <>
                          {/* Epson Printout toggle — available for all projects */}
                          <div className="flex items-center mb-1">
                            <input
                              id="toggle-epson-task"
                              type="checkbox"
                              checked={epsonTaskOverride}
                              onChange={(e) => setEpsonTaskOverride(e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                            />
                            <label htmlFor="toggle-epson-task" className="text-xs text-gray-700">
                              Include Epson Printout step
                              {clientWantsEpson(currentProject.client) && (
                                <span
                                  className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200"
                                  title="This client has Epson Printout enabled in Settings → Customers"
                                >CLIENT DEFAULT</span>
                              )}
                            </label>
                          </div>
                          {clientWantsEpson(currentProject.client) && !epsonTaskOverride && (
                            <p className="text-[11px] text-amber-700 mb-2 -mt-1">
                              This client normally needs an Epson Printout — it will be excluded from this project.
                            </p>
                          )}

                          {/* Plate and Despatch toggle — available for all projects */}
                          <div className="flex items-center mb-2">
                            <input
                              id="toggle-plate-task"
                              type="checkbox"
                              checked={plateTaskOverride}
                              onChange={(e) => setPlateTaskOverride(e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                            />
                            <label htmlFor="toggle-plate-task" className="text-xs text-gray-700">
                              Include Plate and Despatch step
                            </label>
                          </div>

                          {DEFAULT_TASK_WORKFLOW.filter(task => enabledForClient.includes(task.step)).map((task) => (
                            <div key={task.step} className="flex items-center space-x-3 p-2 rounded bg-white">
                              <label htmlFor={`task-${task.step}`} className="flex-1 text-sm text-gray-700 cursor-not-allowed">
                                {task.name}
                              </label>
                            </div>
                          ))}
                        </>
                      );
                    })()}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Preflight and Single-up approval tasks are automatically determined by your client's settings. Update client settings to enable/disable it.
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6 border-t border-gray-200 bg-gray-50 pt-4">
                <button
                  onClick={handleCloseDialog}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProject}
                  className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {rejectionModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Reject Task: {rejectionModal.taskName}</h3>
              <button 
                onClick={closeRejectionModal} 
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Rejection</label>
                <textarea
                  value={rejectionModal.reason}
                  onChange={(e) => setRejectionModal({ ...rejectionModal, reason: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe why this task is being rejected..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Error Categories</label>
                {errorCategoriesLoading ? (
                  <div className="text-sm text-gray-500 py-3">Loading categories...</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">Internal Error Categories</span>
                      </div>
                      {errorCategories.filter(cat => (cat.type || 'internal') === 'internal').length === 0 ? (
                        <div className="text-xs text-gray-500 py-2 px-3 bg-yellow-50 border border-yellow-200 rounded-md">
                          No internal error categories configured. Please add them in Admin Settings.
                        </div>
                      ) : (
                        <div className="border border-gray-300 rounded-md p-3 space-y-2 max-h-40 overflow-y-auto bg-gray-50">
                          {errorCategories
                            .filter(cat => (cat.type || 'internal') === 'internal')
                            .map((category) => {
                              const categoryId = category.id || category._id;
                              const isSelected = (rejectionModal.internalCategories || []).includes(categoryId) ||
                                (rejectionModal.internalCategories || []).includes(category.name);

                              return (
                                <label key={categoryId} className="flex items-center space-x-2 cursor-pointer hover:bg-white p-2 rounded transition-colors">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleErrorCategory(categoryId, category.name, 'internal')}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                  />
                                  <span className="text-sm text-gray-700">{category.name}</span>
                                </label>
                              );
                            })}
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">External Error Categories</span>
                      </div>
                      {errorCategories.filter(cat => (cat.type || 'internal') === 'external').length === 0 ? (
                        <div className="text-xs text-gray-500 py-2 px-3 bg-yellow-50 border border-yellow-200 rounded-md">
                          No external error categories configured. Please add them in Admin Settings.
                        </div>
                      ) : (
                        <div className="border border-gray-300 rounded-md p-3 space-y-2 max-h-40 overflow-y-auto bg-gray-50">
                          {errorCategories
                            .filter(cat => (cat.type || 'internal') === 'external')
                            .map((category) => {
                              const categoryId = category.id || category._id;
                              const isSelected = (rejectionModal.externalCategories || []).includes(categoryId) ||
                                (rejectionModal.externalCategories || []).includes(category.name);

                              return (
                                <label key={categoryId} className="flex items-center space-x-2 cursor-pointer hover:bg-white p-2 rounded transition-colors">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleErrorCategory(categoryId, category.name, 'external')}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                  />
                                  <span className="text-sm text-gray-700">{category.name}</span>
                                </label>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* File Attachments */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Attachments <span className="text-red-500">*</span>
                  <span className="text-gray-400 font-normal text-xs ml-1">
                    (at least one file is required — attach evidence of the error)
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer w-max px-3 py-1.5 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100">
                  <Paperclip className="w-4 h-4" />
                  Attach files
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const newFiles = Array.from(e.target.files || []);
                      setRejectionModal(prev => ({ ...prev, files: [...(prev.files || []), ...newFiles] }));
                      e.target.value = '';
                    }}
                  />
                </label>
                {(rejectionModal.files || []).length === 0 && (
                  <p className="mt-2 text-xs text-red-600">
                    Attach at least one file before rejecting.
                  </p>
                )}
                {(rejectionModal.files || []).length > 0 && (
                  <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                    {(rejectionModal.files || []).map((f, idx) => (
                      <li key={idx} className="flex items-center justify-between text-xs bg-gray-50 border border-gray-200 rounded px-2 py-1">
                        <span className="truncate max-w-xs text-gray-700">{f.name}</span>
                        <button
                          onClick={() => setRejectionModal(prev => ({ ...prev, files: prev.files.filter((_, i) => i !== idx) }))}
                          className="ml-2 text-gray-400 hover:text-red-500 flex-shrink-0"
                          title="Remove"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={closeRejectionModal}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  fetchErrorCategories();
                  setRejectionModal({ ...rejectionModal });
                }}
                className="px-2 py-1 text-xs text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
                title="Refresh categories"
              >
                ↻
              </button>
              <button
                onClick={handleRejectionSubmit}
                disabled={
                  ((!Array.isArray(rejectionModal.internalCategories) || rejectionModal.internalCategories.length === 0) &&
                   (!Array.isArray(rejectionModal.externalCategories) || rejectionModal.externalCategories.length === 0))
                  // Evidence is now mandatory — a rejection without a file cannot
                  // be reviewed later, and it feeds the Pointing System penalty.
                  || (rejectionModal.files || []).length === 0
                }
                title={(rejectionModal.files || []).length === 0 ? 'Attach at least one file first' : ''}
                className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reject &amp; Add Rework
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Rejection Attachments Modal */}
      {viewAttachmentsModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                Attachments: {viewAttachmentsModal.taskName}
              </h3>
              <button
                onClick={() => setViewAttachmentsModal({ isOpen: false, projectId: null, taskId: null, taskName: '', files: [], loading: false })}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 min-h-[80px]">
              {viewAttachmentsModal.loading ? (
                <div className="text-sm text-gray-500 py-4 text-center">Loading attachments...</div>
              ) : viewAttachmentsModal.files.length === 0 ? (
                <div className="text-sm text-gray-500 py-4 text-center">No attachments found for this task.</div>
              ) : (
                <ul className="space-y-2">
                  {viewAttachmentsModal.files.map((f) => (
                    <li key={f.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-medium text-gray-800 truncate">{f.original_name}</span>
                        <span className="text-xs text-gray-400">
                          {f.uploaded_by_name && `By ${f.uploaded_by_name} · `}
                          {f.uploaded_at ? new Date(f.uploaded_at).toLocaleString() : ''}
                          {f.size ? ` · ${(f.size / 1024).toFixed(1)} KB` : ''}
                        </span>
                      </div>
                      <button
                        onClick={() => handleDownloadRejectionFile(f.id, f.original_name)}
                        className="ml-3 p-1.5 text-blue-600 hover:bg-blue-50 rounded-full flex-shrink-0"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex justify-end p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setViewAttachmentsModal({ isOpen: false, projectId: null, taskId: null, taskName: '', files: [], loading: false })}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complexity Modal */}
      {complexityModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Change Project Complexity</h3>
              <button 
                onClick={handleCloseComplexityModal} 
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  Current Complexity: <span className="font-semibold text-gray-900">{complexityModal.currentComplexity}</span>
                </p>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Complexity</label>
                <select
                  value={complexityModal.newComplexity}
                  onChange={(e) => setComplexityModal({ ...complexityModal, newComplexity: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Complexity</option>
                  {Object.keys(complexityConfig || {}).map((key) => (
                    <option key={key} value={key}>
                      {getComplexityDisplayName(key)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Change {complexityModal.requiresReason && <span className="text-red-500">*</span>}
                </label>
                <textarea
                  value={complexityModal.reason}
                  onChange={(e) => setComplexityModal({ ...complexityModal, reason: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe why you are changing the complexity..."
                />
                {complexityModal.requiresReason && !complexityModal.reason.trim() && (
                  <p className="text-xs text-red-500 mt-1">A reason is required since you didn't start the preflight.</p>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={handleCloseComplexityModal}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleComplexityChange}
                disabled={!complexityModal.newComplexity}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Update Complexity
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Project Modal */}
      {cancelProjectModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Cancel Project</h3>
              <button
                onClick={() => setCancelProjectModal({ isOpen: false, projectId: null, projectTitle: '', comment: '' })}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600">
                You are about to cancel <span className="font-semibold">{cancelProjectModal.projectTitle}</span>. This cannot be undone. Please provide a reason.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cancellation reason <span className="text-red-500">*</span></label>
                <textarea
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                  placeholder="Enter the reason for cancellation..."
                  value={cancelProjectModal.comment}
                  onChange={(e) => setCancelProjectModal((prev) => ({ ...prev, comment: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setCancelProjectModal({ isOpen: false, projectId: null, projectTitle: '', comment: '' })}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={handleCancelProject}
                disabled={!cancelProjectModal.comment.trim()}
                className="px-4 py-2 text-sm text-white bg-orange-600 rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hold / Pause Reason Modal */}
      {holdPauseModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 capitalize">
                {holdPauseModal.action === 'hold' ? 'Hold Task' : holdPauseModal.action === 'query' ? 'Raise Query' : 'Pause Task'}
              </h3>
              <button
                onClick={() => setHoldPauseModal({ isOpen: false, taskId: null, action: '', reason: '' })}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600">
                Please provide a reason for {holdPauseModal.action === 'hold' ? 'putting this task on hold' : holdPauseModal.action === 'query' ? 'raising a query on this task' : 'pausing this task'}.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
                <textarea
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder={holdPauseModal.action === 'hold' ? 'Why is this task on hold?' : holdPauseModal.action === 'query' ? 'What is the query?' : 'Why is this task being paused?'}
                  value={holdPauseModal.reason}
                  onChange={(e) => setHoldPauseModal((prev) => ({ ...prev, reason: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setHoldPauseModal({ isOpen: false, taskId: null, action: '', reason: '' })}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={handleHoldPauseSubmit}
                disabled={!holdPauseModal.reason.trim()}
                className={`px-4 py-2 text-sm text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${holdPauseModal.action === 'hold' ? 'bg-red-600 hover:bg-red-700' : holdPauseModal.action === 'query' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-orange-600 hover:bg-orange-700'}`}
              >
                Confirm {holdPauseModal.action === 'hold' ? 'Hold' : holdPauseModal.action === 'query' ? 'Query' : 'Pause'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Long shift — reason collected at clock-out for the morning report.
          The shift has already ended, so this cannot block anything. */}
      {longShiftModal.open && (
        <div className="fixed inset-0 z-[9991] flex items-center justify-center bg-black bg-opacity-60 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="flex items-center p-4 bg-purple-50 border-b border-purple-200">
              <Clock className="w-6 h-6 text-purple-600 mr-3 flex-shrink-0" />
              <h3 className="text-lg font-semibold text-purple-800">Long Shift</h3>
            </div>
            <div className="p-6 text-center">
              <p className="text-gray-800 font-medium mb-1">
                Your shift ran{' '}
                <span className="font-bold text-purple-700">
                  {longShiftModal.minutes >= 60
                    ? `${Math.floor(longShiftModal.minutes / 60)}h ${longShiftModal.minutes % 60}m`
                    : `${longShiftModal.minutes} min`}
                </span>
                {longShiftModal.limit_minutes ? (
                  <>
                    {' '}&mdash;{' '}
                    <span className="font-bold text-purple-700">
                      {Math.floor(longShiftModal.over_minutes / 60)
                        ? `${Math.floor(longShiftModal.over_minutes / 60)}h ${longShiftModal.over_minutes % 60}m`
                        : `${longShiftModal.over_minutes} min`}
                    </span>{' '}
                    over the {Math.floor(longShiftModal.limit_minutes / 60)}h
                    {longShiftModal.limit_minutes % 60 ? ` ${longShiftModal.limit_minutes % 60}m` : ''} limit.
                  </>
                ) : '.'}
              </p>
              <p className="text-sm text-gray-500 mt-3">
                You are already clocked out. Please say why the shift ran long —
                it goes into tomorrow morning's report.
              </p>

              <textarea
                rows={3}
                value={longShiftReason}
                onChange={(e) => setLongShiftReason(e.target.value)}
                placeholder="Why did the shift run long? e.g. urgent client cutoff, covering for absence"
                className="w-full mt-4 border border-gray-300 rounded-md px-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              {longShiftError && (
                <p className="text-sm text-red-600 mt-2 text-left">{longShiftError}</p>
              )}

              <button
                onClick={submitLongShiftReason}
                disabled={longShiftSaving || longShiftReason.trim().length < 5}
                className="w-full mt-4 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 text-sm font-medium"
              >
                {longShiftSaving ? 'Submitting…' : 'Submit reason'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Late clock-in — reason required, emailed to the TL/PM immediately */}
      {lateModal.open && (
        <div className="fixed inset-0 z-[9991] flex items-center justify-center bg-black bg-opacity-60 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="flex items-center p-4 bg-amber-50 border-b border-amber-200">
              <Clock className="w-6 h-6 text-amber-600 mr-3 flex-shrink-0" />
              <h3 className="text-lg font-semibold text-amber-800">Late Shift Start</h3>
            </div>
            <div className="p-6 text-center">
              <p className="text-gray-800 font-medium mb-1">
                You clocked in{' '}
                <span className="font-bold text-amber-600">
                  {lateModal.minutes >= 60
                    ? `${Math.floor(lateModal.minutes / 60)}h ${lateModal.minutes % 60}m`
                    : `${lateModal.minutes} min`}
                </span>{' '}
                after your shift start
                {lateModal.expected_start ? ` (${lateModal.expected_start})` : ''}.
              </p>
              <p className="text-sm text-gray-500 mt-3">
                Please give the reason. Your Team Leader and Manager are notified automatically.
              </p>

              <textarea
                rows={3}
                value={lateReason}
                onChange={(e) => setLateReason(e.target.value)}
                placeholder="e.g. traffic, medical appointment, approved by TL"
                className="w-full mt-4 border border-gray-300 rounded-md px-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              {lateReasonError && (
                <p className="text-sm text-red-600 mt-2 text-left">{lateReasonError}</p>
              )}

              <button
                onClick={submitLateReason}
                disabled={lateReasonSaving || lateReason.trim().length < 5}
                className="w-full mt-4 px-4 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {lateReasonSaving ? 'Submitting…' : 'Submit reason'}
              </button>
              <p className="text-xs text-gray-400 mt-3">
                Your shift has already started — this is recorded against today only.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal Popup */}
      {/* Break Exceeded Blocking Overlay — driven by backend shift summary flag */}
      {shiftSummary.break_exceeded && (
        <div className="fixed inset-0 z-[9990] flex items-center justify-center bg-black bg-opacity-60 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="flex items-center p-4 bg-orange-50 border-b border-orange-200">
              <Coffee className="w-6 h-6 text-orange-600 mr-3 flex-shrink-0" />
              <h3 className="text-lg font-semibold text-orange-800">Break Time Exceeded</h3>
            </div>
            <div className="p-6 text-center">
              <p className="text-gray-800 font-medium mb-1">
                Your{' '}
                <span className="capitalize font-bold text-orange-600">
                  {typeof shiftSummary.break_exceeded === 'object'
                    ? (shiftSummary.break_exceeded.type || 'break')
                    : 'break'}
                </span>{' '}
                break has exceeded the allowed limit
                {typeof shiftSummary.break_exceeded === 'object' && shiftSummary.break_exceeded.exceeded_by_minutes
                  ? ` by ${shiftSummary.break_exceeded.exceeded_by_minutes} min`
                  : ''}.
              </p>

              {/* The actual numbers. Each break type has its own allowance, so
                  "over by 2.1 min" on its own leaves the worker guessing which
                  limit was applied — they assume the longest one and think the
                  block is wrong. */}
              {typeof shiftSummary.break_exceeded === 'object'
                && shiftSummary.break_exceeded.limit_minutes != null && (
                <div className="mt-3 flex justify-center gap-6 text-sm">
                  <div>
                    <div className="font-bold text-orange-700">
                      {shiftSummary.break_exceeded.taken_minutes} min
                    </div>
                    <div className="text-xs text-gray-500">You took</div>
                  </div>
                  <div>
                    <div className="font-bold text-gray-700">
                      {shiftSummary.break_exceeded.limit_minutes} min
                    </div>
                    <div className="text-xs text-gray-500">
                      {shiftSummary.break_exceeded.type} allowance
                    </div>
                  </div>
                </div>
              )}

              <p className="text-sm text-gray-500 mt-3">
                Enter the reason for the extra break time to continue working.
                Your Team Leader and Manager are notified automatically.
              </p>

              <textarea
                rows={3}
                value={breakReason}
                onChange={(e) => setBreakReason(e.target.value)}
                placeholder="Why did the break run over? e.g. queue at the canteen, personal call"
                className="w-full mt-4 border border-gray-300 rounded-md px-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              {breakReasonError && (
                <p className="text-sm text-red-600 mt-2 text-left">{breakReasonError}</p>
              )}

              <button
                onClick={submitBreakReason}
                disabled={breakReasonSaving || breakReason.trim().length < 5}
                className="w-full mt-4 px-4 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {breakReasonSaving ? 'Submitting…' : 'Submit reason & continue working'}
              </button>
              <p className="text-xs text-gray-400 mt-3">
                The block clears for the rest of today. It applies again tomorrow.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* What next? — shown to opted-in operators with nothing running */}
      {whatNext.open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">What next?</h3>
              <p className="text-sm text-gray-500 mt-1">
                You have nothing running. Pick up a parked job, take a break, or get a new one.
              </p>
            </div>

            <div className="p-4 space-y-4 overflow-y-auto">
              {whatNext.loading && (
                <p className="text-sm text-gray-400 animate-pulse py-6 text-center">Loading…</p>
              )}

              {whatNext.message && (
                <div className="px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-md text-sm">
                  {whatNext.message}
                </div>
              )}

              {/* Parked work first — better to finish something started than
                  to pull yet another job. */}
              {(whatNext.data?.parked || []).length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Your parked jobs</p>
                  <div className="space-y-2">
                    {whatNext.data.parked.map((t) => (
                      <div key={t.task_id} className="flex items-center gap-3 p-2.5 border border-gray-200 rounded-md">
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 capitalize">
                          {String(t.status).replace('_', ' ')}
                        </span>
                        <span className="text-sm text-gray-800 truncate flex-1">
                          {t.job_code || t.title || 'Job'} — {t.task_name}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1.5">
                    Open one from the job list and press Start to carry on with it.
                  </p>
                </div>
              )}

              {/* Approval steps never block new work — the client has them. */}
              {(whatNext.data?.awaiting_approval || []).length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Waiting on approval</p>
                  <div className="space-y-2">
                    {whatNext.data.awaiting_approval.map((a) => (
                      <div key={a.task_id} className="flex items-center gap-3 p-2.5 border border-gray-200 rounded-md">
                        <span className="text-sm text-gray-800 truncate flex-1">{a.task_name}</span>
                        <span className="text-xs text-gray-500 shrink-0">
                          {a.waiting_minutes == null
                            ? '—'
                            : a.waiting_minutes >= 60
                              ? `${Math.floor(a.waiting_minutes / 60)}h ${a.waiting_minutes % 60}m`
                              : `${a.waiting_minutes}m`} waiting
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1.5">
                    These are with the client, so they do not stop you taking a new job.
                  </p>
                </div>
              )}

              {whatNext.data?.blocked_reason && (
                <div className="px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
                  {whatNext.data.blocked_reason}
                </div>
              )}
            </div>

            <div className="flex flex-wrap justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setWhatNext({ open: false, loading: false, working: false, data: null, message: '' })}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >Going on a break</button>
              <button
                onClick={() => setWhatNext({ open: false, loading: false, working: false, data: null, message: '' })}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >Resume a parked job</button>
              <button
                onClick={takeNextJob}
                disabled={whatNext.working || whatNext.loading || !whatNext.data?.can_take_new}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >{whatNext.working ? 'Finding a job…' : 'Need a new job'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Second CMD: correction or new change? */}
      {cmdKindModal.open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Why a second CMD?</h3>
              <p className="text-sm text-gray-500 mt-1">
                A CMD task was already completed on this job
                {cmdKindModal.previous?.completed_by_name
                  ? ` by ${cmdKindModal.previous.completed_by_name}` : ''}.
                Tell us what this one is for.
              </p>
            </div>

            <div className="p-4 space-y-3">
              <label className={`flex gap-3 p-3 rounded-md border cursor-pointer ${
                cmdKindModal.kind === 'correction'
                  ? 'bg-orange-50 border-orange-300' : 'border-gray-200 hover:bg-gray-50'
              }`}>
                <input
                  type="radio"
                  name="cmd_kind"
                  checked={cmdKindModal.kind === 'correction'}
                  onChange={() => setCmdKindModal((m) => ({ ...m, kind: 'correction' }))}
                  className="mt-0.5 accent-orange-600"
                />
                <span className="text-sm">
                  <span className="font-medium text-gray-800">Correction of the first CMD</span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Something was wrong with the earlier CMD. Logged as a query against
                    whoever did it, and counted as rework (old job).
                  </span>
                </span>
              </label>

              <label className={`flex gap-3 p-3 rounded-md border cursor-pointer ${
                cmdKindModal.kind === 'new_change'
                  ? 'bg-emerald-50 border-emerald-300' : 'border-gray-200 hover:bg-gray-50'
              }`}>
                <input
                  type="radio"
                  name="cmd_kind"
                  checked={cmdKindModal.kind === 'new_change'}
                  onChange={() => setCmdKindModal((m) => ({ ...m, kind: 'new_change' }))}
                  className="mt-0.5 accent-emerald-600"
                />
                <span className="text-sm">
                  <span className="font-medium text-gray-800">New change</span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    A fresh requirement or change — the first CMD was not at fault.
                    Counted as a new job, nobody is penalised.
                  </span>
                </span>
              </label>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason {cmdKindModal.kind === 'correction' && <span className="text-red-500">*</span>}
                </label>
                <textarea
                  rows={3}
                  value={cmdKindModal.reason}
                  onChange={(e) => setCmdKindModal((m) => ({ ...m, reason: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder={cmdKindModal.kind === 'correction'
                    ? 'What was wrong with the first CMD?'
                    : 'What changed? (optional)'}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setCmdKindModal({ open: false, kind: 'correction', reason: '', saving: false, previous: null })}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
              >Cancel</button>
              <button
                disabled={cmdKindModal.saving
                  || (cmdKindModal.kind === 'correction' && cmdKindModal.reason.trim().length < 5)}
                onClick={() => {
                  setCmdKindModal((m) => ({ ...m, saving: true }));
                  handleInsertCMDTask({
                    cmd_kind: cmdKindModal.kind,
                    reason: cmdKindModal.reason.trim(),
                  });
                }}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
              >{cmdKindModal.saving ? 'Inserting…' : 'Insert CMD'}</button>
            </div>
          </div>
        </div>
      )}

      {errorModal.isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center p-4 border-b border-red-200 bg-red-50">
              <AlertCircle className="w-6 h-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold text-red-800">Error</h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                {errorModal.message}
              </p>
            </div>
            <div className="flex justify-end p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={closeErrorModal}
                className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CMD status acknowledgement — blocks the Project Summary until "Got it" */}
      {cmdAck.open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-4 bg-amber-50 border-b border-amber-200">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <h3 className="text-base font-semibold text-amber-800">CMD work not complete</h3>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-600 mb-3">
                This project still has CMD work in progress. Please check with the CMD team before proceeding.
              </p>
              <div className="space-y-1.5">
                {(cmdAck.tasks || []).map((t, i) => (
                  <div key={t.id || i} className="flex items-center gap-2 text-sm">
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                      {(t.status || 'not started').replace(/_/g, ' ')}
                    </span>
                    {t.assigned_user_name && <span className="text-gray-500">· {t.assigned_user_name}</span>}
                  </div>
                ))}
              </div>
            </div>
            <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                onClick={() => setCmdAck({ open: false, tasks: [] })}
                className="px-5 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700"
              >Got it</button>
            </div>
          </div>
        </div>
      )}

      {/* Meal PIN dialog — shown after starting a meal break when cafeteria is enabled */}
      {mealPinModal.open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden text-center">
            <div className="p-6">
              <div className="text-sm font-medium text-gray-500 mb-1">
                Your {(mealPinModal.meal || 'meal').replace(/_/g, ' ')} PIN
              </div>
              <div className="text-5xl font-extrabold tracking-[0.35em] text-blue-700 my-4">
                {mealPinModal.pin}
              </div>
              <p className="text-sm text-gray-600">
                Show this PIN to the cafeteria staff to collect your meal.
              </p>
            </div>
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setMealPinModal({ open: false, pin: '', meal: '' })}
                className="w-full px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Jobs;